
import { serverUrlFor } from "./net/serverurl";
import { GuildComments, GuildWarArena, GuildWarMenu } from "./net/messages";
import { CharacterPanel, reclaimCommand } from "./ui/character";
import { CharacterWindow } from "./ui/characterwindow";
import { CursorController, CursorShape } from "./ui/cursor";
import { GameButtonWindow } from "./ui/gamebutton";
import { EmpireSelectScreen } from "./ui/empireselect";
import { EquipmentPanel } from "./ui/equipment";
import { SKILL_COMBO_ACTIVE, SKILL_COMBO_VNUM, SkillBar } from "./ui/skillbar";
import { skillStatusOf } from "./ui/skillaffect";
import {
  Connection,
  type ConnectionStatus,
  EntityKind,
  type Inventory,
  type InputSource,
  canPickInstance,
  isArenaManager,
  isAlchemist,
  // ---- mining
  isOreVein,
  isShopkeeper,
  type QuestDialog_,
  type QuestInputPrompt_,
  type QuestConfirmPrompt_,
  type QuestItemPrompt_,
  type QuestEntry_,
  type NpcMarker_,
  type QuestTarget_,
  NO_BAG,
  NO_SOULS,
  NO_PVP_MODE,
  NO_SKILLS,
  NO_QUICK_SLOTS,
  NO_SPIRIT,
  NO_STANDING,
  NO_TARGETED,
  NO_VITALS,
  type RemoteEntity,
  type ExchangeState,
  ChatChannel,
  type GuildFrame,
  type GuildLandStateFrame,
  type LandListFrame,
  type GuildInvitationFrame,
  type PartyFrame,
  type PartyInvitationFrame,
  type EmotePlayedFrame,
  type EmoteRequestFrame,
  type MountFrame,
  // ---- polymorph and pets
  type PetSummonedFrame,
  type PolymorphedFrame,
  type FishingFrame,
  type FishCaughtFrame,
  type FishingActFrame,
  // ---- mining
  type MiningFrame,
  type MineResultFrame,
  type OXQuestionFrame,
  type OXResultFrame,
  type AutoBanQuizFrame,
  // ---- dungeons
  type DungeonStateFrame,
  type WarpFrame,
  type MapChangeFrame,
  type ArenaDuelFrame,
  type ArenaInvitationFrame,
  type ArenaResultFrame,
  // ---- a player's own stall
  type StallSoldFrame,
  type StallWindowFrame,
  type TargetedFrame,
  type AimedFrame as AimFrame,
  type EquipmentView_,
  // ---- marriage and the friend list
  type MarriageStatusFrame,
  type FriendListFrame,
  type MonarchInfoFrame,
  // ---- the auction house and the item mall
  type AuctionBoardFrame,
  type AuctionNewsFrame,
  type MallWindowFrame,
  type CharactersFrame,
  type EmpireSelectPrompt,
  type CubeRecipes,
  type CubeResult,
  type CubeClosed,
  type DragonSoulRefined,
  type DragonSouls,
  type RefineOffer,
  type RefineResult,
  type Safebox,
  type ShopStock,
  type Skills_,
  type QuickSlot_,
  type Affect_,
  type Spirit_,
  type Standing_,
  type Vitals,
  duelStateWith,
  type DuelRecord,
  specificEffectKey,
} from "./net/connection";
import {
  AttackRepeater,
  canUseSkill,
  type CombatContext,
  CombatFeed,
  type CombatResolution,
  decodeCombatFlags,
  fallsHere,
  type FlyLaunch,
  HitFrameQueue,
  shouldPresentRemoteNormalAttack,
  REFERENCE_SWING_MS,
  clickBecomesSwing,
  heldKeyReservesApproach,
  swingIntervalMs,
  swingLockTicksFor,
  ticksForMs,
} from "./client/combat";
import { InputClock } from "./client/clock";
import { aimForSwing, groundAimFlight } from "./client/aim";
import { Predictor } from "./client/prediction";
import {
  AUTO_TARGET_RANGE_MM,
  TargetTracker,
  pickGround,
  pickTarget,
  pickVictim,
  isAttackable,
  pickByDefendingSphere,
} from "./client/target";
import { ARRIVAL_RADIUS_MM, WalkTo, headingToward } from "./client/walkto";
import { isLoot, nearestLoot } from "./client/loot";
import { resolvePursuit, canAct } from "./client/pursuit";
import { activeToggleSkills, dashing, reservedSkillStep, skillReach } from "./client/skillreach";
import { actorMotion, canAttack, canFishing, canUseSkill as actorCanUseSkill, gateInput, isFishing, isUsingSkill, ownAffects as ownBodyAffects, type ActorMotion } from "./client/actorbattle";
import {
  KeyboardInput,
  PlayerInput,
  TouchInput,
  JOYSTICK_RADIUS_PX,
  type WorldPoint,
} from "./input";
import { AttackButton } from "./render/controls";
import { COMBO_CHAIN_GAP_MS, GUILD_SYMBOL_RACE, advanceComboStep, type MotionMark } from "./render/entities";
import type { PushOutcome } from "./render/remotemotion";
import { comboMotionClip, nextComboClip, swingPlaybackRatio, type MotionMode, mountedAttackClip } from "./render/creaturemesh";
import { comboBeat, currentComboType } from "./client/combostep";
import { confirmedTargetView, FrameCounter, Hud, type TargetSocial } from "./render/hud";
import { createRenderer } from "./render/create";
import { buildRenderFrame } from "./render/frame";
import { FrameFx, applyFrameFx, launchFromBlow, launchIndexedFly } from "./render/framefx";
import { StatusLoops, affectLoopKeys } from "./render/effectsanchor";
import { EMOTICON_KEYED_COUNT, emoticonToken, emoticons } from "./render/emoticons";

/** Every guild building's `.mdatr`, once `main` has fetched it; the connections
 *  read it when they place the buildings in view (`ActorInstanceData.cpp:20-37`). */
let guildAttributesTable: GuildAttributeTable | null = null;
/** Whether the client's own body plays a skill motion, once there is a renderer to ask. */
let bodyUsingSkill: (entityId: number) => boolean = () => false;

/** `SpecificEffect` frames waiting for the next drawn frame, as manifest keys. */
const specificEffects: { entityId: number; effect: string }[] = [];
import { modelMatrix } from "./render/props";
import { POINT_EFFECT_NAMES, groundPickEffect, playsPotionSound } from "./render/effects";
import { QuestTargetBeacons } from "./render/targetbeacon";
import { AmbienceZones, SfxAmbienceLoops, loadAmbienceZones } from "./audio/ambience";
import {
  GESTURE_LIFETIME_MS,
  MAX_GESTURES,
  type GestureMark,
} from "./render/gesture";
import { resolveMap } from "./render/mapassets";
import { HeldCrossing, markCrossing, rememberCrossing, sessionStore, takeCrossing } from "./net/crossing";
import { Music, type TrackName } from "./audio/music";
import { creatureAttackSound, creatureDieSound, creaturePainSound, registerCreatureSounds, Sfx, spatial } from "./audio/sfx";
import { playAttackSound } from "./audio/attacksound";
import { BagPanel, FIRST_EMPTY_QUICK_SLOT } from "./ui/bag";
import { AttachMetinPanel } from "./ui/attachmetin";
import { StatusResetPanel } from "./ui/statusreset";
import { loadItemIcons, loadSkillIcons } from "./ui/itemicon";
import { loadDamageDigits } from "./client/damagedigits";
import { QuickSlotBar } from "./ui/quickslot";
import { ShopPanel } from "./ui/shop";
import { RefinePanel } from "./ui/refine";
import { CubePanel } from "./ui/cube";
import { DRAGON_SOUL_SLOT_BASE, DS_REFINE_CHOICE, DragonSoulPanel } from "./ui/dragonsoul";
import { ExchangePanel } from "./ui/exchange";
import { SafeboxPanel } from "./ui/safebox";
import { CostumeWindow } from "./ui/costume";
import { GiftBox } from "./ui/giftbox";
import { EnergyBar } from "./ui/energybar";
import { PARTY_REQUEST_DENIED, PartyPanel, parsePartyRequest } from "./ui/party";
import { QuestDialogPanel, QuestLetterList, QuestLogPanel, QuestPromptPanel, questModalCapturesKeys } from "./ui/quest";
import { ChatBox, kindOfChannel } from "./ui/chat";
import { composing, editLineOwnsReturn } from "./ui/ime";
import { EMOTES, EmotePanel, describe as describeEmote } from "./ui/emotes";
import { FatalErrorScreen } from "./ui/fatalerror";
// ---- polymorph and pets
import { PetPanel } from "./ui/pet";
import { observedSkillClip, skillMotionFor } from "./render/skillclip";
import { FISHING_ACT_START, FISHING_IDLE, FishingFloatEffects, FishingPanel, describeCatch, fishingAct, fishingHeading, floatPoint, lineMode, type FloatEffectCommand } from "./ui/fishing";
// ---- mining
import { MINING_SWINGING, MiningPanel, describeSwing } from "./ui/mining";
import { OXEventPanel } from "./ui/oxevent";
import { AutoBanQuizPanel } from "./ui/autoban";
// ---- dungeons
import { DungeonPanel } from "./ui/dungeon";
import { GuildPanel, MASTER_GRADE, guildQuickSkills, isGuildSkill } from "./ui/guild";
import { stepSitingCamera, type SitingCameraMemory } from "./ui/guildsiting";
import { WarBoard } from "./render/warboard";
import type { Emblem as EmblemFrame } from "./net/messages";
import { EmblemPanel } from "./ui/emblem";
import { emblemsToAsk } from "./render/guildmark";
import { ArenaPanel, describeResult } from "./ui/arena";
import { MarriageAsk } from "./ui/marriage";
import { FriendsPanel } from "./ui/friends";
import { WhisperWindows } from "./ui/whisper";
import { MonarchInfoPanel } from "./ui/monarch";
import { isSocialShortcut, SocialOpener } from "./ui/social";
import {
  StallBuilder,
  StallPanel,
  type StallDraftLine,
} from "./ui/pcshop";
import { AuctionPanel, describeNews } from "./ui/auction";
import { MallPanel } from "./ui/mall";
import { DeathScreen } from "./ui/death";
import { Minimap, recvLandList } from "./ui/minimap";
import { MapNameShower, authoritativeMapName } from "./ui/mapname";
import { AffectsPanel } from "./ui/affects";
import { TaskBar } from "./ui/taskbar";
import { NO_STAMINA, staminaAt, type Stamina_ } from "./net/stamina";
import { PlayerGauge } from "./ui/playergauge";
import { HelpPanel } from "./ui/help";
import { TipBoard } from "./ui/tips";
import { refusalText } from "./net/refusaltext";
import {
  LoginScreen,
  shouldReloadAfterRejection,
  type Credentials,
} from "./ui/login";
import { IntroSequence } from "./ui/intro";
import { PhaseCurtain, curtainReleased } from "./ui/phasecurtain";
import { CharacterCreateScreen } from "./ui/charcreate";
import { CharacterSelectScreen, resumableSlot } from "./ui/charselect";
import {
  CAMERA_MAX_DISTANCE_LONG_MM,
  CAMERA_MAX_DISTANCE_SHORT_MM,
} from "./render/camera";
import { type DrawLevel, drawGateFrom } from "./render/drawgate";
import { SelfMotion } from "./render/selfmotion";
import { ZoomControl, loadZoom } from "./render/zoom";
import { CollisionMap, OpenWorld, TreeCollision, type GuildAttributeTable, type ObjectWorld } from "./sim/collision";
import { assertTrigTables } from "./sim/fixed";
import { DEFAULT_STATS, Gait, TICK_HZ, TICK_MS, moveState } from "./sim/movement";
import { FootstepTracker } from "./audio/footsteps";
import { GameOptionsPanel } from "./ui/gameoptions";
import {
  loadClientOptions,
  pixelRatioCap,
  SystemOptionsPanel,
} from "./ui/systemoptions";
import { SystemMenu } from "./ui/system";
import { PasswordDialog } from "./ui/password";
import { bodyVnumWorn, weaponVnumWorn } from "./wardrobe";
import { ITEM_KIND_OTHER } from "./ui/itemart";

/** Past this, a fight is silent. Matches the interest radius: the server does
 *  not tell this client about anything further away, so a longer range would be
 *  a slider that never moves. */
const SOUND_RANGE_MM = 60_000;

const FIELD_TRACKS: Readonly<Record<number, TrackName>> = {
  1: "enter_the_east",
  2: "back_to_back",
  3: "open_the_gate",
  4: "a_rhapsody_of_war",
  5: "wonderland",
  6: "mountain_of_death",
  7: "save_me",
  8: "follow_war_god",
  9: "death_of_landmark",
  10: "Only_my_battle",
  11: "last-war2",
  12: "xmas",
  // The nine maps `settings.lua:131,143-145,164,167-170` route to these seven
  // tracks fell through to `m2bg` here too until `field_music.py` (server)
  // and `music.ts`'s `TrackName` (this file's sibling) both routed them.
  13: "lost_my_name",
  14: "monkey_temple",
  15: "catacomb_of_devil",
  16: "another_way",
  17: "misty_forest",
  18: "blacksea",
  19: "mt",
};

function fieldTrack(track: number): TrackName {
  return FIELD_TRACKS[track] ?? "m2bg";
}

const APPROACH_RADIUS_MM = 3_000;

const APPROACH_STOP_MM = 2_000;
/** How far inside a reserved skill range a walk aims to stop, so the frame it
 *  arrives on is already under `__ReserveUseSkill`'s range. */
const SKILL_APPROACH_SLACK_MM = 100;

/** Colours sampled from the original's model skins. See `tools/build_creatures.mjs`.
 *  Not per map: every shipped map draws its monsters out of the same species
 *  table, and this file is that table's colours. */
const CREATURE_PALETTE_URL = "creatures.json";

const OFFLINE_SPAWN = { xMm: 526_500, yMm: 623_500 };

async function signIn(
  serverUrl: string,
  fallbackName: string,
  world: ObjectWorld,
  input: InputSource,
  music: Music,
  sfx: Sfx,
): Promise<OnlineSession> {
  return new Promise((resolve) => {
    let screen: LoginScreen | null = null;
    let creation: CharacterCreateScreen | null = null;
    let select: CharacterSelectScreen | null = null;
    let empireSelect: EmpireSelectScreen | null = null;
    let session: OnlineSession | null = null;

    const start = (credentials: Credentials | null, guestName: string): void => {
      session = new OnlineSession(
        serverUrl,
        credentials === null ? guestName || fallbackName : "",
        world,
        input,
        credentials,
        (code, reason) => {
          // A rejection before Welcome is a retryable login failure. Once the
          // player is in the world, Reject means the live account was displaced
          // (legacy `introselect.py:350-352`); the login form is detached, so a
          // page reset is the visible route back to a usable account screen.
          if (shouldReloadAfterRejection(session?.joined === true)) {
            window.location.reload();
            return;
          }
          screen?.fail(code, reason);
        },
      );

      // The most recent slot list. Held outside the callback because the
      // creation screen is opened from the *select* screen's button, which fires
      // long after the frame that built it -- and a screen composing a request
      // out of a stale list would name a slot that has since been filled.
      let latest: CharactersFrame | null = null;
      // The server sends Characters before Welcome on every credentialed
      // reconnect. Remember only the slot the player actually chose; an empty
      // or missing slot is deliberately left to the visible chooser.
      let selectedSlot: number | null = null;

      session.onCharacters((characters) => {
        empireSelect?.dismiss();
        latest = characters;
        // Legacy `root/musicinfo.py:8-9` assigns characterselect.mp3 to both
        // the character list and creation screen. The field theme starts only
        // once Welcome arrives below.
        music.play("characterselect");
        // Credentials accepted: whatever happens next, it is not the login
        // form's business.
        screen?.dismiss();
        // `may_resume` is false only when the server put this session back in
        // the select phase on purpose (`/phase_select`, `cmd_general.cpp:
        // 306-315`), whose whole point is to reach the chooser: the original's
        // client opens its select window on the phase packet and waits
        // (`networkModule.py:204-212`), and the one thing that picks a slot for
        // the player there is the launcher's `autoSelect` flag
        // (`introLogin.py:764`), not the phase.
        const resume = characters.may_resume
          ? resumableSlot(characters.slots, selectedSlot)
          : null;
        if (resume !== null) {
          // A dropped socket has no world yet. Re-submit the server-advertised
          // slot and let the normal Welcome path rebuild the world.
          creation?.dismiss();
          select?.dismiss();
          session?.selectCharacter(resume);
          return;
        }
        selectedSlot = null;
        if (select === null) {
          select = new CharacterSelectScreen(document.body, {
            onPlay: (slot) => {
              selectedSlot = slot;
              session?.selectCharacter(slot);
            },
            onDelete: (slot, confirm) => session?.deleteCharacter(slot, confirm),
            onCreate: (slot) => {
              if (latest === null) return;
              select?.dismiss();
              creation?.update(latest, slot);
            },
            onExit: () => {
              select?.dismiss();
              window.location.reload();
            },
          });
        }
        if (creation === null) {
          creation = new CharacterCreateScreen(document.body, {
            onCancel: () => {
              creation?.dismiss();
              if (latest !== null) select?.update(latest);
            },
            onCreate: (slot, name, race, shape) =>
              session?.createCharacter(slot, name, race, shape),
          });
        }
        if (characters.slots.some((slot) => slot.filled)) {
          creation.dismiss();
          select.update(characters);
        } else {
          select.dismiss();
          creation.update(characters);
        }
      });

      session.onEmpireSelect((prompt) => {
        screen?.dismiss();
        empireSelect?.dismiss();
        empireSelect = new EmpireSelectScreen(prompt, {
          onSelect: (empire) => session?.selectEmpire(empire),
          onExit: () => {
            empireSelect?.dismiss();
            window.location.reload();
          },
        });
      });

      // A refusal reaches whichever of the two screens is up and nowhere else.
      // The chat box does not exist yet, and the alternative -- letting the
      // notice fall on the floor until the world is drawn -- is a form that
      // silently does nothing when a name is taken or a password is mistyped.
      session.onNotice((code, sent) => {
        // A refusal the 2014 client showed nothing for unlocks the screen and
        // is never shown as an empty error (`refusaltext.json`).
        const text = refusalText(code, sent);
        if (empireSelect?.open === true) {
          if (text) empireSelect.fail(text);
          else empireSelect.release();
        } else if (creation?.open === true) {
          if (text) creation.fail(text);
          else creation.release();
        } else if (select?.open === true) {
          // A failed selection is not a durable choice. Let the player choose
          // again instead of auto-submitting the rejected slot on reconnect.
          selectedSlot = null;
          if (text) select.fail(text);
          else select.release();
        }
      });

      // Resolve on the first Welcome, not on the attempt: until the server has
      // answered there is nothing to draw and the screen must stay put.
      const settle = window.setInterval(() => {
        if (session?.joined !== true) return;
        window.clearInterval(settle);
        screen?.dismiss();
        creation?.dismiss();
        select?.dismiss();
        music.play(music.preferredTrack ?? "m2bg");
        resolve(session);
      }, 100);
    };

    screen = new LoginScreen({
      onSubmit: start,
      sfx,
      onRegister: (credentials) => {
        const attempt: Connection = new Connection({
          url: serverUrl,
          playerName: "",
          world,
          guildAttributes: () => guildAttributesTable,
          input,
          registration: credentials,
          onRegistered: (ok, username, text) => {
            screen?.registered(ok, username, text);
            attempt.close();
          },
          onStatus: (status, detail) => {
            if (status === "rejected" || status === "offline") {
              screen?.registered(false, "", detail || "Could not reach the server.");
            }
          },
        });
        attempt.open();
      },
      // One gesture releases both. The music module was armed before the
      // screen existed; the effects context cannot even be created until now.
      onGesture: () => {
        music.unlock();
        void sfx.unlock();
      },
    });

    (window as unknown as Record<string, unknown>).__metin3Entry = {
      signIn: (username: string, password: string): void =>
        start({ username, password }, ""),
      guest: (name: string): void => start(null, name),
    };
  });
}

async function boot(): Promise<void> {
  // Before anything else: if this build's trig tables differ from the ones the
  // server was built against, every prediction is wrong and nothing downstream is
  // worth debugging.
  assertTrigTables();

  const canvas = document.getElementById("view") as HTMLCanvasElement | null;
  const overlay = document.getElementById("overlay");
  if (canvas === null || overlay === null) throw new Error("index.html is missing #view/#overlay");

  // The client's one cursor. On `body` because the original's is the window's
  // and not a widget's -- see `ui/cursor.ts` and the `body` rule in
  // `style.css`, which states the same starting arrow so it is on screen
  // before this line runs.
  const cursor = new CursorController(document.body);

  // Native order is logo -> loading -> next phase. The movie itself is absent,
  // so this uses the shipped logo-bearing art and never adds a timed splash.
  const introSequence = new IntroSequence(document);

  const params = new URLSearchParams(location.search);
  const serverUrl = serverUrlFor(params, location);

  const scene = drawGateFrom(params);

  const arriving = takeCrossing(sessionStore());
  markCrossing("boot", { arriving, serverUrl, href: location.href });
  const mapPromise = resolveMap({
    params,
    serverUrl,
    arrivingOn: arriving?.map ?? null,
  });
  introSequence.loading("Loading map data");
  const map = await mapPromise;
  console.log(`map: ${map.name} (${map.key}), ${map.worldWidthMm / 1000} x ${map.worldHeightMm / 1000} m`);
  markCrossing("map", { name: map.name, key: map.key });

  const renderer = await createRenderer(canvas, map);
  // `?draw=lite` -- the actors without the world behind them, for the checks
  // that assert on drawn pixels. `render/drawgate.ts`.
  renderer.setReducedDraw?.(scene.reduced);
  const clientOptions = loadClientOptions();
  renderer.setPixelRatioCap(pixelRatioCap(clientOptions.graphicsQuality));
  renderer.camera.setMaximumEyeDistanceMm(
    clientOptions.cameraDistance === "long"
      ? CAMERA_MAX_DISTANCE_LONG_MM
      : CAMERA_MAX_DISTANCE_SHORT_MM,
  );
  // These five values are deliberately browser-local.  The original keeps
  // them in CPythonSystem rather than a character packet
  // (`PythonSystem.cpp:317-354`); retaining them as mutable presentation state
  // means a checkbox redraws the next frame but cannot alter a server fact.
  let alwaysShowNames = clientOptions.alwaysShowNames;
  let showDamage = clientOptions.showDamage;
  let showSalesText = clientOptions.showSalesText;
  let viewChat = clientOptions.viewChat;
  // Assigned when the chat box exists below.  Options is constructed before
  // ChatBox because the system menu is mounted before the chat panel; keeping
  // a local sink avoids making a display preference a session callback.
  let applyViewChat: (visible: boolean) => void = () => {};
  let requestPvpMode: (mode: number) => void = () => {};
  let requestBlockMode: (
    whisper: boolean,
    messengerInvite: boolean,
    exchange: boolean,
    partyInvite: boolean,
    guildInvite: boolean,
    partyRequest: boolean,
  ) => void = () => {};
  console.log(`renderer: ${renderer.backend}`);
  const playerGauge = new PlayerGauge(overlay);
  // Beside the diagnostics HUD, because a buff is a fact about the same
  // character the rest of this column describes.
  const affects = new AffectsPanel(overlay);
  // Instrumentation, off unless asked for. See `Hud`'s own constructor doc.
  const hud = new Hud(overlay, params.get("debug") === "1");
  hud.setTargetBoardVisible(clientOptions.targetBoardVisible);
  const minimap = new Minimap(overlay, map);
  // `ArenaDuel.observer_count` is server-owned. Keep the last frame's value
  // beside the minimap so its info line can update without making the minimap
  // depend on the arena panel.
  let minimapObserverCount = 0;
  // `GuildWarArena.observer_count`, the war map's own `ObserverCount`
  // (`war_map.cpp:317-326`, `uiminimap.py:380-385`). A duel arena and a war
  // map are never the same map, so the line shows whichever is not zero.
  let warObserverCount = 0;
  const mapName = new MapNameShower(overlay);
  // `uiHelp.py` owns no server state.  The browser equivalent is constructed
  // alongside the other local overlay windows and opened by `?`, below.
  const help = new HelpPanel(overlay);
  const frames = new FrameCounter();

  const keyboard = new KeyboardInput(window);
  const touch = new TouchInput(canvas);
  // Point-and-click. A goal, not a route: `client/walkto.ts` turns it into the
  // same heading-and-gait a held key produces, one frame at a time, and there
  // is no destination anywhere on the wire -- see that file on why that is the
  // whole point rather than a limitation.
  const walkTo = new WalkTo();
  let selfPosition: () => WorldPoint = () => ({ xMm: 0, yMm: 0 });
  const input = new PlayerInput(
    keyboard,
    touch,
    () => renderer.camera.yawDeg,
    walkTo,
    () => selfPosition(),
  );
  let ownStallIsOpen = false;
  // What the client's own body is playing, as the `CActorInstance` gates read it.
  const ownMotion = (): ActorMotion => actorMotion(
    renderer.activeClip(session.selfId),
    renderer.activeClipEntry(session.selfId),
  );
  bodyUsingSkill = (entityId) => entityId === session.selfId
    ? ownMotion().usingSkill
    : isUsingSkill(renderer.activeClip(entityId));
  const ownAffects = () => ownBodyAffects(session);
  // The session the sampler gates against, once there is one: the connection
  // samples while signing in, before `session` below has been assigned.
  let joined: Session | null = null;
  const gatedInput = gateInput(
    input,
    () => (joined === null ? null : { body: joined, motion: ownMotion() }),
    () => ownStallIsOpen,
  );
  const attackButton = new AttackButton(overlay);
  const attacks = new AttackRepeater();
  const target = new TargetTracker();
  // The last selection this client told the server about, so the frame is sent
  // on a change and not on a frame. Zero is "nothing", which is the wire's own
  // value for it and also the value the server starts every session with -- so
  // an opening zero is never sent and never needs to be.
  let lastTargetSent = 0;

  const playerName = params.get("name") ?? `web-${Math.floor(Math.random() * 9000 + 1000)}`;

  let world: ObjectWorld;
  try {
    const collision = await CollisionMap.fetch(map.assets.terrain);
    renderer.setTerrain(collision);
    world = collision;
  } catch (error) {
    // A missing terrain blob must not be fatal: prediction and the protocol are
    // still demonstrable on open ground, and the console says what is missing.
    console.warn(`terrain unavailable (${String(error)}); running on open ground`);
    // `OpenWorld`, not a two-method literal: it is the same open ground and it
    // also carries the runtime ATTR_OBJECT overlay, so a guild-building
    // footprint still blocks on a map whose terrain blob failed to load. Python
    // gets that by inheritance (`sim/collision.py:224-233`).
    world = new OpenWorld();
  }
  try {
    // Each placed tree's `.spt` spheres and cylinders (`Area.cpp:398-408`).
    const response = await fetch(`treecollision/${map.key}.json`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const treeCollision = await response.json();
    world.setTrees(TreeCollision.fromJson(treeCollision));
    renderer.setTreeCollision?.(treeCollision);
  } catch (error) {
    console.warn(`tree collision unavailable (${String(error)}); trees do not block`);
  }
  try {
    // Every guild building's `.mdatr`, placed where a built one stands (`ActorInstanceData.cpp:20-37`).
    const response = await fetch("guildattributes.json");
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    guildAttributesTable = (await response.json()) as GuildAttributeTable;
    renderer.setGuildAttributes?.(guildAttributesTable);
  } catch (error) {
    console.warn(`guild building attributes unavailable (${String(error)}); guild buildings block on their footprint alone`);
  }

  const music = new Music({ volume: clientOptions.musicVolume });
  const sfx = new Sfx({ volume: clientOptions.soundVolume });
  const ambienceLoops = new SfxAmbienceLoops(sfx);
  let ambienceZones = new AmbienceZones([]);
  if (map.assets.placements) {
    void loadAmbienceZones(map.assets.placements).then((zones) => { ambienceZones = new AmbienceZones(zones); });
  }
  (window as unknown as Record<string, unknown>).__metin3Sfx = sfx;

  // `uisystemoption.py:89-93,207-215` applies both volume sliders immediately
  // and stores them in the original client's local `systemSetting`. This panel
  // keeps that boundary: it owns no session callback and constructs no frame.
  const systemOptions = new SystemOptionsPanel(overlay, {
    music,
    sound: sfx,
    rendererBackend: renderer.backend,
    onGraphicsQuality: (quality) => renderer.setPixelRatioCap(pixelRatioCap(quality)),
    onCameraDistance: (distance) => renderer.camera.setMaximumEyeDistanceMm(
      distance === "long" ? CAMERA_MAX_DISTANCE_LONG_MM : CAMERA_MAX_DISTANCE_SHORT_MM,
    ),
    showTrigger: false,
  }, undefined, clientOptions);

  // `uigameoption.py`'s own local presentation switches, plus PvP Mode and
  // Block (see `requestPvpMode`/`requestBlockMode` above for why those two go
  // through indirection rather than a direct `session` call here).
  const gameOptions = new GameOptionsPanel(overlay, {
    onLocalOption: (name, value) => {
      if (name === "targetBoardVisible") {
        hud.setTargetBoardVisible(value);
      } else if (name === "alwaysShowNames") {
        alwaysShowNames = value;
      } else if (name === "showDamage") {
        showDamage = value;
      } else if (name === "showSalesText") {
        showSalesText = value;
      } else if (name === "viewChat") {
        viewChat = value;
        applyViewChat(value);
      }
    },
    onPvpMode: (mode) => requestPvpMode(mode),
    onBlockMode: (whisper, messengerInvite, exchange, partyInvite, guildInvite, partyRequest) =>
      requestBlockMode(whisper, messengerInvite, exchange, partyInvite, guildInvite, partyRequest),
    showTrigger: false,
  }, undefined, clientOptions);

  overlay.addEventListener("click", (event) => {
    if (event.target instanceof Element && event.target.closest("button") !== null) {
      sfx.play("click");
    }
  });

  void fetch(CREATURE_PALETTE_URL)
    .then((response) => (response.ok ? response.json() : null))
    .then((palettes: Record<string, { body: string; dark: string; sound?: string }> | null) => {
      if (palettes === null) return;
      renderer.setPalettes(palettes);
      // The `sound` field each entry carries is a species' folder-derived
      // sound set (see `tools/build_creatures.mjs`); hand the whole name->set
      // table to `sfx` so a Grizzly Bear sounds like a bear, not the wolf its
      // absence from the boot table would otherwise make it.
      const sounds: Record<string, string> = {};
      for (const [name, entry] of Object.entries(palettes)) {
        if (entry.sound !== undefined) sounds[name] = entry.sound;
      }
      registerCreatureSounds(sounds);
    })
    .catch((error: unknown) => {
      console.warn(`creature palette unavailable (${String(error)})`);
    });
  void loadItemIcons();
  // And the skill bar's, keyed by icon name, on the same terms.
  void loadSkillIcons();
  // And the damage-number sprites `ProcessDamage` swaps a digit at a time
  // (`InstanceBaseEffect.cpp:180-197`), on the same terms again: a fight that
  // waited for a sprite sheet would be a fight broken by an asset, and until
  // the sheet lands `EntityPainter.drawFloater` paints the numbers as text.
  void loadDamageDigits();
  // Armed rather than played: the browser will refuse until a gesture, and the
  // login screen owns that gesture.
  music.play("login_window");

  introSequence.close();

  const session = serverUrl === null
    ? new OfflineSession(world, gatedInput)
    : arriving !== null
      // No login screen, because there is nothing to log in to: the character
      // is in the server's crossing table, not in a world, and a `Login` here
      // would sign the account in on the map that was just left. The token is
      // the whole credential -- `input_login.cpp:167-180`.
      ? (markCrossing("session:arrive"), new OnlineSession(serverUrl, playerName, world, gatedInput, null, undefined, arriving.token))
      : (markCrossing("session:signin"), await signIn(serverUrl, playerName, world, gatedInput, music, sfx));
  joined = session;

  const phaseCurtain = new PhaseCurtain(document, overlay);

  const returnToLogin = (): void => {
    session.close();
    window.location.reload();
  };
  let openMall: () => void = () => {};
  let openWhisper: (name: string) => void = () => {};
  // Set just before `/mall_password` is sent; cleared and acted on by the
  // next `MallWindow` frame (see `session.onMall`, below) -- a wrong
  // password or a cooldown refusal answers with a `Notice` instead of a
  // fresh frame, so the panel never opens for either.
  let mallAwaitingOpen = false;
  const systemMenu = new SystemMenu(overlay, {
    onSystemOptions: () => systemOptions.setVisible(true),
    onGameOptions: () => gameOptions.setVisible(true),
    onCharacterSelect: returnToLogin,
    onLogout: returnToLogin,
    // `net.ExitApplication()` (`uisystem.py:104-106`) quits the native
    // process; a tab cannot do that to itself, so Exit Game ends the
    // session the same way Log Out does rather than opening a second path.
    onExit: returnToLogin,
    // The original closes SystemDialog before invoking its help callback
    // (`client/bin/pack/root/uisystem.py:125-130`).
    onHelp: () => help.open(),
    onMall: () => openMall(),
  });

  // A death gives the player one genuine choice: wait, or take the server-owned
  // town restart now. `Recall` contains no coordinates or penalty bit; the
  // server resolves both from the corpse (`cmd_general.cpp:622-634`).
  const death = new DeathScreen(overlay, () => session.recall(), () => session.restartHere());

  const hitFrames = new HitFrameQueue();

  const frameFx = new FrameFx();
  // The poison/slow/stun loops, kept attached for as long as the affect is on
  // the character -- see `render/effectsanchor.ts`.
  const statusLoops = new StatusLoops();

  // The renderer's half of what the session already does to the combat feed on
  // a reconnect. A new world is new entity ids, so whatever the effect layer
  // was still burning over the old one -- particles, and any arrow still
  // crossing it -- belongs to nobody here.
  if (session instanceof OnlineSession) {
    session.onEnvironment((night) => renderer.setNight(night));
    session.onWeather((kind) => renderer.setWeather(kind));
    session.onFieldMusic((track, volumePercent) => {
      // `root/game.py:202-210` uses the server multiplier *with* the local
      // System Options slider. The message carries no filename, so this closed
      // mapping cannot be used to make the browser fetch attacker-selected media.
      music.playField(fieldTrack(track), volumePercent / 100);
    });
    const enteredWorld = (): void => {
      renderer.effects.clear();
      frameFx.clear();
      // A reconnect reissues entity ids; a hit frame armed for the world
      // before it must not sit in the queue waiting to be spent (or silently
      // dropped by `locallyPresented`, which the reconnect already nulls) --
      // it belongs to nobody in this world.
      hitFrames.clear();
      const entered = authoritativeMapName(map, session.mapId);
      markCrossing("entered", { mapId: session.mapId, mapName: map.name, entered });
      if (entered !== null) mapName.show(entered);
      else mapName.hide();
    };
    session.onNewWorld(enteredWorld);
    // A normal sign-in returns only after Welcome, so its callback happened
    // before panels could subscribe. A crossing constructs the session before
    // Arrive completes and reaches the callback above instead.
    if (session.joined) enteredWorld();
  } else {
    // Offline has no server and therefore no competing authority. `map.name`
    // still came from the generated manifest (or its named a1 fallback), not
    // from an inbound game message.
    mapName.show(map.name);
  }

  // The predictor exists now, so a click-to-walk can be aimed from where the
  // character actually is. See `selfPosition`'s declaration for why it is a box.
  selfPosition = () => session.predictor.state;

  const chat = new ChatBox({
    mount: overlay,
    sfx,
    onSend: (line) => {
      if (line.channel === ChatChannel.WHISPER) {
        session.whisper(line.whisperTo, line.text);
        chat.push({ kind: "whisper-sent", from: line.whisperTo, text: line.text });
        return;
      }
      session.say(line.text, line.channel);
    },
  });
  applyViewChat = (visible) => chat.setViewChat(visible);
  applyViewChat(viewChat);
  const equipment = new EquipmentPanel(overlay);
  session.onEquipmentView((view) => equipment.update(view));
  hud.setTargetActions({
    onClearTarget: () => target.clear(),
    // `SetWhisperEvent(self.interface.OpenWhisperDialog)` (`game.py:106`).
    onWhisper: (name) => openWhisper(name),
    onExchange: (id) => session.exchangeStart(id),
    onFriend: (name) => session.addFriend(name),
    onPartyInvite: (id) => session.partyInvite(id),
    onGuildInvite: (id) => session.guildInvite(id),
    // `__OnEmotionAllow` is one chat line (`uitarget.py:364-365`).
    onEmotionAllow: (id) => session.say(`/emotion_allow ${id}`),
    // LEAVE_PARTY / EXCLUDE (`uitarget.py:340-344`), the same two calls the
    // roster panel's own buttons make.
    onPartyLeave: () => session.partyLeave(),
    onPartyKick: (id) => {
      const slot = lastParty?.members.findIndex((member) => member.entity_id === id) ?? -1;
      if (slot >= 0 && lastParty !== null) session.partyKick(slot, lastParty.roster_revision);
    },
    onInspectEquipment: (id) => {
      equipment.requestOpen();
      session.setTarget(id);
    },
    onDuel: (id) => session.say(`/pvp ${id}`),
    // `TargetBoard.__OnRequestParty` (`uitarget.py:358-359`) is the same
    // shape: one chat line naming the vid, and no local state.
    onPartyRequest: (id) => session.say(`/party_request ${id}`),
  });
  // Enter opens the box; the box's own handler stops the keystroke reaching the
  // movement keys while it holds focus. Bound on `window` rather than the canvas
  // because a player who just clicked the HUD still expects Enter to work.
  window.addEventListener("keydown", (event) => {
    // A committed candidate arrives as Enter too; opening the chat box on it
    // would steal the keystroke that ends someone else's composition.
    if (composing(event) || event.key !== "Enter" || chat.focused) return;
    // The active window answers Return first and the manager stops there
    // (`PythonWindowManager.cpp:1077-1083`); the chat box is the fallback for a
    // Return no edit line owned. Without this the box opened over every field
    // in the game and cancelled the submission the keystroke was meant to make.
    if (editLineOwnsReturn(event.target)) return;
    event.preventDefault();
    chat.focus();
  });
  const attachMetin = new AttachMetinPanel(
    overlay,
    (stone, target) => {
      // `uiattachmetin.py:132`'s `Accept()` plays this before it even knows
      // whether the 30% roll landed; the server's real outcome is whatever
      // `useItemToItem` gets back, never this clip.
      sfx.play("metinstone_insert");
      session.useItemToItem(stone, target);
    },
  );
  const costume = new CostumeWindow(overlay, {
    onUnequip: (wear) => session.unequip(wear),
  });
  // The four facts `TargetBoard.RefreshButton` asks about the selected player
  // (`uitarget.py:378-441`). Held here because they arrive on four different
  // frames and the HUD is repainted from one model: the panels below already
  // subscribe to each, so this is the same value, not a second source.
  let lastParty: PartyFrame | null = null;
  let friendNames = new Set<string>();
  let mayInviteToGuild = false;
  let guildMemberNames = new Set<string>();
  /** `TargetSocial` for one confirmed target, or undefined when there is none. */
  const socialFor = (id: number): TargetSocial | undefined => {
    if (id === 0) return undefined;
    const entity = session.entity(id);
    const name = entity?.name ?? "";
    const party = lastParty;
    const inMyParty =
      party !== null &&
      party.leader_id !== 0 &&
      party.members.some((member) => member.entity_id === id);
    const here = selfPosition();
    return {
      // `messenger.IsFriendByName` (`uitarget.py:401`).
      friend: name !== "" && friendNames.has(name),
      // All three halves of `uitarget.py:396-400`: the authority, the target
      // not already being in this guild, and -- the one this wire cannot see
      // -- the target being in no guild at all, which the server re-checks.
      mayInviteToGuild: mayInviteToGuild && (name === "" || !guildMemberNames.has(name)),
      inMyParty,
      targetLeadsMyParty: party !== null && party.leader_id === id,
      selfInParty: party !== null && party.leader_id !== 0,
      selfLeadsParty: party !== null && party.leader_id === session.selfId,
      distanceMm:
        entity === null || entity === undefined
          ? undefined
          : Math.hypot(entity.xMm - here.xMm, entity.yMm - here.yMm),
    };
  };
  let bindBagToQuick: (quickIndex: number, bagSlot: number) => void = () => {};
  const bag = new BagPanel(overlay, {
    sfx,
    onUse: (slot) => session.useItem(slot),
    onDrop: (slot) => session.dropItem(slot),
    // `__PutItem` (`game.py:1284-1290`): released over an NPC, the item is
    // `SendGiveItemPacket`, which a smith answers with its refine offer
    // (`char_item.cpp:6618-6626`).
    onGiveAt: (slot, x, y) => {
      const over = document.elementFromPoint(x, y);
      if (over !== canvas && over !== overlay) return false;
      const pickable = session.others(performance.now()).filter(canPickInstance);
      const pointer = { x, y };
      const picked = spherePick(pointer, pickable)
        ?? pickTarget(pickable, pointer, renderer.camera, (px, py) => renderer.groundAtMm(px, py));
      const subject = picked === null ? null : session.entity(picked);
      if (subject === null || (subject.kind !== EntityKind.NPC && subject.kind !== EntityKind.BUILDING)) return false;
      session.useItemOnNpc(slot, subject.id);
      return true;
    },
    onDropGold: (amount) => session.dropGold(amount),
    onUnequip: (wear) => session.unequip(wear),
    onSell: (_npcId, slot) => session.sellItem(slot),
    // Still no local reordering: the drag says what was asked for and the
    // `Bag` frame that comes back says what happened. See `moveItem`.
    onMove: (source, destination) => session.moveItem(source, destination),
    // Shift-click one slot, then click another: `UseItemToItem`. The server
    // recognises a treasure key against a treasure box and a Blessing Scroll
    // against a refinable item; this panel intentionally pre-filters neither
    // -- see `BagActions.onUseOnItem`.
    onUseOnItem: (slot, target) => {
      const stone = session.bag.slots.find((held) => held.slot === slot);
      const equipment = session.bag.slots.find((held) => held.slot === target);
      // `uiinventory.py:882-906` opens uiAttachMetin only for a compatible
      // Spirit Stone pairing.  Everything else keeps the generic two-cell path;
      // neither branch can state a vnum, socket, item id or result to the server.
      if (stone !== undefined && equipment !== undefined && attachMetin.open(stone, equipment)) return;
      session.useItemToItem(slot, target);
    },
    onArm: (slot, name) => {
      souls.armBagSlot(slot, name);
      party.armBagSlot(slot);
    },
    // `DSSButton` and `MallButton`, the two buttons the source hangs on the
    // inventory's own equipment board (`locale/en/ui/inventorywindow.py:96-124`).
    // Both open a panel this build already has; the third button on that board,
    // `CostumeButton`, has no costume system here to open (see `bag.ts`).
    onOpenDragonSoul: () => {
      // `interface.ToggleDragonSoulWindow` (`uiinventory.py:474-476`), the
      // same open/close the taskbar's own Expand button runs below.
      if (souls.showing) souls.cancel();
      else souls.update(session.souls, session.bag);
    },
    onOpenMall: () => session.say("/click_mall"),
    onCostume: () => costume.toggle(),
    // Inventory drag onto a Fast Access Field (`uitaskbar.py:829-847`). The
    // bag cannot import the bar (cycle through `iconFor`), so the shell binds.
    onBindQuickSlot: (quickIndex, bagSlot) => bindBagToQuick(quickIndex, bagSlot),
    onDropOnExchange: (bagSlot) => exchange.offer(bagSlot),
    onDropOnSafebox: (bagSlot, cell) => safebox.depositInto(bagSlot, cell),
    onDropOnStallBuilder: (bagSlot, cell) => stallBuilder.offer(bagSlot, cell),
    onHoverStallBuilder: (vnum, cell) => stallBuilder.hover(vnum, cell),
  });
  bag.update(session.bag);
  const statusReset = new StatusResetPanel(overlay, {
    onAccept: () => session.statusReset(),
  });
  session.onStatusResetOffer(() => {
    statusReset.offer();
  });
  session.onStatusResetConfirm(() => statusReset.confirm());
  let paintTaskbarQuick: () => void = () => {};
  let useQuickSkill: (vnum: number) => void = () => {};
  // The guild's seven skills as quick slots paint them, from the last roster.
  let guildQuickRows: ReturnType<typeof guildQuickSkills> = [];
  const quickslots = new QuickSlotBar(overlay, {
    onUse: (cell) => session.useItem(cell),
    onUseSkill: (vnum) => useQuickSkill(vnum),
    onEmote: (emotion) => session.emote(emotion, target.id ?? 0),
    onAdd: (pos, type, slotTarget) => session.sendQuickSlotAdd(pos, type, slotTarget),
    onDel: (pos) => session.sendQuickSlotDel(pos),
    // Every box a player types into. The digits are hotkeys now, so a bar that
    // only asked about the chat box would put a potion in the middle of a
    // sentence typed into the guild or friend panel. Read at keystroke time,
    // which is why these three are named lazily and not captured.
    blocked: () => chat.focused || guild.typing || friends.typing,
    onChange: () => paintTaskbarQuick(),
  });
  quickslots.update(session.bag);
  quickslots.updateSkills(session.skills.slots);
  quickslots.setSlots(session.quickslots);
  session.onQuickSlots((slots) => {
    quickslots.setSlots(slots);
  });
  bindBagToQuick = (quickIndex, bagSlot) => {
    const index =
      quickIndex === FIRST_EMPTY_QUICK_SLOT
        ? quickslots.firstEmpty()
        : quickslots.globalIndex(quickIndex);
    if (index === null) return;
    // The bag cell itself, not what is in it: `SetQuickslot` reads an item
    // slot's `pos` as `TItemPos(INVENTORY, pos)` (`char_quickslot.cpp:63`) and
    // keeps it pointing at the right cell from there on (`SyncQuickslot`).
    quickslots.bindItem(index, bagSlot);
  };
  // Filled in once the trade window and the storeroom exist. They are built
  // below because both take `session` callbacks, and the bag sink is registered
  // here with the rest of the counter wiring; one forward reference is smaller
  // than moving either.
  let intoWindows: (held: Inventory) => void = () => {};
  // The dealer's own counter. Separate from the bag because what a dealer sells
  // is a list this client has never seen and cannot derive; what a dealer buys
  // is already drawn in the bag, which is why selling stays a mode on that
  // panel rather than a second window.
  const shop = new ShopPanel(overlay, {
    onBuy: (slot) => session.buyItem(slot),
    onClose: () => {
      session.closeShop();
      cursor.set(CursorShape.NORMAL);
    },
    onSellMode: (selling) =>
      cursor.set(
        !shop.showing
          ? CursorShape.NORMAL
          : selling
            ? CursorShape.SELL
            : CursorShape.BUY,
      ),
  });
  session.onShop((stock) => shop.update(stock));
  let lastGold = session.bag.gold;
  // Set the moment the Alchemist is clicked, cleared by the very next
  // `QuestDialog` this connection receives -- see the `onQuestDialog` sink
  // and `isAlchemist(npc)` below.
  let talkingToAlchemist = false;
  // The Alchemist menu page on screen and its refinement row, if any.
  let alchemistRefineChoice: { token: number; choice: number } | null = null;
  let questPrompt: QuestPromptPanel | null = null;
  session.onBag((held) => {
    guild.setBag(held);
    // Any authoritative bag replacement invalidates a preview made from its
    // predecessor. The server would re-read the cells safely either way, but a
    // player must not be invited to accept a picture that is no longer true.
    attachMetin.close();
    bag.update(held);
    costume.update(held.worn);
    // Affordability is drawn from the purse the server last stated, and a
    // purchase is exactly what changes it.
    shop.setGold(held.gold);
    // The same for a stall's shelves, and the builder needs the bag itself --
    // it is composing a listing out of cells, so it redraws when a cell moves.
    stall.setGold(held.gold);
    stallBuilder.setBag(held);
    // The auction form composes a listing out of bag cells, and the mall needs
    // to know where a checked-out item would land, so both redraw when a cell
    // moves.
    auction.setBag(held);
    mall.setBag(held);
    if (held.gold !== lastGold) sfx.play("money");
    lastGold = held.gold;
    // The trade window and the storeroom each draw their own list of what could
    // go in, because neither can reach into the bag panel -- and both have to be
    // told when a purchase, a sale or the other half of a trade changes it.
    intoWindows(held);
    quickslots.update(held);
    paintTaskbarQuick();
    questPrompt?.setBag(held);
  });
  shop.setGold(session.bag.gold);
  const tips = new TipBoard(overlay);
  session.onNotice((code, sent, big) => {
    // A refusal without a server sentence reads as the 2014 client read its
    // bare failure packet (`refusaltext.json`); where that was silence it
    // draws nothing.
    const text = refusalText(code, sent);
    if (text.length === 0) return;
    // The original's own fork: `CHAT_TYPE_NOTICE` -> `BINARY_SetTipMessage`
    // and `CHAT_TYPE_BIG_NOTICE` -> `BINARY_SetBigMessage`
    // (`PythonNetworkStreamPhaseGame.cpp:1351-1358`). Two windows, one frame,
    // and the server picks (`Notice.big`).
    if (big) tips.big(text);
    else tips.setTip(text);
    // `AppendChat(kChat.type, buf)` is called for both branches, unconditionally
    // (`PythonNetworkStreamPhaseGame.cpp:1355-1364`) -- a big notice reaches
    // chat too, but as its own type, `CHAT_TYPE_BIG_NOTICE`, distinct from
    // `CHAT_TYPE_NOTICE`. `"notice-big"` carries that distinction here.
    chat.push({ kind: big ? "notice-big" : "notice", from: "", text });
  });
  // `/minfo` is still typed through ordinary SAY. Its GC answer is structured
  // separately from notices so the browser draws three server-owned seats and
  // never parses ruler names or a private treasury out of formatted chat.
  const monarchInfo = new MonarchInfoPanel(overlay);
  session.onMonarchInfo((info) => monarchInfo.update(info));

  // Conversations and the quest log. Both are pure sinks for what the server
  // sends: the dialogue answers with the server's own token and the log sends
  // back the server's own row index, and neither advances anything by itself.
  const questDialog = new QuestDialogPanel(overlay, {
    onReply: (token, choice) => {
      // `dragon_soul_refine.quest:4-7` answers this row with
      // `ds.open_refine_window()`, whose packet opens the refinement window
      // (`char_dragonsoul.cpp:114-133`, `interfacemodule.py:1028-1035`).
      const refine = alchemistRefineChoice;
      alchemistRefineChoice = null;
      if (refine !== null && refine.token === token && refine.choice === choice) {
        souls.openRefine(session.souls, session.bag);
      }
      session.questReply(token, choice);
    },
    // Three of the seven NPCs with something to say are also dealers, so a
    // click can mean both. The conversation wins the screen; the sale is still
    // there when it closes.
    onOpened: () => bag.exitSellMode(),
  });
  questPrompt = new QuestPromptPanel(overlay, {
    onInput: (token, text) => session.questInput(token, text),
    onConfirm: (token, accept) => session.questConfirm(token, accept),
    onItem: (token, cell) => session.questItem(token, cell),
    onOpened: () => bag.exitSellMode(),
  });
  questPrompt.setBag(session.bag);
  const questLog = new QuestLogPanel(overlay, {
    onOpen: (index) => session.questInfo(index),
    // The row's other body -- the read-only description the original opens from
    // the character sheet's own task list (`uicharacter.py:660` sends
    // `idx | 0x80000000`). The row click stays on `button`.
    onDescribe: (index) => session.questInfo(index, true),
  });
  const questLetters = new QuestLetterList(overlay, {
    onOpen: (index) => session.questInfo(index),
  });
  questLog.update(session.questLog);
  questLetters.update(session.questLog);
  session.onQuestDialog((page) => {
    const index = talkingToAlchemist ? page.choices.findIndex((c) => c.trim() === DS_REFINE_CHOICE) : -1;
    alchemistRefineChoice = index >= 0 ? { token: page.token, choice: index + 1 } : null;
    talkingToAlchemist = false;
    questDialog.update(page);
  });
  session.onQuestInput((prompt) => questPrompt?.updateInput(prompt));
  session.onQuestConfirm((prompt) => questPrompt?.updateConfirm(prompt));
  session.onQuestItem((prompt) => questPrompt?.updateItem(prompt));
  session.onQuestLog((entries) => {
    questLog.update(entries);
    questLetters.update(entries);
  });
  const questBeacons = new QuestTargetBeacons();
  session.onQuestTarget((event) => {
    minimap.updateQuestTarget(event);
    questBeacons.update(event, renderer.effects, (xMm, yMm) => renderer.groundAtMm(xMm, yMm));
  });
  // `SendNPCPosition` at `Entergame` (`input_login.cpp:548`): the map screen's
  // markers, from the server's own regen rows rather than a list shipped here.
  session.onNpcPositions((markers) => {
    minimap.setNpcMarkers(markers);
  });

  const refine = new RefinePanel(overlay, {
    sfx,
    onRefine: (slot) => session.refine(slot),
    // `RefineDialogNew.CancelRefine` uses a 255 slot sentinel
    // (`uirefine.py:418-424`); Escape and the visible Cancel button must
    // release the same pending offer.
    onCancel: () => session.refine(255),
  });
  session.onRefineOffer((offer) => refine.update(offer));
  session.onRefineResult((result) => refine.settle(result));

  // The cube counter. Same shape as the smith and for the same reason: the
  // menu, the odds, the costs and the material counts are all the server's,
  // and the panel sends bag cells and repaints on what comes back. What it
  // does *not* send is which recipe -- see `ui/cube.ts`.
  const cube = new CubePanel(overlay, {
    onMake: (slots) => session.cubeMake(slots),
  });
  session.onCubeRecipes((recipes) => cube.open(recipes, session.bag));
  session.onCubeResult((result) => cube.settle(result));
  session.onCubeClosed(() => cube.close());

  const souls = new DragonSoulPanel(overlay, {
    onMove: (source, dest) => session.dragonSoulMove(source, dest),
    onDeck: (deck) => session.dragonSoulDeck(deck),
    onRefine: (operation, cells, stones) => {
      session.dragonSoulRefine(operation, cells, stones);
    },
    // The other half of `onArm` above. `dest` already carries
    // `DRAGON_SOUL_SLOT_BASE`, so this is the same two-cell send the bag makes
    // for its own pairings -- no new message, and no field naming what is in
    // either cell.
    onUseFromBag: (slot, dest) => {
      // The arm is spent here as well as in the panel that offered it: the
      // `Bag` frame the server sends back would clear it a moment later, but
      // until then a click on a bag cell would fire a second `UseItemToItem`
      // naming a slot whose item has already gone.
      bag.disarm();
      session.useItemToItem(slot, dest);
    },
    // `SendShopSellPacketNew(slot, count, DRAGON_SOUL_INVENTORY)`
    // (`uidragonsoul.py:418`). The window travels in the cell number, the way
    // it does for `onUseFromBag` above: the original's binding drops the third
    // argument (`PythonNetworkStreamModule.cpp:877-888`) and the server then
    // sells the *bag* cell of that number (`shop_manager.cpp:272`).
    onSell: (_npcId, cell, count) =>
      session.sellItem(DRAGON_SOUL_SLOT_BASE + cell, count),
    onItemLink: (link) => chat.paste(link),
  });
  session.onDragonSouls((frame) => {
    if (souls.showing) souls.update(frame, session.bag);
    // `qualified` is a one-way flag (`char_dragonsoul.cpp:50-60`); the task
    // bar's own button tooltip follows it the same way the original's
    // `DragonSoulGiveQuilification` callback does, independent of whether
    // the panel itself happens to be open right now.
    taskbar.setDragonSoulQualified(frame.qualified);
  });
  session.onDragonSoulRefined((result) => souls.settle(result));

  const exchange = new ExchangePanel(overlay, {
    onOffer: (slot) => session.exchangeOffer(slot),
    onWithdraw: (slot) => session.exchangeWithdraw(slot),
    onGold: (amount) => session.exchangeGold(amount),
    // The token, and it comes from the panel rather than from here: it has to
    // be the one that was drawn, not the one that is current. See `exchange.ts`.
    onAccept: (token) => session.exchangeAccept(token),
    onCancel: () => session.exchangeCancel(),
    onInfo: (text) => chat.push({ kind: "system", from: "", text }),
  });
  exchange.setBag(session.bag);
  session.onExchange((state) => exchange.update(state));

  const safebox = new SafeboxPanel(overlay, {
    onDeposit: (slot, cell) => session.safeboxDeposit(slot, cell),
    onWithdraw: (cell, slot) => session.safeboxWithdraw(cell, slot),
    onMove: (source, destination) => session.safeboxMove(source, destination),
    onClose: () => session.closeSafebox(),
    // `ChangePasswordDialog.OnAccept` (`uisafebox.py:179`), typed the way the
    // player would type it.
    onChangePassword: (oldPassword, newPassword) =>
      session.say(`/safebox_change_password ${oldPassword} ${newPassword}`),
    // `uisafebox.py:462` / `uiinventory.py:739`'s grab-into-cursor instant.
    // See `SafeboxActions.onPick`.
    onPick: () => sfx.play("pick"),
  });
  safebox.setBag(session.bag);
  session.onSafebox((box) => {
    safebox.update(box);
  });
  // The mall's door (`ui/mall.ts`) shares this exact dialog with the
  // storeroom rather than drawing a second one -- code exists once. What
  // differs is where OK sends the digits, so the last `ask()` caller records
  // which lock it raised the prompt for.
  let passwordFor: "safebox" | "mall" = "safebox";
  const password = new PasswordDialog(overlay, {
    onAccept: (value) => {
      if (passwordFor === "mall") {
        mallAwaitingOpen = true;
        // `do_mall_password` (`cmd_general.cpp:917-950`), reached the same
        // way the player would type it.
        session.say(`/mall_password ${value}`);
      } else {
        session.openSafebox(value);
      }
    },
  });
  intoWindows = (held) => {
    exchange.setBag(held);
    safebox.setBag(held);
  };

  // The party. A sink like every panel above it: the roster, who is standing
  // with the leader and what the group is currently worth are all the server's,
  // and none of the four buttons here changes anything locally.
  const party = new PartyPanel(overlay, {
    // The token comes from the panel rather than from here, and that is the
    // whole safety property: it has to be the token that was *drawn*, not the
    // one that is current. See `ui/party.ts`.
    onAnswer: (token, accept) => session.partyAnswer(token, accept),
    // `player.GetCharacterDistance(partyLeaderVID)` (`game.py:969`). The panel
    // turns a Yes pressed further than 5000 legacy units away into a No, in
    // silence, which is what the original does.
    distanceTo: (id) => {
      const inviter = session.entity(id);
      if (inviter === undefined || inviter === null) return null;
      const here = selfPosition();
      return Math.hypot(inviter.xMm - here.xMm, inviter.yMm - here.yMm);
    },
    // `do_party_request_accept` / `do_party_request_deny`
    // (`cmd_general.cpp:1399-1425`) name the asker's vid, not a token.
    onRequestAnswer: (vid, accept) =>
      session.say(`/party_request_${accept ? "accept" : "deny"} ${vid}`),
    // `PartyMemberInfoBoard.OnMouseLeftButtonDown` targets the linked member
    // (`uiparty.py:341-348`). The tracker emits the ordinary authoritative
    // target frame on its next change; the roster never invents a character.
    onSelect: (memberId) => target.select(memberId),
    onLeave: () => session.partyLeave(),
    onKick: (slot, rosterRevision) => session.partyKick(slot, rosterRevision),
    onMode: (mode) => session.partyExpMode(mode),
    onAttacker: (slot, assign, rosterRevision) =>
      session.partyAttacker(slot, assign, rosterRevision),
    onRole: (slot, role, assign, rosterRevision) =>
      session.partyRole(slot, role, assign, rosterRevision),
    onHeal: () => session.partyHeal(),
    // `SendExchangeStartPacket` then `SendExchangeItemAddPacket(slot, 0)`
    // (`uiparty.py:347-352`): the trade is opened and the held cell put on it
    // in one gesture. The arm is spent in the bag too, for `onUseFromBag`'s
    // reason.
    onOfferTo: (memberId, slot) => {
      bag.disarm();
      session.exchangeStart(memberId);
      session.exchangeOffer(slot);
    },
  });
  party.setSelf(session.selfId);
  session.onParty((roster) => {
    // Re-stated on every roster because `selfId` is zero until the first
    // Welcome, and a panel built before it would never know which row is yours.
    lastParty = roster;
    party.setSelf(session.selfId);
    party.update(roster);
    questLetters.arrange(party.showing);
  });
  session.onPartyInvitation((invitation) => party.invited(invitation));
  session.onTargeted((targeted) => {
    // Your own name, when the quarry is you. `session.entity` is the table of
    // *remote* entities and a client is never in its own -- so without this
    // line the one selection a player most wants to see on the roster, the one
    // aimed at them, is the only one that draws nothing.
    const quarry =
      targeted.target === session.selfId
        ? session.displayName
        : (session.entity(targeted.target)?.name ?? "");
    party.focused(targeted.who, targeted.target, quarry);

    // Close (or open) the human-visible board the instant this client's own
    // selection is confirmed or refused, rather than leaving it to whichever
    // animation frame next rebuilds `HudModel`. See `Hud.setConfirmedTarget`.
    if (targeted.who === session.selfId) {
      hud.setConfirmedTarget(
        confirmedTargetView(
          targeted,
          targeted.target === 0 ? null : session.entity(targeted.target),
          duelStateWith(session.duels, session.selfId, targeted.target),
          socialFor(targeted.target),
        ),
      );
    }
  });

  // The guild. A sink like the party above it, and for the same reason: the
  // roster, the ranks and what this player's own rank is are all the server's,
  // and none of the four controls here changes anything locally.
  // `CGuildMarkUploader::__LoadSymbol` refuses anything but a 64x128 picture
  // before it sends a byte (`GuildMarkUploader.cpp:119-128`); the picture is
  // re-encoded as the JPEG the server keeps and the insignia draws.
  const pickSymbol = async (file: File): Promise<void> => {
    const image = await createImageBitmap(file).catch(() => null);
    if (image === null || image.width !== 64 || image.height !== 128) {
      chat.push({ kind: "system", from: "", text: "A guild symbol must be a 64x128 picture." });
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = 64;
    canvas.height = 128;
    canvas.getContext("2d")?.drawImage(image, 0, 0);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
    if (blob !== null) session.uploadGuildSymbol(new Uint8Array(await blob.arrayBuffer()));
  };
  const guild = new GuildPanel(overlay, {
    onFound: (name) => session.guildCreate(name),
    // The token comes from the panel rather than from here -- it has to be the
    // one that was *drawn*. See `ui/guild.ts`.
    onAnswer: (token, accept) => session.guildAnswer(token, accept),
    onLeave: () => session.guildLeave(),
    onDisband: () => session.guildDisband(),
    // Ranks, the bank and contribution. A grade index or a roster row goes
    // out, never a permission set and never a player -- see `ui/guild.ts`.
    onRank: (grade, name, mayInvite, mayKick, mayNotice, maySkill) =>
      session.guildRank(grade, name, mayInvite, mayKick, mayNotice, maySkill),
    onPromote: (slot, grade, rosterRevision) =>
      session.guildPromote(slot, grade, rosterRevision),
    onExpel: (slot, rosterRevision) => session.guildExpel(slot, rosterRevision),
    onGeneral: (slot, assign, rosterRevision) =>
      session.guildGeneral(slot, assign, rosterRevision),
    onHandOver: (slot, rosterRevision) =>
      session.guildHandOver(slot, rosterRevision),
    onBank: (amount, withdraw) => session.guildBank(amount, withdraw),
    onOffer: (amount) => session.guildOffer(amount),
    onChargePower: (amount) => session.guildChargePower(amount),
    onSkillUp: (skill) => session.guildSkillUp(skill),
    onUseSkill: (skill) => session.guildUseSkill(skill),
    onAddQuickSkill: (skill) => {
      const index = quickslots.firstEmpty();
      if (index !== null) quickslots.bindSkill(index, skill);
    },
    onBindQuickSlot: (quickIndex, skill) =>
      quickslots.bindSkill(quickslots.globalIndex(quickIndex), skill),
    onPickSymbol: (file) => void pickSymbol(file),
    onWarChallenge: (choice) => session.guildWarChallenge(choice),
    onWarRefuse: (choice) => session.guildWarRefuse(choice),
    onPostComment: (content) => session.guildPostComment(content),
    onDeleteComment: (slot, boardRevision) =>
      session.guildDeleteComment(slot, boardRevision),
    onRefreshComments: () => session.guildRefreshComments(),
    onPlaceBuilding: (vnum, x, y, rotation) => session.guildPlaceBuilding(vnum, x, y, rotation),
    sitingOrigin: () => ({ xMm: session.predictor.state.xMm, yMm: session.predictor.state.yMm }),
  });
  guild.setBag(session.bag);
  const sitingCameraMemory: SitingCameraMemory = { saved: null, applied: guild.siting.cameraLocks };
  // The board asks for both guilds' signs itself: `ui.MarkBox.SetIndex` reads
  // one out of the shared atlas the original keeps in step behind the scenes
  // (`uiguild.py:410-411,424-425`), and `RequestEmblem` is this build's only
  // way to get one.
  const warBoard = new WarBoard(overlay, (guildId) => session.requestEmblem(guildId));
  session.onGuild((roster) => {
    // `guild.MainPlayerHasAuthority(guild.AUTH_ADD_MEMBER)` (`uitarget.py:398`)
    // is a function of the reader's grade (`guild.h:232`), which is exactly
    // what `your_grade` and the grade table say.
    mayInviteToGuild =
      roster.guild_id !== 0 &&
      (roster.grades[roster.your_grade - 1]?.may_invite ?? roster.your_grade === MASTER_GRADE);
    guildMemberNames = new Set(roster.members.map((member) => member.name));
    guild.update(roster);
    guildQuickRows = guildQuickSkills(roster);
    quickslots.updateSkills([...session.skills.slots, ...guildQuickRows]);
    paintTaskbarQuick();
    warBoard.setOwnGuild(roster.guild_id, roster.name);
    warBoard.update(session.guildWarArena);
  });
  session.onGuildInvitation((invitation) => guild.invited(invitation));
  // A quest-originated, server-only event.  It opens the same form as the
  // normal guild key; the later `GuildCreate` remains the only request and
  // names only the human-entered string.
  session.onGuildFounding(() => guild.requestFounding());
  session.onGuildWarMenu((menu) => guild.updateWar(menu, session.guildWarArena));
  session.onGuildWarArena((arena) => {
    guild.updateWar(session.guildWarMenu, arena);
    warBoard.update(arena);
    warObserverCount = arena.observer_count;
  });
  session.onGuildComments((comments) => guild.updateComments(comments));
  session.onGuildLand((land) => guild.updateLand(land));
  // `RecvLandPacket`: the atlas's plots, the guild's buildable areas and the
  // owners' symbols. Registered again when the roster lands, because on join
  // the server sends `Guild` after `LandList` (`GameLoop.on_joined`), where
  // 2014's own guild id came ahead of it in the character's own add packet
  // (`input_login.cpp:546,580`).
  minimap.setGuildNames(session.guildNames);
  minimap.setGuildEmblems(session.guildEmblems, (ids) => session.wantEmblems(ids));
  let lands: LandListFrame["lands"] = [];
  const registerLands = (): void =>
    recvLandList(lands, session.guildId, minimap, guild, (ids) => session.wantSymbols(ids));
  session.onLandList((list) => {
    lands = list.lands;
    registerLands();
  });
  session.onGuild(() => registerLands());

  // ---- the guild emblem. A sink like the roster above it, and it takes both
  // of its permissions from the same frame: which guild, and whether this
  // player is its master. Nothing here decides either -- see `ui/emblem.ts`,
  // and `GameLoop.on_upload_emblem` for the check that actually counts.
  const emblem = new EmblemPanel(overlay, {
    onUpload: (bgra) => session.uploadEmblem(bgra),
    onRequest: (guildId) => session.requestEmblem(guildId),
  });
  session.onGuild((roster) =>
    emblem.setGuild(roster.guild_id, roster.your_grade === MASTER_GRADE),
  );
  session.onEmblem((frame) => {
    emblem.update(frame);
    warBoard.updateEmblem(frame);
  });

  const emotes = new EmotePanel(overlay, {
    onEmote: (emote, targetId) => session.emote(emote, targetId),
    // The token comes from the panel rather than from here -- it has to be the
    // one that was *drawn*. See `ui/emotes.ts`.
    onAnswer: (token, accept) => session.emoteAnswer(token, accept),
  });
  // Marks are created only by the server's `EmotePlayed` frame below. Keeping
  // them here, beside the other short-lived render events, means a button
  // press cannot make a gesture appear before the server accepts it.
  const gestures: GestureMark[] = [];
  // The act's one-shots on the local body, and the loops it stands in between
  // them -- see `render/entities.ts`'s `startActClips`.
  const motions: MotionMark[] = [];
  let nextMotionKey = 1;
  let playerMotionMode: MotionMode = "general";
  let lineState = FISHING_IDLE;
  let lineRod = 0;
  // Every body whose line is out, with the weapon it cast holding: `RecvFishing`
  // leaves a body fishing from a start to its end (`PythonNetworkStreamPhaseGame.cpp:3738-3759`),
  // and one that left view or changed weapon has had its motion mode refreshed
  // out of the fishing wait (`InstanceBase.cpp:2870-2895`).
  const anglers = new Map<number, number>();
  const floatEffects = new FishingFloatEffects();
  const runFloatEffects = (commands: readonly FloatEffectCommand[]): void => {
    for (const command of commands) {
      if (command.kind === "spawn") renderer.effects.spawn(command.effect, { ...command.at, instance: command.instance });
      else renderer.effects.deactivate(command.instance);
    }
  };
  const pushMotion = (
    entityId: number, clip: MotionMark["clip"] | null, then: MotionMark["clip"] | null = null,
  ): void => {
    if (clip === null) return;
    motions.push({ key: nextMotionKey++, entityId, clip, bornMs: performance.now(), then });
    while (motions.length > MAX_GESTURES) motions.shift();
  };
  let nextGestureKey = 0;
  let riding = false;
  // Kept as well as drawn: `__PressJKey` (`game.py:442-452`) branches on
  // `player.IsMountingHorse()`, and `Mount.riding` is this wire's word for it.
  session.onMount((mount) => {
    riding = mount.riding;
  });

  const fishing = new FishingPanel(overlay, {
    // A pull sends the facing as it is; a cast first sweeps for water the float
    // can land on and turns to it, and sends nothing where there is none
    // (`PythonPlayerInput.cpp:531-548`).
    onFish: () => {
      const me = session.predictor.state;
      if (isFishing(ownMotion().clip)) { // PythonPlayerInput.cpp:532-535
        session.fish(me.heading);
        return;
      }
      if (!canFishing(ownAffects(), ownMotion())) return; // PythonPlayerInput.cpp:538
      const cast = fishingHeading(me.heading, me.xMm, me.yMm, (x, y) => world.isWater(x, y));
      if (cast === null) return;
      session.fish(cast);
    },
  });
  session.onFishing((line) => {
    fishing.update(line);
    lineState = line.state;
    lineRod = line.rod_vnum;
    if (playerMotionMode !== "digging") playerMotionMode = lineMode(lineState, lineRod, weaponVnumWorn(session.bag.worn));
  });
  session.onFishCaught((caught) => {
    fishing.caught(caught);
    // And in the chat as well as on the panel, because a catch is the thing a
    // player wants a record of -- the panel shows only the last one.
    chat.push({ kind: "system", from: "", text: describeCatch(caught) });
  });

  session.onFishingAct((act) => {
    const self = act.entity_id === session.selfId;
    const body = self ? session.predictor.state : session.entity(act.entity_id);
    // `RecvFishing` returns on a body it does not know (`PythonNetworkStreamPhaseGame.cpp:3730-3732`).
    if (body === null) return;
    const next = fishingAct(act.act, anglers.has(act.entity_id));
    if (next.fishing) anglers.set(act.entity_id, self ? 0 : (body as RemoteEntity).weapon);
    else anglers.delete(act.entity_id);
    pushMotion(act.entity_id, next.clip);
    // `StartFishing`'s float (`InstanceBaseMotion.cpp:53-64`), where the
    // `FishingEffectFlag` events land rather than on the body
    // (`ActorInstanceMotionEvent.cpp:175-184`).
    const float = act.act !== FISHING_ACT_START ? null : floatPoint(act.heading, body.xMm, body.yMm,
      (x, y) => renderer.waterHeightAt?.(x, y) ?? null, renderer.groundAtMm(body.xMm, body.yMm));
    const armour = self ? bodyVnumWorn(session.bag.worn) : (body as RemoteEntity).armour;
    const race = self ? session.selfRace : (body as RemoteEntity).race;
    runFloatEffects(floatEffects.act(act.entity_id, act.act, next.fishing, performance.now(), float,
      (clip) => renderer.attackClipDurationMs(armour, race, clip)));
  });

  const mining = new MiningPanel(overlay, {
    onMine: (heading) => session.mine(heading),
  });
  session.onMining((swing) => {
    mining.update(swing);
    playerMotionMode = swing.state === MINING_SWINGING
      ? "digging"
      : lineMode(lineState, lineRod, weaponVnumWorn(session.bag.worn));
  });
  session.onMineResult((result) => {
    mining.resolved(result);
    // And in the chat, for the catch's reason: the panel keeps only the last
    // swing and a run of them is what a player wants a record of.
    chat.push({ kind: "system", from: "", text: describeSwing(result) });
  });

  // ---- OX quiz event. The panel returns the token it actually drew and one
  // bit. It never sees the bank or the answer before the result.
  const oxevent = new OXEventPanel(overlay, {
    onAnswer: (token, answer) => session.answerOX(token, answer),
  });
  session.onOXQuestion((question) => oxevent.update(question));
  session.onOXResult((result) => oxevent.showResult(result));

  // The native client opens this locked window from the server's AutoBan quiz
  // packet (`uiautoban.py:64-85`). Keep the challenge on the same production
  // wire path as OX: the panel only renders/selects, while answer and refresh
  // requests remain server-owned (`uiautoban.py:107-126`).
  const autoban = new AutoBanQuizPanel(overlay, {
    onAnswer: (answer) => session.say(`/autoban_answer ${answer}`),
    onRefresh: () => session.say("/autoban_refresh"),
    now: () => performance.now(),
  });
  session.onAutoBanQuiz((quiz) => autoban.open(quiz.quiz, quiz.duration_seconds));

  const dungeon = new DungeonPanel(overlay, {
    onUseDoor: () => session.useDungeonDoor(),
  });
  session.onDungeon((state) => dungeon.update(state));

  session.onWarp(() => {
    renderer.effects.clear();
    frameFx.clear();
    target.clear();
  });
  session.onMapChange((change) => {
    markCrossing("mapchange", { map_id: change.map_id, token: change.token !== "" });
    if (!rememberCrossing(sessionStore(), change.map_id, change.token)) {
      chat.push({
        kind: "system",
        from: "",
        text: "cannot travel: this browser will not remember the journey",
      });
      return;
    }
    markCrossing("reload");
    location.reload();
  });
  const pets = new PetPanel(overlay, {
    onDismiss: () => session.dismissPet(),
  });
  session.onPolymorphed((shape) => {
    pets.setShape(shape, performance.now());
    // Shown unasked when a transformation starts, because it carries a
    // countdown and a list of things that have just stopped working -- a
    // player whose skill bar has gone dead needs the sentence in front of
    // them, not behind a key they do not know about.
    if (shape.model !== "") pets.setVisible(true);
  });
  session.onPetSummoned((pet) => pets.setPet(pet));

  const arena = new ArenaPanel(overlay, {
    // The token comes from the panel rather than from here -- it has to be the
    // one that was *drawn*. See `ui/arena.ts`.
    onAnswer: (token, accept) => session.arenaAnswer(token, accept),
    onStopWatching: () => session.arenaWatch(0),
  });
  arena.setSelf(session.selfId);
  session.onArenaInvitation((invitation) => arena.invited(invitation));
  session.onArenaDuel((duel) => {
    // Re-stated on every frame for `party.setSelf`'s reason: `selfId` is zero
    // until the first Welcome, and a panel built before it would never know
    // which of the two columns is yours.
    arena.setSelf(session.selfId);
    arena.update(duel);
    minimapObserverCount = duel.duel_id === 0 ? 0 : duel.observer_count;
  });
  session.onArenaResult((result) => {
    arena.setSelf(session.selfId);
    arena.finished(result);
    // And in the chat as well as on the screen, because the result box is
    // dismissable and the score is the thing a player wants a record of.
    chat.push({ kind: "system", from: "", text: describeResult(result) });
  });

  // A player's own stall, both halves. The builder composes one; the window is
  // what everybody -- the seller included -- looks at afterwards. See
  // `ui/pcshop.ts` for why neither of them decides anything.
  const stallBuilder = new StallBuilder(overlay, {
    onOpen: (sign, listings) => session.openStall(sign, listings),
    onInfo: (text) => chat.push({ kind: "system", from: "", text }),
    sfx,
  });
  const stall = new StallPanel(overlay, {
    onBuy: (slot) => session.buyFromStall(slot),
    onClose: () => session.leaveStall(),
    onPackUp: () => session.closeStall(),
    onInfo: (text) => chat.push({ kind: "system", from: "", text }),
    onShopping: (mode) =>
      cursor.set(mode === "buy" ? CursorShape.BUY : mode === "sell" ? CursorShape.SELL : CursorShape.NORMAL),
  });
  stall.setSelf(session.selfId);
  session.onStall((window_) => {
    // Re-stated every frame for `arena.setSelf`'s reason: `selfId` is zero
    // until the first Welcome, and a panel built before it cannot tell its own
    // stall from a stranger's -- which here decides whether the footer offers
    // to pack up or explains how to buy.
    stall.setSelf(session.selfId);
    // And the purse, for the priming reason above: affordability is drawn from
    // the last `Bag`, and a buyer who has not spent anything this session has
    // had exactly one.
    stall.setGold(session.bag.gold);
    stall.update(window_);
  });
  session.onStallSign((entityId, sign) => {
    if (entityId !== session.selfId) return;
    // This player's own stall went up or came down. The builder closes on the
    // first, which is the only honest signal that the request worked: it costs
    // a Bundle and is refused for eight reasons, so a panel that closed on the
    // button would be telling a player they were trading when they were not.
    ownStallIsOpen = sign !== "";
    if (ownStallIsOpen) stallBuilder.close();
    chat.push({
      kind: "system",
      from: "",
      text:
        sign !== ""
          ? `Your stall is open: ${sign}. You cannot move or touch your bag until you pack up.`
          : "You packed up your stall.",
    });
  });
  session.onStallSold((sale) => {
    // The one thing about a sale the `Bag` frame beside it cannot say. In the
    // chat rather than a floater because it is a record a seller wants to
    // scroll back through -- a stall's whole afternoon is a list of these.
    const item = sale.count > 1 ? `${sale.name} x${sale.count}` : sale.name;
    const taxed = sale.tax > 0 ? ` (${sale.tax} withheld)` : "";
    chat.push({
      kind: "system",
      from: "",
      text: `${sale.buyer_name} bought ${item} for ${sale.price}${taxed}.`,
    });
  });

  const marriage = new MarriageAsk(overlay, {
    // The token comes from the dialog and is the one it *drew*. Two questions
    // can be on screen at once and one of them may be a divorce.
    onAnswer: (token, accept) => session.answerMarriage(token, accept),
  });
  // The last frame, for the harness alone. There is no window to read it off
  // any more (`ui/marriage.ts`), and `in_wedding_hall` -- which is
  // `marriage.in_my_wedding()`, the antecedent of NPC 9011's ceremony row at
  // `marriage_manage.quest:209` -- is otherwise invisible from the page.
  let lastMarriage: MarriageStatusFrame | null = null;

  const whisperWindows = new WhisperWindows(overlay, {
    onSend: (name, text) => session.whisper(name, text),
  });
  openWhisper = (name) => whisperWindows.open(name);
  session.onWhisper((from, text, refusal) => {
    const receipt = whisperWindows.receive(from, text, refusal);
    // `RecvWhisper` announces `RECEIVE_MESSAGE` only the first time a name
    // gets a button with no dialog open for it, not on every message that
    // arrives while it stays minimized (`interfaceModule.py:1438-1450`,
    // `locale_game.txt:390` -- "%s sent you a message.").
    if (receipt === "new-tab") {
      chat.push({ kind: "system", from: "", text: `${from} sent you a message.` });
    }
  });

  const friends = new FriendsPanel(overlay, {
    onClose: () => setSocialOpen(false),
    onAdd: (name) => session.addFriend(name),
    onAnswer: (token, accept) => session.answerFriend(token, accept),
    onRemove: (name) => {
      session.removeFriend(name);
      chat.push({
        kind: "system",
        from: "",
        text: `${name} is off your list. You may still be on theirs.`,
      });
    },
    onBlock: (whisper, invite, exchange, partyInvite, guildInvite) =>
      session.setBlockMode(whisper, invite, exchange, partyInvite, guildInvite),
    // The original's own double-click gesture (`uimessenger.py:107-108`)
    // opens `OpenWhisperDialog`, not a prefilled chat line -- this used to
    // read `chat.prefill(\`/w ${name} \`)` before `ui/whisper.ts` existed.
    onWhisper: (name) => whisperWindows.open(name),
    // The Messenger's own `GuildButton` (`uimessenger.py:367,377,498-499`):
    // a plain forward to the separate, fuller Guild window, never a second
    // roster drawn inside the friends panel -- see `ui/friends.ts`.
    onOpenGuild: () => guild.toggle(),
  });
  session.onFriendList((list) => {
    friendNames = new Set(list.friends.map((row) => row.name));
    friends.update(list);
  });
  session.onMarriageStatus((status) => {
    lastMarriage = status;
    marriage.update(status);
    // The rest of the frame is the messenger's Family group, which is the only
    // place the original ever draws a spouse (`uimessenger.py:591-597,
    // 661-704`). There is no marriage window: the five verbs are NPC dialogue
    // rows of `marriage_manage.quest` -- see `ui/marriage.ts`.
    friends.setLover(status);
    // The original wedding map announces `PlayMusic 1 wedding` from
    // `server/src/game/src/wedding.cpp:217-221,266-277`. This status is the
    // browser's server-confirmed equivalent of being in that hall; map changes
    // reload the page and therefore restore the field theme.
    if (status.in_wedding_hall) music.play("wedding");
  });
  session.onGuild((roster) => friends.updateGuild(roster));

  let socialOpen = false;
  const setSocialOpen = (open: boolean): void => {
    socialOpen = open;
    friends.setVisible(open);
    social.setExpanded(open);
  };
  const social = new SocialOpener(overlay, {
    onToggle: () => setSocialOpen(!socialOpen),
  });

  const auction = new AuctionPanel(overlay, {
    onRefresh: () => session.openAuction(),
    onList: (cell, starting, buyNow) => session.listLot(cell, starting, buyNow),
    onBid: (slot, total, boardRevision) => session.placeBid(slot, total, boardRevision),
    onBuyNow: (slot, boardRevision) => session.buyLotNow(slot, boardRevision),
    onClaim: (slot, boardRevision) => session.claimLot(slot, boardRevision),
  });
  session.onAuctionBoard((board) => auction.update(board));
  session.onAuctionNews((news) => {
    chat.push({ kind: "system", from: "", text: describeNews(news) });
  });
  const mall = new MallPanel(overlay, {
    onCheckout: (cell, slot) => session.mallCheckout(cell, slot),
    // `uisafebox.py:604`: `MallWindow.Close` sends `/mall_close`
    // (`do_mall_close`, `cmd_general.cpp:952-960`) before anything local.
    onClose: () => session.say("/mall_close"),
  });
  const giftbox = new GiftBox(overlay, {
    onOpenMall: () => {
      mall.setBag(session.bag);
      mall.toggle();
    },
  });
  session.onMall((window_) => {
    mall.update(window_);
    giftbox.setHasGift(window_.cells.length > 0);
    if (mallAwaitingOpen) {
      mallAwaitingOpen = false;
      mall.setBag(session.bag);
      if (!mall.showing) mall.open();
    }
  });
  openMall = () => {
    mall.setBag(session.bag);
    mall.toggle();
    if (mall.showing) giftbox.hide();
  };

  session.onEmoteRequest((request) => emotes.requested(request));
  session.onEmotePlayed((played) => {
    // The legacy receiver started a native motion here
    // (`PythonNetworkStreamPhaseGame.cpp:1605`). This build has no matching
    // actor clips, so retain the same server-confirmed event as a visible,
    // short-lived glyph; it is what every watcher gets, including the actor.
    gestures.push({
      key: nextGestureKey++,
      actorId: played.actor,
      targetId: played.target,
      emote: played.emote,
      bornMs: performance.now(),
    });
    while (gestures.length > MAX_GESTURES) gestures.shift();
    const actor =
      played.actor === session.selfId
        ? session.displayName
        : (session.entity(played.actor)?.name ?? "");
    const targetName =
      played.target === 0
        ? ""
        : played.target === session.selfId
          ? "you"
          : (session.entity(played.target)?.name ?? "somebody");
    chat.push({
      kind: "system",
      from: "",
      text: describeEmote(played.emote, actor, targetName),
    });
  });

  const sheet = new CharacterPanel(overlay, {
    onSpend: (stat) => session.spendPoint(stat),
    onReclaim: (stat) => session.say(reclaimCommand(stat)),
    onPvpMode: (mode) => session.setPvpMode(mode),
  });
  sheet.update(session.standing);
  sheet.updateSpirit(session.spirit);
  sheet.updatePvpMode(session.pvpMode);
  // Game Options' PvP Mode and Block rows are two more requesters of the same
  // session facts the character sheet already uses -- see `requestPvpMode`/
  // `requestBlockMode` above, assigned here now that `session` exists.
  requestPvpMode = (mode) => session.setPvpMode(mode);
  requestBlockMode = (whisper, messengerInvite, exchange, partyInvite, guildInvite, partyRequest) =>
    session.setBlockMode(whisper, messengerInvite, exchange, partyInvite, guildInvite, partyRequest);
  gameOptions.updatePvpMode(session.pvpMode);
  // Assigned once CharacterWindow exists; Standing/Skills frames before that
  // still reach the sheet, and the first `check` seeds the nags from the
  // pools already held on the session (`uigamebutton.py:CheckGameButton`).
  let gameButtons: GameButtonWindow | null = null;
  const energyBar = new EnergyBar(overlay);
  energyBar.update(session.standing.energy, session.standing.maxEnergy);
  session.onStanding((standing) => {
    sheet.update(standing);
    gameButtons?.check(standing.points, session.skills.points);
    energyBar.update(standing.energy, standing.maxEnergy);
  });
  session.onSpirit((spirit) => sheet.updateSpirit(spirit));
  session.onPvpMode((mode) => {
    sheet.updatePvpMode(mode);
    gameOptions.updatePvpMode(mode);
  });

  // The skill bar. Cooldowns count down locally and are *replaced* by each
  // server frame rather than reconciled against it: the server owns whether a
  // skill is ready, and a client that quietly disagreed would be showing a
  // player a button that does nothing.
  const skillbar = new SkillBar(overlay, {
    // For the group-0 preview only (`skillgroups.ts`). Read live rather than
    // captured, exactly as `__SelectSkillGroup` reads `net.GetMainActorRace()`
    // (`uicharacter.py:1172`): this bar is built before the first entity delta
    // lands and a captured race would be zero for everyone.
    race: () => session.selfRace,
    status: () => skillStatusOf(session.standing, session.spirit.maxSp),
    onBindQuickSlot: (quickIndex, vnum) => {
      // A drop names a local index; the table is global
      // (`PythonPlayer.cpp:704-707`). A skill slot carries the vnum -- see
      // `ui/quickslot`'s docstring for why this build's identity and not the
      // original's skill-array index.
      quickslots.bindSkill(quickslots.globalIndex(quickIndex), vnum);
    },
    onUse: (vnum, targetId) => {
      if (!canUseSkill(session.dead, ownStallIsOpen)) return false;
      if (!actorCanUseSkill(ownAffects(), ownMotion())) return false; // PythonPlayerSkill.cpp:456
      if (ownMotion().usingSkill) return false; // PythonPlayerSkill.cpp:514-517
      const aimed = targetId === 0 ? null : (session.entity(targetId) ?? null);
      const here = session.predictor.state;
      const reach = skillReach({
        vnum,
        selfId: session.selfId,
        selfXMm: here.xMm,
        selfYMm: here.yMm,
        target: aimed,
        attackable: aimed !== null && isAttackable(aimed),
        targetIsPlayer: aimed !== null && aimed.kind === EntityKind.PLAYER,
        // `POINT_BOW_DISTANCE` is not on this client's wire; the server's own
        // range check (`char_skill.cpp:2197`) still counts it.
        bowDistance: 0,
        reservedVnum: pursuit?.skill?.vnum ?? null,
      });
      if (reach.kind === "approach") {
        // `__SendUseSkill(dwSkillSlotIndex, 0)` (`PythonPlayerSkill.cpp:414-418`).
        if (reach.chargeStart) session.useSkill(vnum, 0);
        // `__ReserveUseSkill`, then `NEW_MoveToDestInstanceDirection` each
        // frame (`PythonPlayerSkill.cpp:420`, `PythonPlayerInputMouse.cpp:367`).
        const body = session.entity(reach.entityId)!;
        pursuit = { entityId: reach.entityId, talk: false, challenged: 0, skill: { vnum, rangeUnits: reach.rangeUnits } };
        walkTo.setDestination(
          here.xMm,
          here.yMm,
          { xMm: body.xMm, yMm: body.yMm, stopMm: reach.rangeUnits * 10 - SKILL_APPROACH_SLACK_MM },
          performance.now(),
        );
        return false;
      }
      session.useSkill(vnum, targetId);
      // `NEW_UseSkill` intercepts the skill's own motion on the caster, and a
      // motion that flies, warps or strikes a target keeps the target for as
      // long as it plays (`InstanceBaseBattle.cpp:339`, `ActorInstanceFly.cpp:111-116`).
      const clip = skillMotionFor(vnum, session.skills.slots.find((slot) => slot.vnum === vnum)?.level ?? 0);
      pushMotion(session.selfId, clip);
      const holdMs = clip === null
        ? null
        : renderer.skillClipHoldMs(
          bodyVnumWorn(session.bag.worn), session.selfRace, clip,
          session.selfMount === 0 ? null : weaponVnumWorn(session.bag.worn),
        );
      if (holdMs !== null) target.holdWhileFlying(performance.now() + holdMs);
      session.combat.cast(
        {
          casterId: session.selfId,
          vnum,
          xMm: session.predictor.state.xMm,
          yMm: session.predictor.state.yMm,
        },
        performance.now(),
      );
      return true;
    },
    dashing: () => dashing(liveAffects),
    onTrain: (vnum) => session.trainSkill(vnum),
    target: () => target.id,
  });
  skillbar.update(session.skills);
  // A guild skill on a quick slot casts through `UseGuildSkill`, never the
  // player's own list (`SKILL_TYPE_GUILD`, `PythonPlayerSkill.cpp:119-121`).
  useQuickSkill = (vnum) => (isGuildSkill(vnum) ? session.guildUseSkill(vnum) : skillbar.useVnum(vnum));
  // The affects panel draws each buff with the icon of whatever applied it, and
  // the skill bar is where the client already knows a vnum's name and icon. So
  // the bar feeds it: no new wire field, and an affect from something not on
  // the bar still draws, with what it modifies for a label.
  let affectSources = session.skills.slots;
  affects.update([], affectSources);
  session.onSkills((skills) => {
    skillbar.update(skills);
    quickslots.updateSkills([...skills.slots, ...guildQuickRows]);
    paintTaskbarQuick();
    affectSources = skills.slots;
    gameButtons?.check(session.standing.points, skills.points);
  });
  session.onAffects((live) => {
    affects.update(live, affectSources);
    // Kept for the frame loop: each affect's loop effect plays on a bone of
    // the player's own body for as long as it is set
    // (`playersettingmodule.py:357-389`; `render/framefx.ts`).
    liveAffects = live;
  });
  session.onSpecialEffect((entityId, effect) => {
    if (playsPotionSound(effect) && entityId === session.selfId) sfx.play("eat_potion");
  });
  session.onSpecificEffect((entityId, effectFile) => {
    specificEffects.push({ entityId, effect: specificEffectKey(effectFile) });
  });
  session.onPointChange((_entityId, point) => {
    // `level_up.mse` carries `level_up.mss`, so the flare is heard wherever it is seen.
    if (POINT_EFFECT_NAMES.get(point) === "level_up") sfx.play("levelup");
  });
  setInterval(() => skillbar.tick(), TICK_MS);
  // One 253x361 CharacterWindow, four tabs — never four floating panels.
  // `characterwindow.py:14-22`; C/V/B/N select STATUS/SKILL/EMOTICON/QUEST
  // (`uicharacter.py:183-209`).
  const charwin = new CharacterWindow(overlay, {
    status: sheet.element,
    skill: skillbar.element,
    emoticon: emotes.element,
    quest: questLog.element,
  });
  skillbar.docked();
  // Unspent-point nags beside the taskbar (`uigamebutton.py` /
  // `gamewindow.py`). Clicks reuse the same CharacterWindow toggles as C/V.
  gameButtons = new GameButtonWindow(overlay, {
    onStatus: () => charwin.toggle("status"),
    onSkill: () => charwin.toggle("skill"),
    onBuild: () => guild.openBuilding(),
  });
  gameButtons.check(session.standing.points, session.skills.points);
  const taskbar = new TaskBar(overlay, {
    onCharacter: () => charwin.toggle("status"),
    onInventory: () => bag.toggle(),
    onMessenger: () => setSocialOpen(!socialOpen),
    onSystem: () => systemMenu.toggle(),
    // The Expand button's own row (`interfacemodule.py`'s `ToggleDragonSoulWindow`)
    // is a plain open/close toggle, the same as `souls.close()`/`.update()`
    // already give the dragon soul panel elsewhere in this file.
    onDragonSoul: () => {
      if (souls.showing) souls.cancel();
      else souls.update(session.souls, session.bag);
    },
    onQuickSlot: (index) => quickslots.fireLocal(index),
    // `__OnClickQuickPageUpButton`/`DownButton` are `player.SetQuickPage(
    // player.GetQuickPage() -/+ 1)` (`uitaskbar.py:623-627`), and the wrap is
    // `SetQuickPage`'s own (`PythonPlayer.cpp:692-699`).
    onPreviousPage: () => quickslots.setPage(quickslots.page - 1),
    onNextPage: () => quickslots.setPage(quickslots.page + 1),
  });
  taskbar.setDragonSoulQualified(session.souls.qualified);
  // The taskbar already draws the eight Slot_Base cells; the QuickSlotBar is
  // the key logic sitting on top of that geometry, so its own strip is
  // hidden (`locale/en/ui/taskbar.py:391-445`).
  quickslots.element.hidden = true;
  paintTaskbarQuick = () => {
    taskbar.setQuickPage(quickslots.page);
    taskbar.setQuickSlots(
      // Eight cells, and they are the current page's:
      // `LocalQuickSlotIndexToGlobalQuickSlotIndex` (`PythonPlayer.cpp:704-707`).
      Array.from({ length: 8 }, (_, local) => {
        const binding = quickslots.assigned(quickslots.globalIndex(local));
        if (binding === null) return null;
        if (binding.kind === "skill") {
          const skill = [...session.skills.slots, ...guildQuickRows].find((slot) => slot.vnum === binding.vnum);
          return {
            kind: "skill" as const,
            icon: skill?.icon ?? "",
            name: skill?.name ?? "",
            level: skill?.level ?? 0,
          };
        }
        if (binding.kind === "command") {
          const gesture = EMOTES.find((entry) => entry.id === binding.emotion);
          return {
            kind: "command" as const,
            emotion: binding.emotion,
            name: gesture?.label ?? `gesture ${String(binding.emotion)}`,
          };
        }
        // An item slot names a bag *cell* (`char_quickslot.cpp:63`), which the
        // server keeps pointing at the right item (`SyncQuickslot`, `:12-34`).
        const held = session.bag.slots.find((slot) => slot.slot === binding.cell);
        return {
          kind: "item" as const,
          vnum: held?.vnum ?? 0,
          name: held?.name ?? "",
          count: held?.count ?? 0,
          // The bag slot has carried the icon byte all along; the quick slot
          // dropped it and drew nothing for a vnum with no painting.
          itemKind: held?.kind ?? ITEM_KIND_OTHER,
        };
      }),
    );
  };
  paintTaskbarQuick();
  // `SetSlotCoolTime(slotNumber, coolTime, elapsedTime)` on every skill cell of
  // the page (`uitaskbar.py:801-804`), from the bar's own countdown.
  setInterval(() => {
    taskbar.setCoolTimes(
      Array.from({ length: 8 }, (_, local) => {
        const binding = quickslots.assigned(quickslots.globalIndex(local));
        if (binding === null || binding.kind !== "skill") return null;
        const full = session.skills.slots.find((slot) => slot.vnum === binding.vnum)?.cooldownTicks ?? 0;
        const left = skillbar.cooldownOf(binding.vnum);
        return full > 0 && left > 0 ? 1 - left / full : null;
      }),
    );
    // `CPythonPlayer::SetAffect`/`ResetAffect` off the character's own affect
    // flags (`PythonPlayerSkill.cpp:15-58`), into both slot windows.
    const toggles = activeToggleSkills(session.selfAffectFlags);
    skillbar.setActiveSkills(toggles);
    taskbar.setActiveSlots(
      Array.from({ length: 8 }, (_, local) => {
        const binding = quickslots.assigned(quickslots.globalIndex(local));
        return binding !== null && binding.kind === "skill" && toggles.has(binding.vnum);
      }),
    );
  }, TICK_MS);
  taskbar.update({
    hp: session.selfVitals.hp,
    maxHp: session.selfVitals.maxHp,
    hpRecovery: session.spirit.hpRecovery,
    sp: session.spirit.sp,
    maxSp: session.spirit.maxSp,
    spRecovery: session.spirit.spRecovery,
    stamina: staminaAt(session.stamina, performance.now()),
    maxStamina: session.stamina.maxStamina,
    exp: session.standing.exp,
    expToNext: session.standing.expToNext,
  });
  // Counted down locally between server frames, exactly as the skill bar counts
  // a cooldown: the server sends a duration at the moment of sending, never a
  // deadline, so a panel that only moved on a frame would sit still for a
  // second at a time.
  setInterval(() => affects.tick(1), TICK_MS);
  setInterval(() => auction.tick(), 1000);

  // The four horse-summoning items `__PressJKey` walks the bag for
  // (`game.py:450`), verbatim.
  const MOUNT_ITEM_VNUMS = [71114, 71116, 71118, 71120];
  // F1 is the browser's help key and F3 its find bar. Capture so the game
  // owns F1-F4 before the browser does; firing still lives on QuickSlotBar.
  // Only F1-F4: `onPressKeyDict` binds those four and no more
  // (`game.py:330-333`), so F5-F8 are the browser's again.
  window.addEventListener(
    "keydown",
    (event) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (/^F[1-4]$/.test(event.key)) event.preventDefault();
    },
    true,
  );
  // Ctrl+Q's own flag, mirroring `interfaceModule.IsQBHide` (`:48`) -- the
  // original's global toggle bit for `__PressQKey`'s Ctrl half, below.
  let questButtonsHidden = false;
  window.addEventListener("keydown", (event) => {
    if (composing(event)) return;
    if (chat.focused || guild.typing || friends.typing) return;
    if (questModalCapturesKeys(questDialog, questPrompt)) return;
    if (isSocialShortcut(event)) {
      event.preventDefault();
      setSocialOpen(!socialOpen);
      return;
    }
    if (event.metaKey) return;
    if (event.key === "Escape") {
      event.preventDefault();
      if (help.showing) {
        help.close();
        return;
      }
      if (charwin.showing) {
        charwin.hide();
        return;
      }
      // Flagged for lane S13 (owns this dispatch block): `options` split into
      // `systemOptions` and `gameOptions` -- both checked here since either
      // may be the one currently open.
      if (systemOptions.showing) {
        systemOptions.setVisible(false);
        return;
      }
      if (gameOptions.showing) {
        gameOptions.setVisible(false);
        return;
      }
      if (bag.showing) {
        bag.toggle();
        return;
      }
      if (friends.showing) {
        friends.setVisible(false);
        return;
      }
      if (guild.showing) {
        guild.toggle();
        return;
      }
      if (shop.showing) {
        shop.dismiss();
        return;
      }
      if (mall.showing) {
        mall.hide();
        return;
      }
      if (costume.showing) {
        costume.close();
        return;
      }
      if (cube.showing) {
        cube.close();
        return;
      }
      if (refine.showing) {
        refine.close();
        return;
      }
      if (exchange.showing) {
        exchange.close();
        return;
      }
      if (souls.showing) {
        souls.cancel();
        return;
      }
      if (pets.showing) {
        pets.setVisible(false);
        return;
      }
      // `SystemDialog` is what ESC opens (`uisystem.py`); its own listener
      // consumes the key while it is already showing.
      systemMenu.toggle();
      return;
    }
    // F1-F8 are Fast Access Fields (capture listener above).
    if (event.ctrlKey) {
      if (event.ctrlKey && event.code === "KeyG") {
        // `__PressGKey`, `:472-474`.
        event.preventDefault();
        session.say("/ride");
      } else if (event.ctrlKey && event.code === "KeyH") {
        // `__PressHKey`, `:453-455`.
        event.preventDefault();
        session.say("/user_horse_ride");
      } else if (event.ctrlKey && event.code === "KeyB") {
        // `__PressBKey`, `:459-461`.
        event.preventDefault();
        session.say("/user_horse_back");
      } else if (event.ctrlKey && event.code === "KeyF") {
        // `__PressFKey`, `:466-468`.
        event.preventDefault();
        session.say("/user_horse_feed");
      } else if (event.ctrlKey && event.code === "KeyJ") {
        // `__PressJKey`, `:442-452`: mounted, dismount; otherwise use the first
        // mount item in the bag -- the four vnums are the original's own list,
        // and the commented-out `/user_horse_ride` above them is why this is an
        // item use rather than a command.
        event.preventDefault();
        if (riding) session.say("/unmount");
        else {
          const mountItem = session.bag.slots.find((slot) =>
            MOUNT_ITEM_VNUMS.includes(slot.vnum),
          );
          if (mountItem !== undefined) session.useItem(mountItem.slot);
        }
      } else if (/^Digit[1-9]$/.test(event.code)) {
        event.preventDefault();
        const index = Number(event.code.slice(5)) - 1;
        const token = index < EMOTICON_KEYED_COUNT ? emoticonToken(index) : null;
        if (token !== null && emoticons.play(session.selfId, index, Date.now())) {
          session.say(token);
        }
      } else if (event.ctrlKey && event.code === "KeyQ") {
        event.preventDefault();
        questButtonsHidden = !questButtonsHidden;
        questLetters.element.hidden = questButtonsHidden || questLetters.count === 0;
      }
      return;
    }
    // ---- Alt+G. `__PressGKey`'s bare half (`:476-477`) opens the guild window
    // when `ShowNameFlag` is set, and that flag is the name-display key
    // (`DIK_LALT` -> `ShowName`, `:335`; released at `:414`). Alt held is the
    // browser's reading of the same state.
    if (event.altKey) {
      if (event.altKey && event.code === "KeyG") {
        event.preventDefault();
        guild.toggle();
      }
      return;
    }
    if (event.code === "KeyI") {
      event.preventDefault();
      bag.toggle();
      return;
    }
    if (event.code === "KeyC") {
      event.preventDefault();
      charwin.toggle("status");
      return;
    }
    if (event.code === "KeyV") {
      event.preventDefault();
      charwin.toggle("skill");
      return;
    }
    if (event.code === "Backquote" || event.code === "KeyZ") {
      event.preventDefault();
      const me = session.predictor.state;
      const found = nearestLoot(session.others(performance.now()), me.xMm, me.yMm);
      if (found !== null) session.pickUp(found);
      return;
    }
    if (event.code === "KeyM") {
      event.preventDefault();
      minimap.pressM(event.shiftKey);
      return;
    }
    if (event.code === "NumpadAdd") {
      event.preventDefault();
      minimap.scaleUp();
      return;
    }
    if (event.code === "NumpadSubtract") {
      event.preventDefault();
      minimap.scaleDown();
      return;
    }


    if (event.code === "KeyJ") {
      // Bare J casts/pulls; Shift+J opens company. They share one arm so
      // neither can shadow the other, which the former pair of branches did.
      event.preventDefault();
      if (event.shiftKey) pets.toggle();
      else fishing.press();
      return;
    }
    if (event.code === "KeyN") {
      event.preventDefault();
      charwin.toggle("quest");
      return;
    }

    if (event.code === "KeyL") {
      event.preventDefault();
      chat.toggleLog();
      return;
    }
    // `DIK_X` is bound nowhere in the original's key dict (`game.py:345-386`).
    // `DungeonPanel` opens itself the moment there is something to say
    // (`ui/dungeon.ts`'s `update`) and its own button is the door, so the key
    // was removed rather than moved.
    if (event.code === "KeyH") {
      // `__PressHKey`'s `else` (`game.py:456-457`):
      // `self.interface.OpenHelpWindow()`. The horse panel that had taken this
      // letter is the Ctrl half of the same key upstream (`/user_horse_ride`).
      event.preventDefault();
      help.toggle();
      return;
    }
    // `DIK_R` is `app.ZoomCamera(app.CAMERA_TO_NEGATIVE)` (`game.py:351`),
    // held, and lives with the other held keys in `input/index.ts`. Restarting
    // in town is the death screen's own button (`DeathScreen`, above), and
    // training a horse is the stable NPC's dialogue, not a key.
    if (event.code === "KeyB") {
      event.preventDefault();
      charwin.toggle("emoticon");
      return;
    }
    if (event.code === "KeyO") {
      event.preventDefault();
      if (souls.showing) souls.cancel();
      else souls.update(session.souls, session.bag);
      return;
    }
  });

  session.onChat((from, text, channel) => {
    // `game_open_safebox` (`questlua_game.cpp:70-77`) is
    // `ChatPacket(CHAT_TYPE_COMMAND, "ShowMeSafeboxPassword")`. The only
    // password this build can accept is `000000` (`ClientManager.cpp:599-619`).
    if (channel === ChatChannel.COMMAND) {
      if (text === "ShowMeSafeboxPassword") {
        passwordFor = "safebox";
        password.ask();
      }
      // `/click_mall` (`do_click_mall`, `cmd_general.cpp:2299-2302`) is one
      // line: `ChatPacket(CHAT_TYPE_COMMAND, "ShowMeMallPassword")`. The
      // server does not open anything here either -- it asks for the same
      // box the storeroom uses.
      if (text === "ShowMeMallPassword") {
        passwordFor = "mall";
        password.ask();
      }
      // `CHARACTER::__OpenPrivateShop` (`char_item.cpp:1501`):
      // `ChatPacket(CHAT_TYPE_COMMAND, "OpenPrivateShop")`. The original
      // client maps that to the stall-name dialog; here the builder is
      // the same panel KeyY opens.
      if (text === "OpenPrivateShop") {
        stallBuilder.setBag(session.bag);
        stallBuilder.open();
      }
      // `SendMyShopPriceListCmd` (`char_item.cpp:1510-1516`): one
      // `MyShopPriceList <vnum> <price>` line per kind of item the seller has
      // priced before, sent just ahead of `OpenPrivateShop` on the first
      // Bundle use of a session (`char_item.cpp:1521-1549`).
      if (text.startsWith("MyShopPriceList ")) {
        const [, rawVnum, rawPrice] = text.split(" ");
        const vnum = Number(rawVnum);
        const price = Number(rawPrice);
        if (Number.isInteger(vnum) && Number.isInteger(price)) {
          stallBuilder.rememberPrice(vnum, price);
        }
        return;
      }
      if (text.startsWith("StoneDetect ")) {
        return;
      }
      const requester = parsePartyRequest(text);
      if (requester !== null) {
        party.requested(requester, session.entity(requester)?.name ?? "");
        return;
      }
      // `PartyRequestDenied` (`char.cpp:4238`, and the same line on a deny and
      // on the ten-second lapse) is the asker's answer. No receiver ships
      // either, and the original has no notice window of its own for it, so it
      // is said where every other refusal in this build is said.
      if (text === "PartyRequestDenied") {
        chat.push({ kind: "system", from: "", text: PARTY_REQUEST_DENIED });
        return;
      }
      return;
    }
    chat.push({ kind: kindOfChannel(channel), from, text });
  });
  // The Whisper frame's own content now lives in its dedicated window
  // (`ui/whisper.ts`, wired above near `FriendsPanel`) rather than the main
  // chat log -- `RecvWhisper` never puts message text in the main window
  // either, only a button and (once) a notice (`interfaceModule.py:1438-1450`).


  let selfComboStep: { readonly step: number; readonly lastMs: number } | undefined;
  /** `m_isPreInput`: an attack input held over until the playing step's
   *  direct input time -- see `comboPreInput`. */
  let selfPreInput = false;
  let lastSwingIntervalMs = REFERENCE_SWING_MS;
  const remoteComboSteps = new Map<number, { readonly step: number; readonly lastMs: number }>();

  /** Where an entity stands right now, on the ground, or null when this client
   *  has not been told about it. The one lookup both fly paths use. */
  const groundOf = (id: number) => {
    const who = id === session.selfId ? session.predictor.state : (session.entity(id) ?? null);
    return who === null
      ? null
      : { xMm: who.xMm, yMm: who.yMm, zMm: renderer.groundAtMm(who.xMm, who.yMm) };
  };

  // `RecvCreateFlyPacket` (`PythonNetworkStreamPhaseGame.cpp:2640-2657`): the
  // server throws a mote of experience, life or spirit from one entity to
  // another, or opens a firework over one. Nothing else about the event is on
  // the frame, and nothing else is needed -- the type is the flight script.
  // `__Push`'s motion half (`ActorInstanceSync.cpp:41-50`): a shove past 150
  // units intercepts the body's motion with `NAME_DAMAGE_FLYING` and queues
  // `NAME_STAND_UP`; the position half is `EntityInterpolator.push`.
  session.onSyncPosition((entityId, shove) => {
    if (shove.knockDown) pushMotion(entityId, "knockdown", "standUp");
  });
  // `RecvCharacterMovePacket`'s skill functions (`InstanceBase.cpp:1464-1492,1524-1554`):
  // somebody in sight began a skill motion.
  session.onSkillMotion((entityId, motion, grade) => {
    pushMotion(entityId, observedSkillClip(session.entity(entityId)?.kind === EntityKind.MONSTER, motion, grade));
  });

  session.onCreateFly((launch) => {
    launchIndexedFly(frameFx, launch, groundOf, performance.now(), (id) =>
      (id === session.selfId ? session.predictor.state : (session.entity(id) ?? null))?.heading ?? null);
  });

  // `RecvFlyTargetingPacket` (`PythonNetworkStreamPhaseGame.cpp:2542-2578`):
  // somebody in sight has pointed a bow. An aim at an entity is already drawn
  // by the blow that lands on it, so only the aim at a bare point -- the shot
  // the original's own else-branch exists for -- puts an arrow in the air here.
  session.onAimed((aim) => {
    const flight = groundAimFlight(
      { shooter: aim.shooter, target: aim.target, xMm: aim.x_mm, yMm: aim.y_mm },
      (id) => (id === session.selfId ? session.predictor.state : (session.entity(id) ?? null)),
      (xMm, yMm) => renderer.groundAtMm(xMm, yMm),
    );
    if (flight === null) return;
    renderer.effects.launchArrow(
      `aim:${aim.shooter}:${performance.now()}`,
      flight.from,
      flight.to,
      performance.now(),
      null,
    );
  });

  session.onCombat((event) => {
    const outcome = decodeCombatFlags(event.flags);
    const me = session.predictor.state;
    const at = (id: number) =>
      id === session.selfId ? me : (session.entity(id) ?? null);

    // The projectile a ranged skill flies: the original spawns it at the cast
    // clip's FLY frame (`ActorInstanceMotionEvent.cpp:253-299`); this build
    // keys it off the blow, whose `skill` says which script
    // (`render/projectiles.ts`). Nothing for a swing, a melee skill or a buff.
    launchFromBlow(frameFx, event, groundOf, performance.now());

    const attacker = at(event.attackerId);
    if (
      attacker !== null &&
      shouldPresentRemoteNormalAttack(event, session.selfId)
    ) {
      const place = spatial(attacker.xMm - me.xMm, attacker.yMm - me.yMm, SOUND_RANGE_MM);
      const remoteAttacker = session.entity(event.attackerId);
      if (remoteAttacker !== null && remoteAttacker.kind === EntityKind.PLAYER) {
        const nowMs = performance.now();
        const previous = remoteComboSteps.get(event.attackerId);
        const nextStep = advanceComboStep(previous, nowMs);
        remoteComboSteps.set(event.attackerId, nextStep);
        const attackClip = renderer.weaponAttackClip(remoteAttacker.weapon);
        const clip = event.comboMotion
          ? comboMotionClip(attackClip, event.comboMotion)
          : nextComboClip(attackClip, nextStep.step);
        // How fast this attacker's motions play, the same way `entities.ts`'s
        // own combo loop derives it -- see `swingPlaybackRatio`. The previous
        // clip's own authored gap against the gap that actually elapsed.
        const intervalMs = previous === undefined ? 0 : nowMs - previous.lastMs;
        const previousClip = previous === undefined
          ? null
          : nextComboClip(attackClip, previous.step);
        const ratio = swingPlaybackRatio(
          previousClip === null
            ? null
            : renderer.comboSwingIntervalMsFor(
                remoteAttacker.armour, remoteAttacker.race, previousClip, 100),
          intervalMs,
        );
        if (!playAttackSound(sfx, clip, place, remoteAttacker.race, ratio)) sfx.play("swing", place);
      } else {
        sfx.play("swing", place);
        if (remoteAttacker !== null) {
          sfx.play(creatureAttackSound(remoteAttacker.name), place);
        }
      }
    }
    if (outcome.missed || event.targetId === 0) return;

    const target = at(event.targetId);
    const where = target ?? attacker;
    if (where === null) return;
    const place = spatial(where.xMm - me.xMm, where.yMm - me.yMm, SOUND_RANGE_MM);

    if (!session.combat.locallyPresented(event.attackerId)) {
      // The player's own grunt is not positioned: it comes from where they are.
      if (event.targetId === session.selfId) {
        sfx.play("hurt");
      } else {
        sfx.play("hit", place);
        const name = session.entity(event.targetId)?.name ?? "";
        sfx.play(creaturePainSound(name), place);
      }
    }

    if (fallsHere(outcome, event.targetId, session.selfId) && event.targetId === session.selfId) {
      sfx.play("die_player");
    }

    if (outcome.killed && event.targetId !== session.selfId) {
      const name = session.entity(event.targetId)?.name ?? "";
      sfx.play(creatureDieSound(name), place);
    }
  });

  // Bound to the overlay canvas, which is the element the pointer stream reaches;
  // the GL canvas sits behind it and never sees an event.
  renderer.camera.mmPerPixel = renderer.camera.clampZoom(loadZoom());
  const zoom = new ZoomControl(renderer.camera, canvas);

  // The pointer's line through the world, from where it meets two heights, and
  // the body whose defending sphere it crosses first (`__NEW_Pick`,
  // `PythonCharacterManager.cpp:684-797`). `POINTER_RAY_RISE_MM` is only a
  // second point on the line.
  const POINTER_RAY_RISE_MM = 2_000;
  const spherePick = (pointer: { x: number; y: number }, bodies: readonly RemoteEntity[]): number | null => {
    const low = renderer.camera.unproject(pointer.x, pointer.y, 0);
    const high = renderer.camera.unproject(pointer.x, pointer.y, POINTER_RAY_RISE_MM);
    if (low === null || high === null) return null;
    const eye = renderer.camera.eyeMm;
    return pickByDefendingSphere(
      bodies.map((body) => ({
        id: body.id,
        dead: body.maxHp > 0 && body.hp <= 0,
        xMm: body.xMm,
        yMm: body.yMm,
        zMm: renderer.groundAtMm(body.xMm, body.yMm),
        spheres: renderer.defendingSpheres?.(body.id) ?? [],
      })),
      { originMm: [low.xMm, low.yMm, 0], dirMm: [high.xMm - low.xMm, high.yMm - low.yMm, POINTER_RAY_RISE_MM] },
      [eye.xMm, eye.yMm, eye.zMm],
    );
  };

  let lastMousePx: { x: number; y: number } | null = null;
  canvas.addEventListener("pointermove", (event) => {
    if (event.pointerType !== "mouse") return;
    lastMousePx = { x: event.clientX, y: event.clientY };
    // The canvas is `position: fixed` at the viewport origin (`style.css`'s
    // `#view`), so a client coordinate is already a canvas coordinate -- the
    // same assumption `TouchInput.onDown` states and `taps` hands to the pick.
    const pickable = session.others(performance.now()).filter(canPickInstance);
    const pointer = { x: event.clientX, y: event.clientY };
    // A line that crosses no defending sphere keeps `pickTarget`'s thumb radius
    // round the body's feet, where `__NEW_Pick` would pick nothing.
    const picked = spherePick(pointer, pickable)
      ?? pickTarget(pickable, pointer, renderer.camera, (x, y) => renderer.groundAtMm(x, y));
    const subject = picked === null ? null : session.entity(picked);
    cursor.hover(subject, {
      hasStall: subject !== null && session.stallSigns.has(subject.id),
    });
  });
  // A pointer that has left the world is over nothing in it, and the shape
  // underneath -- the shop's Buy or Sell, or the plain arrow -- is what the
  // panel it moved onto should show. Without this the last thing hovered in
  // the world stays on the counter's cells.
  canvas.addEventListener("pointerleave", () => cursor.hover(null));
  let liveAffects: readonly Affect_[] = [];

  renderer.camera.snapTo(session.predictor.state.xMm, session.predictor.state.yMm);

  const selfMotion = new SelfMotion();
  selfMotion.snapTo(session.predictor.state);

  (window as unknown as Record<string, unknown>).__metin3 = {
    session,
    renderer,
    creatureSound: (name: string) => ({
      die: creatureDieSound(name),
      pain: creaturePainSound(name),
      attack: creatureAttackSound(name),
    }),
    // The phase curtain, so a browser check can assert what the player could
    // actually see at a given moment rather than screenshotting and guessing.
    // `opacity` 1 is "the world is covered"; `down` is "it never will be
    // again". `tools/e2e/fresh_account.mjs` reads both.
    curtain: () => ({
      opacity: phaseCurtain.opacity,
      down: phaseCurtain.down,
      selfBodyDraws: renderer.renderedCharacterParts?.(session.selfId)?.bodyDraws ?? 0,
    }),
    // Turn the scene paint off (or back on) on a running client, for the same
    // reason `?draw=0` exists -- a check that has already logged in and wants
    // the rest of its round trips at frame rate. `render/drawgate.ts`.
    draw: (on: boolean | DrawLevel) => {
      scene.set(on);
      renderer.setReducedDraw?.(scene.reduced);
    },
    drawing: () => scene.drawing,
    drawLevel: () => scene.level,
    /** Frame phases and, on the WebGL path, the per-pass GL breakdown. */
    frameProfile: () => ({
      ...frameProfile,
      passes: (renderer as { profile?: unknown }).profile ?? null,
    }),
    zoom,
    systemOptions,
    gameOptions,
    /** The last `MarriageStatus`. Read-only, and the harness's only view of
     *  it now that the panel is gone. */
    marriageStatus: () => lastMarriage,
    // The actual two audio elements are intentionally not visual UI, but this
    // read-only hook lets the browser audit assert their selected source and
    // mixed volume rather than mistaking receipt of FieldMusic for playback.
    music,
    world,
    bag,
    /** The Fast Access Fields, so a browser check can read what a drag or a
     *  Ctrl-click actually bound rather than screenshotting eight cells. */
    quickslots,
    // The selection, so a harness can put a target in the same place a tap
    // does and then press the same key a player does. Read-through to the same
    // object the key handlers use -- there is no second copy of "who is
    // selected", which is the only thing that would make this a lie.
    target,
    // The gesture bar, so a harness can press the buttons a player presses
    // rather than calling `session.emote` behind them. Pressing the button is
    // the point: it is what proves the target the panel holds is the target the
    // selection put there, which a direct call would step over.
    emotes,
    // And the fishing panel, so a harness can wait for a bite it has no way to
    // predict -- the delay is 10 to 40 seconds and is never on the wire.
    fishing,
    // And the mining panel, for the same reason: a swing lasts between ten and
    // thirty seconds and how long is rolled on the server, so a harness that
    // guessed would be guessing at the one number this message refuses to
    // carry inbound.
    mining,
    // And the dungeon panel, which is a place rather than a timer: the
    // player is standing at a door or standing on a floor, and either way the
    // panel is describing where they are.
    dungeon,
    pets,
    emblem,
    auction,
    // The stall builder, whose player-facing opener is the server's own
    // `OpenPrivateShop` command (`char_item.cpp:1501`, handled in `onChat`);
    // the harness opens it directly rather than using the sign item.
    stallBuilder,
    // The pending click-to-walk, so a harness can tell "the click was heard and
    // the character is on its way" from "the click did nothing" -- which is
    // exactly the difference the bug this hook was added for consisted of, and
    // which a position sample alone cannot see in the first frames of a walk.
    walkTo,
    // The duel panel, so a harness can read the scoreboard and the verdict.
    // There is no other way to ask: the score is drawn from a frame nothing
    // else keeps, and `ArenaResult` is deliberately not stored on the
    // connection -- it is an event, not a state.
    arena,
    // The actual entry title, so the browser harness can assert the thing a
    // player reads rather than infer it from the terrain request log.
    mapName,
    propModelMatrix: modelMatrix,
    tips,
  };
  window.addEventListener("resize", () => renderer.resize());

  const footsteps = new FootstepTracker();
  let footstepXMm = session.predictor.state.xMm;
  let footstepYMm = session.predictor.state.yMm;

  /** Squared-distance test in world millimetres, so the approach checks below
   *  read as the question they are asking. No square root: nothing here needs
   *  the distance, only whether it is under a gate. */
  const withinMm = (
    a: { readonly xMm: number; readonly yMm: number },
    b: { readonly xMm: number; readonly yMm: number },
    rangeMm: number,
  ): boolean => {
    const dx = a.xMm - b.xMm;
    const dy = a.yMm - b.yMm;
    return dx * dx + dy * dy <= rangeMm * rangeMm;
  };

  const engageNpc = (
    npc: { readonly id: number; readonly name: string; readonly flags: number },
    challenged: number,
  ): void => {
    session.clickNpc(npc.id);
    if (isOreVein(npc)) {
      const me = session.predictor.state;
      const target = session.entity(npc.id);
      if (target !== null) {
        mining.aimAt(headingToward(target.xMm - me.xMm, target.yMm - me.yMm));
      }
    }
    if (isAlchemist(npc)) {
      // Nothing opens on the click: the Alchemist's menu comes first, and only
      // its refinement row opens the window (see `onReply` above).
      talkingToAlchemist = true;
    }
    if (isArenaManager(npc)) {
      session.arenaChallenge(challenged);
    }
    if (isShopkeeper(npc)) {
      bag.enterSellMode(npc.id, npc.name);
      // The soul window sells out of itself too (`uidragonsoul.py:366-367`),
      // off the same cursor mode, so it learns about the counter at the same
      // moment the bag does.
      souls.enterSellMode(npc.id, npc.name);
      if (!bag.showing) bag.toggle();
      // And ask what they sell. Both halves of the counter open on one click,
      // because a player who walked up to a dealer wants the shop, not two
      // gestures. The panel that appears is the server's answer, never this
      // call: the click sends and waits.
    }
  };

  let pursuit: {
    entityId: number;
    talk: boolean;
    challenged: number;
    /** `MODE_USE_SKILL`: the cast this walk ends in (`PythonPlayerInput.cpp:854-864`). */
    skill?: { vnum: number; rangeUnits: number };
  } | null = null;
  let clickSwing: number | null = null;

  const spendHitFrames = (nowMs: number, others: readonly RemoteEntity[]): void => {
    for (const hit of hitFrames.due(nowMs)) {
      // Not for an arm this client has stopped presenting. A reconnect clears
      // the feed and its declaration (`CombatFeed.clear`), and a hit armed in
      // the world before it must not land in the world after -- the ids have
      // been reissued and the victim it would name is somebody else.
      if (!session.combat.locallyPresented(hit.attackerId)) continue;
      const me = session.predictor.state;
      const selected = target.id === null ? null : (session.entity(target.id) ?? null);
      // The selection first, then whatever is nearest in reach -- `pickVictim`,
      // the one rule `attackBeat` uses too, so what lands here is exactly what
      // the packet named.
      const victim = pickVictim(selected, others, me.xMm, me.yMm);
      // Nothing in the arc: the swing whiffs, visibly and audibly, and nothing
      // is drawn on anybody. That is the original too -- `AttackProcess` finds
      // no victim and `__ProcessDataAttackSuccess` is never reached.
      if (victim === null) continue;
      // `hit.dueMs`, not `nowMs`: the flinch clip and the ground mark both
      // want the instant the blade went through, and at the two frames a
      // second a headless capture manages the two are far apart.
      session.combat.impact(
        {
          entityId: victim.id,
          attackerId: session.selfId,
          xMm: victim.xMm,
          yMm: victim.yMm,
          // Local knowledge cannot say critical or blocked -- those are the
          // server's verdict, and they reach the screen on the number.
          style: "hit",
        },
        hit.dueMs,
      );
      const place = spatial(victim.xMm - me.xMm, victim.yMm - me.yMm, SOUND_RANGE_MM);
      sfx.play("hit", place);
      sfx.play(creaturePainSound(victim.name), place);
    }
  };

  const attackBeat = (nowMs: number): void => {
    // Computed once for the whole beat: the local hit test below and the
    // quarry resolution further down used to each re-fetch and re-filter
    // `session.others(nowMs)` themselves, twice over on every 20 Hz tick.
    const others = session.others(nowMs).filter(canPickInstance);
    // First, because a blow whose frame has arrived lands before this beat can
    // start the next swing -- and because the render loop on a loaded machine
    // may be half a second away.
    spendHitFrames(nowMs, others);
    // A click that is owed a swing holds the repeater down exactly as a key
    // would, and is released again the moment the beat spends it -- see
    // `clickSwing`. Ordering matters: the click is checked first so that a
    // player who is also holding the key is not released out from under.
    // `__OnPressSmart` again on every frame the left button stays down
    // (`PythonPlayerInputMouse.cpp:376-377`): an attackable body under it and
    // in reach is attacked by `__OnPressActor` (`PythonPlayerInput.cpp:306-356`).
    const pressedAt = touch.mouseHold();
    const pressedId = pressedAt === null
      ? null
      : spherePick(pressedAt, others)
        ?? pickTarget(others, pressedAt, renderer.camera, (px, py) => renderer.groundAtMm(px, py));
    const pressedBody = pressedId === null ? null : (session.entity(pressedId) ?? null);
    const me = session.predictor.state;
    const pressing = pressedBody !== null && !isLoot(pressedBody) && clickBecomesSwing(
      pressedBody,
      Math.hypot(pressedBody.xMm - me.xMm, pressedBody.yMm - me.yMm),
      AUTO_TARGET_RANGE_MM,
      !ownStallIsOpen && canAttack(ownAffects(), ownMotion()),
    );
    if (pressing) target.select(pressedBody.id);
    const holding = keyboard.attacking || attackButton.held || pressing;
    if (holding || clickSwing !== null) attacks.press(nowMs);
    else attacks.release();
    const predictor = session.predictor;
    if (predictor === null) return;
    const canSwing = !ownStallIsOpen && canAttack(ownAffects(), ownMotion()); // PythonPlayerInput.cpp:737-765
    const weaponVnum = weaponVnumWorn(session.bag.worn);
    const footClip = renderer.weaponAttackClip(weaponVnum);
    const attackClip = riding ? mountedAttackClip(footClip) : footClip; // MODE_HORSE_<weapon> (InstanceBase.cpp:2896-2923)
    const armourVnum = bodyVnumWorn(session.bag.worn);
    const comboSkill = session.skills.slots.find((slot) => slot.vnum === SKILL_COMBO_VNUM);
    const chain = renderer.comboChainFor(session.selfRace, attackClip, currentComboType(
      riding, comboSkill?.level ?? 0, ((comboSkill?.flags ?? 0) & SKILL_COMBO_ACTIVE) !== 0));
    const previousStep = selfComboStep;
    const playingClip = previousStep === undefined
      ? null
      : nextComboClip(attackClip, previousStep.step, chain);
    const swingMs = playingClip === null
      ? 0
      : renderer.comboSwingIntervalMsFor(
          armourVnum, session.selfRace, playingClip,
          session.standing.attackSpeed)
        // A pack with no `ComboInputData` for this clip: fall back to the
        // server's gate rather than to nothing, so an incomplete pack swings
        // slowly instead of flooding the uplink.
        ?? swingIntervalMs(session.standing.attackSpeed);
    lastSwingIntervalMs = swingMs;
    // The one combo the pack carries follows `InputComboAttackCommand`
    // (`ActorInstanceBattle.cpp:156-213`) on the motion's own clock: a click
    // outside the playing step's input window plays nothing.
    const beat = chain === null
      ? null
      : comboBeat(
        previousStep ?? null,
        playingClip === null ? null : renderer.attackClipEntry(armourVnum, session.selfRace, playingClip),
        renderer.activeClip(session.selfId), nowMs, session.standing.attackSpeed,
        chain.length, riding, selfPreInput, attacks.held && canSwing);
    selfPreInput = beat?.preInput ?? false;
    const clickStep = beat === null ? undefined : beat.step;
    // A click that neither plays a step nor is held over as a pre-input is
    // spent on nothing (`ActorInstanceBattle.cpp:173-183`).
    if (clickSwing !== null && clickStep === null && !selfPreInput) {
      clickSwing = null;
      if (!holding) attacks.release();
    }
    if (!((attacks.held || selfPreInput) && canSwing && session.swingDue) || clickStep === null) return;
    selfPreInput = false;
    attacks.spend(nowMs);
    // Spent. One click, one blow; the walk that brought the character here is
    // already cleared by the pursuit resolver that armed this.
    if (clickSwing !== null) {
      clickSwing = null;
      if (!holding) attacks.release();
    }
    const selected = target.id === null ? null : (session.entity(target.id) ?? null);
    const quarry = pickVictim(selected, others, predictor.state.xMm, predictor.state.yMm);
    if ((selected === null || !isAttackable(selected)) && quarry !== null) {
      target.select(quarry.id);
    }
    const reserve = heldKeyReservesApproach(selected, quarry, canSwing);
    if (reserve !== null) {
      const body = session.entity(reserve);
      if (body !== null) {
        pursuit = { entityId: reserve, talk: false, challenged: 0 };
        walkTo.setDestination(
          predictor.state.xMm,
          predictor.state.yMm,
          { xMm: body.xMm, yMm: body.yMm, stopMm: APPROACH_STOP_MM },
          nowMs,
        );
        return;
      }
    }
    // Armed at the top of the very input tick that is about to sample the
    // gait, so a held movement key cannot walk through the swing clip -- the
    // ordering the render loop used to get by arming before `pumpInput()`,
    // now guaranteed by where this runs rather than by who calls it first.
    session.armSwingLock();
    session.combat.swing(
      session.selfId, nowMs, clickStep, clickStep === undefined ? undefined : nextComboClip(attackClip, clickStep, chain));
    const previousClip = playingClip;
    const windowMs = previousClip === null
      ? COMBO_CHAIN_GAP_MS
      : renderer.comboChainGapMsFor(armourVnum, session.selfRace, previousClip)
        ?? COMBO_CHAIN_GAP_MS;
    const nextStep = clickStep === undefined ? advanceComboStep(previousStep, nowMs, windowMs) : { step: clickStep, lastMs: nowMs };
    selfComboStep = nextStep;
    const clip = nextComboClip(attackClip, nextStep.step, chain);
    const intervalMs = previousStep === undefined ? 0 : nowMs - previousStep.lastMs;
    const clipMs = renderer.attackClipDurationMs(armourVnum, session.selfRace, clip);
    const ratio = swingPlaybackRatio(
      playingClip === null
        ? null
        : renderer.comboSwingIntervalMsFor(armourVnum, session.selfRace, playingClip, 100),
      intervalMs,
    );
    if (!playAttackSound(sfx, clip, {}, session.selfRace, ratio)) sfx.play("swing");
    // Every race's `bow/attack.msa` carries the fly event (`MotionEventType 6`)
    // that holds the target for as long as the shot's motion plays.
    if (renderer.attackIsRanged(weaponVnum)) target.holdWhileFlying(nowMs + clipMs / ratio);
    const hitStartMs = renderer.attackIsRanged(weaponVnum)
      ? null
      : renderer.attackHitStartMs(armourVnum, session.selfRace, clip);
    if (hitStartMs !== null) hitFrames.arm(session.selfId, nowMs + hitStartMs / ratio);
    session.combat.presentsOwnBlows(hitStartMs === null ? null : session.selfId);
    const lockTicks = swingLockTicksFor(clipMs, session.standing.attackSpeed);
    const nextSwingTicks = ticksForMs(
      renderer.comboSwingIntervalMsFor(
        armourVnum, session.selfRace, clip, session.standing.attackSpeed)
      ?? swingIntervalMs(session.standing.attackSpeed));
    if (quarry !== null) {
      const me = predictor.state;
      session.faceTowards(
        headingToward(quarry.xMm - me.xMm, quarry.yMm - me.yMm));
    }
    if (quarry !== null && isAttackable(quarry)) {
      // The aim goes out first and the swing that looses it second, which is
      // the order the original's fly event and shoot packet leave in
      // (`PythonPlayerEventHandler.cpp:232-244`).
      const aim = aimForSwing(quarry, renderer.attackIsRanged(weaponVnum));
      if (aim !== null) session.flyTarget(aim.target, aim.xMm, aim.yMm);
      session.attack(quarry.id, lockTicks, nextSwingTicks, chain === null ? 0 : nextStep.step);
    } else if (chain !== null) {
      // Every combo step goes out, hit or not (`OnAttack`, `PythonPlayerEventHandler.cpp:102-108`).
      session.attack(0, lockTicks, nextSwingTicks, nextStep.step);
    } else {
      session.armSwingLock(lockTicks, nextSwingTicks);
    }
  };

  const frameProfile = { frames: 0, sceneMs: 0, uiMs: 0, loopMs: 0 };
  let lastMs = performance.now();
  const frame = (nowMs: number): void => {
    const elapsedMs = nowMs - lastMs;
    const dtMs = Math.min(100, elapsedMs);
    lastMs = nowMs;
    frames.push(dtMs);

    // A second finger means the player is zooming, not steering. Release the
    // touch before consuming camera rotation so a strip drag that happened in
    // the same gap cannot turn the camera for one stale frame.
    if (zoom.pinching) touch.release();

    renderer.camera.rotate(input.cameraYawDeltaDeg(elapsedMs));
    renderer.camera.pitchBy(input.cameraPitchDeltaDeg(elapsedMs));
    // R/F zoom and T/G pitch, held, for the reason above: the original starts
    // them on key-press (`game.py:351,353`, `__PressFKey`/`__PressGKey`) and
    // stops them on release (`:404-407`), which is a per-frame rate.
    const zoomFactor = input.cameraZoomFactor(elapsedMs);
    if (zoomFactor !== 1) {
      renderer.camera.mmPerPixel = renderer.camera.clampZoom(
        renderer.camera.mmPerPixel * zoomFactor,
      );
    }

    // The attack beat is deliberately *not* here any more: it runs once per
    // simulation tick, from `attackBeat` above, so a slow renderer no longer
    // slows the player's swings, their combo chain or their own swing lock.
    // What stays in this loop is only what is genuinely per-frame.

    // The siting window's own ghost blocks like a real building only while its
    // PC blocker would hold (`__EnablePCBlocker`, `root/uiguild.py:2370-2375`);
    // set every frame, ahead of the pump below, so this frame's own movement
    // already sees it.
    world.setGhostFootprint(guild.siting.previewFootprintMm);
    runFloatEffects(floatEffects.step(nowMs, (id) => id === session.selfId || session.entity(id) !== null));

    // Before anything is read off the predictor: the frame is the most reliable
    // wake-up this client has, and the clock behind this decides how many input
    // frames elapsed time owes rather than handing out one per call. Drawing a
    // state that is one pump stale would show the player their own input latency.
    session.pumpInput();

    const predictor = session.predictor;
    // The body to paint. Everything else in this frame -- targeting, ranges,
    // the swing, the wire -- keeps reading `predictor.state`, which is the
    // authority; this is only what the renderer draws and what the camera
    // follows.
    const body = selfMotion.follow(predictor.state, predictor.stats, predictor.world, dtMs);
    const others = session.others(nowMs).filter(canPickInstance);

    {
      const state = predictor.state;
      const dxMm = state.xMm - footstepXMm;
      const dyMm = state.yMm - footstepYMm;
      footstepXMm = state.xMm;
      footstepYMm = state.yMm;
      const steps = footsteps.advance(Math.hypot(dxMm, dyMm), state.gait, dtMs, DEFAULT_STATS.runMmS);
      for (let i = 0; i < steps; i++) sfx.play("step_ground");
    }

    // `BUILD_OnUpdate` then `BUILD_OnMouseLeftButtonDown`
    // (`root/interfacemodule.py:1603-1627`): the sited building follows the
    // ground under the pointer, and the window spends the click first.
    if (guild.siting.mode === "positioning" && lastMousePx !== null) {
      const sited = pickGround(
        lastMousePx,
        renderer.camera,
        (x, y) => renderer.groundAtMm(x, y),
        renderer.groundAtMm(predictor.state.xMm, predictor.state.yMm),
      );
      if (sited !== null) guild.sitingPoint(sited.xMm, sited.yMm);
    }
    const taken = touch.taps.take();
    const tap = taken !== null && guild.sitingClick() ? null : taken;
    if (tap !== null) {
      const wasSelected = target.id === null ? null : (session.entity(target.id) ?? null);
      const challenged =
        wasSelected !== null && wasSelected.kind === EntityKind.PLAYER
          ? wasSelected.id
          : 0;
      const sphered = spherePick(tap, others);
      const picked = sphered !== null
        ? target.choose(sphered)
        : target.pick(others, tap, renderer.camera, (x, y) => renderer.groundAtMm(x, y));
      // A tap on a drop is a request to take it, not a request to select it.
      // Nothing on the ground can be attacked, so a selection there would be a
      // target the attack button could never spend, and the ring around it
      // would read as one.
      const chosen = picked === null ? null : session.entity(picked);
      if (chosen !== null && isLoot(chosen)) {
        session.pickUp(chosen.id);
        target.clear();
      } else if (
        chosen !== null &&
        chosen.kind === EntityKind.PLAYER &&
        session.stallSigns.has(chosen.id)
      ) {
        session.enterStall(chosen.id);
        target.clear();
      } else if (
        chosen !== null &&
        (chosen.kind === EntityKind.NPC || chosen.kind === EntityKind.BUILDING)
      ) {
        if (withinMm(predictor.state, chosen, APPROACH_RADIUS_MM)) {
          pursuit = null;
          engageNpc(chosen, challenged);
        } else {
          pursuit = { entityId: chosen.id, talk: true, challenged };
          walkTo.setDestination(
            predictor.state.xMm,
            predictor.state.yMm,
            { xMm: chosen.xMm, yMm: chosen.yMm, stopMm: APPROACH_STOP_MM },
            nowMs,
          );
        }
        target.select(chosen.id);
      } else {
        // Any other tap -- ground, a monster, empty air -- ends the trade.
        // Without this, walking off to fight something would leave the panel
        // quietly offering to sell to a dealer three screens away.
        if (bag.sellingTo !== null) bag.exitSellMode();
        souls.exitSellMode();
        if (shop.showing) shop.dismiss();
        pursuit = null;
        if (chosen !== null) {
          const started = walkTo.setDestination(
            predictor.state.xMm,
            predictor.state.yMm,
            { xMm: chosen.xMm, yMm: chosen.yMm, stopMm: APPROACH_STOP_MM },
            nowMs,
          );
          // Followed rather than aimed once, because a monster does not wait
          // where it was clicked. The block below re-reads its position every
          // frame; without it the character walks to where the wolf *was* and
          // stands there, which is what the first cut of this did.
          if (started) pursuit = { entityId: chosen.id, talk: false, challenged: 0 };
          // Already within reach: `__OnPressActor` swings immediately rather
          // than reserving the click (`PythonPlayerInput.cpp:431-443`), so
          // there is nothing to walk to and the walk that was just started is
          // stopped again.
          if (!isLoot(chosen) && clickBecomesSwing(
            chosen,
            Math.hypot(chosen.xMm - predictor.state.xMm,
                       chosen.yMm - predictor.state.yMm),
            AUTO_TARGET_RANGE_MM,
            !ownStallIsOpen && canAttack(ownAffects(), ownMotion()),
          )) {
            walkTo.clear();
            pursuit = null;
            target.select(chosen.id);
            clickSwing = chosen.id;
          }
        } else {
          const ground = pickGround(
            tap,
            renderer.camera,
            (x, y) => renderer.groundAtMm(x, y),
            renderer.groundAtMm(predictor.state.xMm, predictor.state.yMm),
          );
          if (ground !== null) {
            const walking = walkTo.setDestination(
              predictor.state.xMm,
              predictor.state.yMm,
              { xMm: ground.xMm, yMm: ground.yMm, stopMm: ARRIVAL_RADIUS_MM },
              nowMs,
            );
            if (walking) {
              renderer.effects.spawn(groundPickEffect(), {
                xMm: ground.xMm,
                yMm: ground.yMm,
                zMm: renderer.groundAtMm(ground.xMm, ground.yMm),
              });
            }
          }
        }
      }
    }

    if (pursuit?.skill !== undefined) {
      const reserved = pursuit.skill;
      const quarry = session.entity(pursuit.entityId) ?? null;
      const step = reservedSkillStep(predictor.state.xMm, predictor.state.yMm, quarry, reserved.rangeUnits);
      if (step === "cast") {
        // `SetTarget(m_dwVIDReserved)`, then `__UseSkill` again, and the
        // reservation is cleared only when that sends
        // (`PythonPlayerInputMouse.cpp:356-362`).
        walkTo.clear();
        target.select(pursuit.entityId);
        const reservation = pursuit;
        if (skillbar.useVnum(reserved.vnum) && pursuit === reservation) pursuit = null;
      } else if (step === "follow" && walkTo.walking) {
        walkTo.follow({ xMm: quarry!.xMm, yMm: quarry!.yMm, stopMm: reserved.rangeUnits * 10 - SKILL_APPROACH_SLACK_MM });
      } else {
        // Gone, or the walk ended short of it: a wall, or a hand on the keys,
        // both of which clear a reservation (`PythonPlayerInput.cpp:421-451`).
        pursuit = null;
      }
    } else if (pursuit !== null) {
      const quarry = session.entity(pursuit.entityId) ?? null;
      switch (resolvePursuit({
        quarry,
        talk: pursuit.talk,
        selfXMm: predictor.state.xMm,
        selfYMm: predictor.state.yMm,
        walking: walkTo.walking,
        reachMm: AUTO_TARGET_RANGE_MM,
        arriveMm: APPROACH_STOP_MM,
        canAct: !ownStallIsOpen && canAct(session.dead, session.selfFlags),
        quarryIsLoot: quarry !== null && isLoot(quarry),
      })) {
        case "drop":
          // The target is gone, or the walk ended short of it -- a wall
          // (`WalkTo`'s stall detector), or a hand on the keys.
          pursuit = null;
          break;
        case "swing":
          walkTo.clear();
          target.select(quarry!.id);
          clickSwing = quarry!.id;
          pursuit = null;
          break;
        case "talk":
          // The reserved click spent as a conversation: the same loop's
          // `__SendClickActorPacket` arm (`PythonPlayerInput.cpp:891-897`).
          // The server's "too far away to talk" (`quests.py:8133`) stays where
          // it is, as the last guard; this is the client not earning it.
          walkTo.clear();
          engageNpc(quarry!, pursuit.challenged);
          pursuit = null;
          break;
        case "follow":
          walkTo.follow({
            xMm: quarry!.xMm,
            yMm: quarry!.yMm,
            stopMm: APPROACH_STOP_MM,
          });
          // And a stall window -- but only somebody else's. Your own is how you
          // watch your shelves empty, and closing it on the next tap would make
          // it impossible to keep on screen while standing still, which is the
          // whole of what a seller does.
          if (stall.showing && stall.seller !== session.selfId) stall.dismiss();
          break;
      }
    }
    // A target that left the world -- died, or walked out of interest range -- is
    // dropped rather than kept as an id that will never resolve again.
    target.prune(session.aliveIds());

    const selected = target.id ?? 0;
    if (selected !== lastTargetSent) {
      lastTargetSent = selected;
      session.setTarget(selected);
    }

    const aimed =
      target.id === null ? null : (session.entity(target.id) ?? null);
    const aimedPlayer = aimed !== null && aimed.kind === EntityKind.PLAYER ? aimed : null;
    emotes.retarget(aimedPlayer?.id ?? 0, aimedPlayer?.name ?? "");

    const tradingWith = bag.sellingTo ?? shop.dealer;
    if (tradingWith !== null) {
      const dealer = session.entity(tradingWith);
      const dx = dealer === null ? Infinity : dealer.xMm - predictor.state.xMm;
      const dy = dealer === null ? Infinity : dealer.yMm - predictor.state.yMm;
      if (dx * dx + dy * dy > APPROACH_RADIUS_MM * APPROACH_RADIUS_MM) {
        bag.exitSellMode();
        souls.exitSellMode();
        shop.dismiss();
      }
    }

    if (touch.everTouched) attackButton.reveal();
    attackButton.setReadiness(attacks.readiness(nowMs, lastSwingIntervalMs));

    spendHitFrames(nowMs, others);

    // Expire finished effects once, here, so drawing stays a pure read.
    session.combat.prune(nowMs);
    for (let i = gestures.length - 1; i >= 0; i--) {
      if (nowMs - gestures[i].bornMs >= GESTURE_LIFETIME_MS) gestures.splice(i, 1);
    }
    for (let i = motions.length - 1; i >= 0; i--) {
      if (nowMs - motions[i].bornMs >= GESTURE_LIFETIME_MS) motions.splice(i, 1);
    }
    for (const [id, weapon] of anglers) {
      if (id !== session.selfId && session.entity(id)?.weapon !== weapon) anglers.delete(id);
    }

    // The scene, and only the scene. `?draw=0` skips it and leaves every
    // other line of this frame alone -- see `render/drawgate.ts` for why a
    // headless check wants the game running without the paint.
    const sceneStart = performance.now();
    scene.scene(() => {
      session.wantEmblems([session.guildId ?? 0, ...others.map((other) => other.guildId)]);
      session.wantSymbols(
        others.filter((other) => other.buildingVnum === GUILD_SYMBOL_RACE).map((other) => other.guildId),
      );
      stepSitingCamera(guild.siting, renderer.camera, sitingCameraMemory);
      const renderFrame = buildRenderFrame(session, {
        sitingGhost: guild.siting.ghost,
        cameraAnchor: guild.siting.cameraAnchor,
        player: body,
        others,
        targetId: target.id,
        gestures,
        motions,
        playerMotionMode,
        anglers,
        alwaysShowNames,
        // `ShowNameFlag`, read once a frame off the held set rather than
        // latched on the key event -- `game.py:335` sets it on left Alt down
        // and `:414` clears it on release, and `__IsShowName` (`:1134-1142`)
        // reads it every time a name is about to be drawn.
        showNameHeld: keyboard.nameRevealHeld,
        showDamage,
        showSalesText,
        nowMs,
        dtMs,
      });
      // Before the draw: this frame's refine glows and affect loops on every
      // drawn weapon bone, and the projectiles stepped to now with each
      // landing's bomb spawned where it landed (`render/framefx.ts`).
      applyFrameFx(frameFx, renderFrame, renderer);
      statusLoops.frame(
        renderer.effects,
        renderFrame.playerId,
        affectLoopKeys(renderFrame.playerAffectFlags ?? 0, renderFrame.player.gait !== Gait.IDLE),
        {
          // The character's own root, which is where `RegisterEffect`'s empty
          // bone name attaches (`playersettingmodule.py:365`).
          xMm: renderFrame.player.xMm,
          yMm: renderFrame.player.yMm,
          zMm: renderer.groundAtMm(renderFrame.player.xMm, renderFrame.player.yMm),
        },
      );
      // `CPythonBackground::Update` hands the player's position to the zones
      // every frame (`PythonBackground.cpp:338`).
      ambienceZones.update(
        {
          xMm: renderFrame.player.xMm,
          yMm: renderFrame.player.yMm,
          zMm: renderer.groundAtMm(renderFrame.player.xMm, renderFrame.player.yMm),
        },
        ambienceLoops,
      );
      const loopBodies = new Set<number>([renderFrame.playerId]);
      for (const other of renderFrame.others) {
        loopBodies.add(other.id);
        statusLoops.frame(
          renderer.effects,
          other.id,
          affectLoopKeys(other.affectFlags, other.gait !== Gait.IDLE),
          {
            xMm: other.xMm,
            yMm: other.yMm,
            zMm: renderer.groundAtMm(other.xMm, other.yMm),
          },
        );
      }
      // A poisoned body that walks out of view stops arriving rather than
      // arriving clean, and these loops are `LoopCount 0` -- see `retainOnly`.
      statusLoops.retainOnly(renderer.effects, loopBodies);
      // `RecvSpecificEffect` attaches the file as a temporary effect on the body
      // (`PythonNetworkStreamPhaseGameItem.cpp:903-906`), played where the
      // emoticons below play theirs.
      for (const play of specificEffects.splice(0)) {
        const mount = play.entityId === renderFrame.playerId
          ? renderFrame.playerMount ?? 0
          : renderFrame.others.find((entity) => entity.id === play.entityId)?.mount ?? 0;
        const head = renderer.headAnchor?.(play.entityId, mount);
        if (head != null) renderer.effects.spawn(play.effect, { ...head, followId: play.entityId });
      }
      for (const play of emoticons.drain()) {
        const mount = play.entityId === renderFrame.playerId
          ? renderFrame.playerMount ?? 0
          : renderFrame.others.find((entity) => entity.id === play.entityId)?.mount ?? 0;
        const head = renderer.headAnchor?.(play.entityId, mount);
        if (head != null) {
          renderer.effects.spawn(play.effect, { ...head, followId: play.entityId });
        }
      }
      renderer.draw(renderFrame);
      if (touch.visible) {
        renderer.drawJoystick(
          { x: touch.originX, y: touch.originY },
          touch.knobPosition(),
          JOYSTICK_RADIUS_PX,
        );
      }
    });
    frameProfile.sceneMs += performance.now() - sceneStart;
    const uiStart = performance.now();

    chat.update();
    // The Ctrl hint image over the right mouse button, held exactly as long as
    // the key is (`game.py:336,415` -> `uitaskbar.py:640-646`). Read off the
    // held set once a frame, for the reason the camera keys are.
    taskbar.setMouseHintShown(keyboard.mouseHintHeld);
    // Drawing only: the bar running out ends nothing, and the server retires
    // the line on its own clock. See `ui/fishing.ts`.
    fishing.tick();
    // And the swing's, for the line's reason: the bar is drawn from when this
    // client saw the frame, and the interval it drains over is the one the
    // server said it would enforce.
    mining.tick();
    // The native window recomputes the remaining duration in OnUpdate
    // (`uiautoban.py:131-136`). The frame clock is the browser equivalent;
    // expiry is display-only and the server still judges its own deadline.
    if (autoban.showing) autoban.update(nowMs);
    death.update({ dead: session.dead });
    // Current HP and the display name are not part of `Standing`
    // (`metin3/net/messages.py`'s `Standing` carries only `max_hp`, and no
    // name at all), so the sheet takes them the same way the taskbar and
    // player gauge already do.
    sheet.updateHp(session.selfVitals.hp);
    sheet.updateName(session.displayName);
    taskbar.update({
      hp: session.selfVitals.hp,
      maxHp: session.selfVitals.maxHp,
      hpRecovery: session.spirit.hpRecovery,
      sp: session.spirit.sp,
      maxSp: session.spirit.maxSp,
      spRecovery: session.spirit.spRecovery,
      stamina: staminaAt(session.stamina, performance.now()),
      maxStamina: session.stamina.maxStamina,
      exp: session.standing.exp,
      expToNext: session.standing.expToNext,
    });
    playerGauge.update(session.selfVitals.hp, session.selfVitals.maxHp);

    hud.update({
      status: session.status,
      detail: session.detail,
      pingMs: session.pingMs,
      tick: session.tick,
      entities: session.entityCount,
      errorMm: predictor.lastCorrection?.errorMm ?? 0,
      unacked: predictor.unacked,
      mispredictionRate: predictor.mispredictionRate,
      fps: frames.fps,
      xMm: predictor.state.xMm,
      yMm: predictor.state.yMm,
      // The ring follows `target.id` immediately; the board waits for the
      // server's `Targeted`. `RecvTargetPacket` owns the original board too
      // (`PythonNetworkStreamPhaseGame.cpp:2367-2395`).
      target: confirmedTargetView(
        session.targeted,
        session.targeted.target === 0
          ? null
          : session.entity(session.targeted.target),
        duelStateWith(session.duels, session.selfId, session.targeted.target),
        socialFor(session.targeted.target),
      ),
      playerLevel: session.selfVitals.level,
    });

    // `__Update_NotifyGuildAreaEvent` (`PythonPlayer.cpp:249-273`) each frame.
    gameButtons?.setBuild(guild.mayBuildAt(predictor.state.xMm, predictor.state.yMm));
    minimap.update({
      xMm: predictor.state.xMm,
      yMm: predictor.state.yMm,
      heading: predictor.state.heading,
      observerCount: Math.max(minimapObserverCount, warObserverCount),
      entities: others.map((entity) => ({
        id: entity.id,
        kind: entity.kind,
        xMm: entity.xMm,
        yMm: entity.yMm,
      })),
    });

    // The curtain, last: `renderer.draw` above is what fills in
    // `renderedCharacterParts`, so asking before it would be asking about the
    // previous frame and would uncover the world one frame early. One
    // `CURTAIN_STEP` per frame, exactly as `OnUpdate` does it.
    if (!phaseCurtain.down) {
      if (curtainReleased({
        selfId: session.selfId,
        snapshotsApplied: session.snapshotsApplied,
        selfBodyDraws: renderer.renderedCharacterParts?.(session.selfId)?.bodyDraws ?? 0,
      })) phaseCurtain.release();
      phaseCurtain.step();
    }

    frameProfile.uiMs += performance.now() - uiStart;
    frameProfile.loopMs += performance.now() - nowMs;
    frameProfile.frames += 1;

    requestAnimationFrame(frame);
  };
  if (session instanceof OnlineSession) {
    session.onInputBeat(() => attackBeat(performance.now()));
  }
  requestAnimationFrame(frame);
}

/** What the frame loop needs, whether or not a server is on the other end. */
export interface Session {
  readonly predictor: Predictor;
  /** Stop this session before returning to the login flow. */
  close(): void;
  pumpInput(): void;
  readonly status: ConnectionStatus;
  readonly detail: string;
  readonly pingMs: number;
  readonly tick: number;
  readonly entityCount: number;
  readonly selfId: number;
  /** Entity snapshots applied so far. The phase curtain waits on the first
   *  one (`ui/phasecurtain.ts`); nothing else reads it. */
  readonly snapshotsApplied: number;
  readonly selfVitals: Vitals;
  /** Which of the eight bodies this character wears, off his own snapshot.
   *  Zero -- the novice warrior -- until that snapshot arrives. */
  readonly selfRace: number;
  /** The mob vnum of the horse this character is on, or 0 on foot. */
  readonly selfMount: number;
  /** Public flags on this character from the authoritative self snapshot. */
  readonly selfFlags: number;
  readonly selfAffectFlags: number;
  readonly selfGmLevel: number;
  readonly standing: Standing_;
  /** True while this character is at zero health. */
  readonly dead: boolean;
  /** Seconds a dead character waits, from the server's Welcome. */
  readonly respawnSeconds: number;
  /** This player's own target as confirmed by the server. The local selection
   *  ring is deliberately separate; only this may feed the target board. */
  readonly targeted: TargetedFrame;
  /** What to draw over this player. The server owns it once online. */
  readonly displayName: string;
  readonly combat: CombatFeed;
  others(nowMs: number): readonly RemoteEntity[];
  entity(id: number): RemoteEntity | null;
  aliveIds(): ReadonlySet<number>;
  attack(targetId: number, lockTicks?: number, nextSwingTicks?: number, comboStep?: number): void;
  /** Whether the held attack key's next swing is due on the tick about to be
   *  produced. `SwingLock.dueForSwing` -- the beat's only clock. */
  readonly swingDue: boolean;

  /** Face the swing's victim for as long as the swing lock holds -- the
   *  original turns the attacker to its target on every melee blow, on both
   *  sides of its own wire (`battle.cpp:157`, `PythonPlayerInput.cpp:588`).
   *  See `Connection.faceTowards`. */
  faceTowards(heading: number): void;
  /** Root the local prediction for one swing's cooldown with no target and no
   *  wire traffic -- the "swung at nothing" case `attack()` cannot cover
   *  because there is no id to send. See `Connection.armSwingLock`. */
  armSwingLock(ticks?: number, nextSwingTicks?: number): void;
  say(text: string, channel?: number): void;
  /** Whisper one named player. A name and not an entity id -- see
   *  `Connection.whisper`. */
  whisper(targetName: string, text: string): void;
  readonly bag: Inventory;
  pickUp(entityId: number): void;
  useItem(slot: number): void;
  dropItem(slot: number): void;
  /** Drop only this amount. The purse itself stays server-owned. */
  dropGold(amount: number): void;
  /** Rearrange the bag. Always the whole stack: the drag gesture has no way to
   *  say "half of these", so there is no count for this to forward. */
  moveItem(source: number, destination: number): void;
  equipItem(slot: number): void;
  unequip(wear: number): void;
  /** Sell one cell to a dealer -- a bag slot, or a dragon-soul storage cell
   *  offset by `DRAGON_SOUL_SLOT_BASE`. `count` zero is the whole stack, which
   *  is all either panel offers. */
  sellItem(slot: number, count?: number): void;
  /** Buy one shelf, by the cell number the server put on it. No price, no item,
   *  no dealer: all three are already the server's. */
  buyItem(slot: number): void;
  /** Tell the server the counter is shut. */
  closeShop(): void;
  /** What the open dealer sells, or a dealer of zero meaning closed. */
  onShop(sink: (stock: ShopStock) => void): void;
  /** The whole inventory, every time it changes. */
  onBag(sink: (bag: Inventory) => void): void;
  /** Why something the player asked for did not happen. */
  onNotice(sink: (code: number, text: string, big: boolean) => void): void;
  /** Register the sink for incoming chat. One sink: the box is the only reader,
   *  and a list of them would be a way to double every line. */
  onChat(sink: (from: string, text: string, channel: number) => void): void;
  /** One whisper. Its own sink because the frame carries no entity id. */
  onWhisper(sink: (fromName: string, text: string, refusal: boolean) => void): void;
  /** Every resolved swing this client was told about, for sound. The feed
   *  already consumed it for floaters; this is the same event, not a second. */
  onCombat(sink: (event: CombatResolution) => void): void;
  /** Every indexed mote the server threw between two entities (`CreateFly`). */
  onCreateFly(sink: (launch: FlyLaunch) => void): void;
  /** Every body the server moved by force, with what the shove does to it (`SyncPosition`). */
  onSyncPosition(sink: (entityId: number, shove: PushOutcome) => void): void;
  /** Every body in view that began a skill motion (`Snapshot.skill_motions`). */
  onSkillMotion(sink: (entityId: number, motion: number, grade: number) => void): void;
  /** The character sheet, and the two things that change it. A statistic is
   *  named, never valued: the client says "spend one on strength", and what
   *  that is worth is the server's to decide. */
  readonly spirit: Spirit_;
  readonly stamina: Stamina_;
  readonly pvpMode: number;
  /** Every duel this client has been told about (`PvpDuel`). */
  readonly duels: ReadonlyMap<string, DuelRecord>;
  /** The skill bar, and the two things that change it. A skill is named by its
   *  vnum and a target by its entity id -- never a damage number, never a
   *  cooldown the client decided had elapsed. */
  readonly skills: Skills_;
  useSkill(vnum: number, targetId: number): void;
  trainSkill(vnum: number): void;
  onSkills(sink: (skills: Skills_) => void): void;
  readonly quickslots: readonly QuickSlot_[];
  sendQuickSlotAdd(pos: number, type: number, target: number): void;
  sendQuickSlotDel(pos: number): void;
  sendQuickSlotSwap(pos: number, posTo: number): void;
  onQuickSlots(sink: (slots: readonly QuickSlot_[]) => void): void;
  spendPoint(stat: number): void;
  /** Confirm a server-offered reset scroll; there is no client-supplied state. */
  statusReset(): void;
  onStatusResetOffer(sink: () => void): void;
  onStatusResetConfirm(sink: () => void): void;
  setPvpMode(mode: number): void;
  onStanding(sink: (standing: Standing_) => void): void;
  onSpirit(sink: (spirit: Spirit_) => void): void;
  /** Every live buff and debuff, whole, whenever any of them changes. */
  onAffects(sink: (affects: readonly Affect_[]) => void): void;
  onSpecialEffect(sink: (entityId: number, effect: number) => void): void;
  onSpecificEffect(sink: (entityId: number, effectFile: string) => void): void;
  onPointChange(sink: (entityId: number, point: number, value: number) => void): void;
  onPvpMode(sink: (mode: number) => void): void;
  /** Click an NPC. Its live server-side type decides what happens. */
  clickNpc(npcId: number): void;
  /** Answer the open conversation with the server's own token. */
  questReply(token: number, choice: number): void;
  questInput(token: number, text: string): void;
  questConfirm(token: number, accept: boolean): void;
  questItem(token: number, cell: number): void;
  /** Open a quest from the log, by the server's own row index. */
  questInfo(index: number, describe?: boolean): void;
  /** Every open quest, whole, whenever any of it changes. */
  readonly questLog: readonly QuestEntry_[];
  onQuestDialog(sink: (page: QuestDialog_) => void): void;
  onQuestInput(sink: (prompt: QuestInputPrompt_) => void): void;
  onQuestConfirm(sink: (prompt: QuestConfirmPrompt_) => void): void;
  onQuestItem(sink: (prompt: QuestItemPrompt_) => void): void;
  onQuestLog(sink: (entries: readonly QuestEntry_[]) => void): void;
  onQuestTarget(sink: (event: QuestTarget_) => void): void;
  onNpcPositions(sink: (markers: readonly NpcMarker_[]) => void): void;
  /** Risk improving the bag slot a smith's offer names; 255 cancels (`uirefine.py:414-420`). */
  refine(slot: number): void;
  onRefineOffer(sink: (offer: RefineOffer) => void): void;
  onRefineResult(sink: (result: RefineResult) => void): void;
  /** Ask a cube counter what it builds, then put cells on it and build. Two
   *  calls, and the second names no recipe and no reward: the server picks
   *  both out of its own table, so there is nothing here to aim. */
  cubeInfo(): void;
  cubeMake(slots: readonly number[]): void;
  onCubeRecipes(sink: (recipes: CubeRecipes) => void): void;
  onCubeResult(sink: (result: CubeResult) => void): void;
  onCubeClosed(sink: (closed: CubeClosed) => void): void;
  /** Dragon souls. Three requests and two frames; the window on screen is
   *  whatever the server last described, never what was dragged. */
  dragonSoulMove(source: number, dest: number): void;
  dragonSoulDeck(deck: number): void;
  dragonSoulRefine(
    operation: number,
    souls: readonly number[],
    stones: readonly number[],
  ): void;
  onDragonSouls(sink: (souls: DragonSouls) => void): void;
  /** The dragon soul window as the server last described it. State rather than
   *  only an event, for `bag`'s reason: it is pushed at join, before any of
   *  these panels exist. */
  readonly souls: DragonSouls;
  onDragonSoulRefined(sink: (result: DragonSoulRefined) => void): void;
  /** Player-to-player trade. Every one of these is a request; the window on
   *  screen is whatever the server last described, never what was clicked. */
  exchangeStart(targetId: number): void;
  exchangeOffer(slot: number): void;
  exchangeWithdraw(slot: number): void;
  exchangeGold(amount: number): void;
  /** Agree, naming the server's own token for the pair of offers being agreed
   *  to. The token is the panel's to supply -- it must be the one drawn. */
  exchangeAccept(token: number): void;
  exchangeCancel(): void;
  onExchange(sink: (state: ExchangeState) => void): void;
  /** Parties. Every one of these is a request; the roster on screen is whatever
   *  the server last described. */
  partyInvite(targetId: number): void;
  /** Answer one named invitation. The token is the panel's to supply -- it must
   *  be the one drawn, never the one that is current. */
  partyAnswer(token: number, accept: boolean): void;
  partyLeave(): void;
  partyKick(slot: number, rosterRevision: number): void;
  partyExpMode(mode: number): void;
  partyAttacker(slot: number, assign: boolean, rosterRevision: number): void;
  partyRole(slot: number, role: number, assign: boolean, rosterRevision: number): void;
  partyHeal(): void;
  onParty(sink: (party: PartyFrame) => void): void;
  onPartyInvitation(sink: (invitation: PartyInvitationFrame) => void): void;
  /** Gestures. A request like every other: nothing plays until the server says
   *  it did, on `onEmotePlayed`. */
  emote(emote: number, targetId: number): void;
  /** Allow or refuse one named request. The token is the panel's to supply --
   *  it must be the one drawn, never the one that is current. */
  emoteAnswer(token: number, accept: boolean): void;
  onEmotePlayed(sink: (played: EmotePlayedFrame) => void): void;
  onEmoteRequest(sink: (request: EmoteRequestFrame) => void): void;
  onMount(sink: (mount: MountFrame) => void): void;
  /** The arena. Ask the manager to put a duel to a player, answer one named
   *  challenge, or take/leave a spectator's seat. Nothing is decided here:
   *  the scoreboard on screen is the server's description of a match. */
  arenaChallenge(opponentId: number): void;
  /** Answer one named challenge. The token is the panel's to supply -- it must
   *  be the one drawn, never the one that is current. */
  arenaAnswer(token: number, accept: boolean): void;
  arenaWatch(duelId: number): void;
  onArenaInvitation(sink: (invitation: ArenaInvitationFrame) => void): void;
  onArenaDuel(sink: (duel: ArenaDuelFrame) => void): void;
  onArenaResult(sink: (result: ArenaResultFrame) => void): void;
  /** A player's own stall. Every one of these is a request; what is on screen
   *  is whatever the server last described. */
  openStall(sign: string, listings: readonly StallDraftLine[]): void;
  closeStall(): void;
  enterStall(sellerId: number): void;
  leaveStall(): void;
  /** Buy one shelf by the display cell the server put on it. No price -- see
   *  `Connection.buyFromStall`. */
  buyFromStall(slot: number): void;
  /** What every open stall's board says, keyed by the seller's entity id. Read
   *  every frame to decide whether a tap on a player opens a shop. */
  readonly stallSigns: ReadonlyMap<number, string>;
  onStallSign(sink: (entityId: number, sign: string) => void): void;
  onStall(sink: (stall: StallWindowFrame) => void): void;
  onStallSold(sink: (sale: StallSoldFrame) => void): void;
  /** Somebody's selection, confirmed by the server -- this client's own or a
   *  party member's. `who` says which; a `target` of zero means it is gone. */
  onTargeted(sink: (targeted: TargetedFrame) => void): void;
  onEquipmentView(sink: (view: EquipmentView_) => void): void;
  /** Tell the server what is selected, or zero for nothing. Send only on a
   *  change -- see `Connection.setTarget`. */
  setTarget(id: number): void;
  /** Point the bow at something, once per shot, just before the swing that
   *  looses it (`PythonPlayerEventHandler.cpp:232-244`). */
  flyTarget(id: number, xMm: number, yMm: number): void;
  /** Somebody in sight has pointed a bow. */
  onAimed(sink: (aim: AimFrame) => void): void;
  /** Marriage and the friend list. Every one of these is a request; what is on
   *  screen is whatever the server last described. Both `answer` calls take the
   *  token the panel *drew* -- see `ui/marriage.ts`. */
  proposeMarriage(targetId: number): void;
  answerMarriage(token: number, accept: boolean): void;
  marriageCommand(command: number): void;
  onMarriageStatus(sink: (status: MarriageStatusFrame) => void): void;
  addFriend(name: string): void;
  answerFriend(token: number, accept: boolean): void;
  removeFriend(name: string): void;
  setBlockMode(
    whisper: boolean,
    messengerInvite: boolean,
    exchange: boolean,
    partyInvite: boolean,
    guildInvite: boolean,
    partyRequest?: boolean,
  ): void;
  onFriendList(sink: (list: FriendListFrame) => void): void;
  /** The read-only answer to the existing SAY `/minfo` command. */
  onMonarchInfo(sink: (info: MonarchInfoFrame) => void): void;
  /** The auction house. Every one of these is a request; the board on screen is
   *  whatever the server last described, and `claimLot` carries no role or
   *  durable lot id at all. */
  openAuction(): void;
  listLot(sourceSlot: number, startingBid: number, buyNowPrice: number): void;
  placeBid(slot: number, total: number, boardRevision: number): void;
  buyLotNow(slot: number, boardRevision: number): void;
  claimLot(slot: number, boardRevision: number): void;
  onAuctionBoard(sink: (board: AuctionBoardFrame) => void): void;
  onAuctionNews(sink: (news: AuctionNewsFrame) => void): void;
  /** The item mall. One verb, and no purchase anywhere -- see `ui/mall.ts`. */
  mallCheckout(cell: number, slot: number): void;
  onMall(sink: (mall: MallWindowFrame) => void): void;
  /** Cast a line, or pull one in. One call for both -- see `net/connection.ts`. */
  fish(heading: number): void;
  /** Put the pet away. There is no summon beside it: a pet comes out through
   *  `useItem`, where the original puts it. See `PetCommand`. */
  dismissPet(): void;
  onPolymorphed(sink: (shape: PolymorphedFrame) => void): void;
  onPetSummoned(sink: (pet: PetSummonedFrame) => void): void;

  /** Restart in town instead of waiting out the respawn timer. No arguments --
   *  where town is, is the server's own reading. See `net/connection.ts`. */
  recall(): void;
  restartHere(): void;
  onFishing(sink: (line: FishingFrame) => void): void;
  onFishCaught(sink: (caught: FishCaughtFrame) => void): void;
  /** Somebody's fishing act, this player's own included -- see `FishingAct`. */
  onFishingAct(sink: (act: FishingActFrame) => void): void;
  // ---- mining
  /** Swing a pick at a vein, or stop swinging. One call for both -- see
   *  `net/connection.ts`. */
  mine(heading: number): void;
  onMining(sink: (swing: MiningFrame) => void): void;
  onMineResult(sink: (result: MineResultFrame) => void): void;
  // ---- OX quiz event
  answerOX(token: number, answer: boolean): void;
  onOXQuestion(sink: (question: OXQuestionFrame) => void): void;
  onOXResult(sink: (result: OXResultFrame) => void): void;
  onAutoBanQuiz(sink: (quiz: AutoBanQuizFrame) => void): void;
  // ---- dungeons
  /** Go through the dungeon door in front of you, or come back out of the one
   *  you are in. No arguments -- see `net/connection.ts`. */
  useDungeonDoor(): void;
  onDungeon(sink: (state: DungeonStateFrame) => void): void;
  /** A relocation on the map this client is already drawing. */
  onWarp(sink: (warp: WarpFrame) => void): void;
  /** A crossing onto another map. Ends this page -- see `net/crossing.ts`. */
  onMapChange(sink: (change: MapChangeFrame) => void): void;
  /** Guilds. Every one of these is a request; the roster on screen is whatever
   *  the server last described. */
  guildCreate(name: string): void;
  guildInvite(targetId: number): void;
  /** Answer one named invitation. The token is the panel's to supply -- it must
   *  be the one drawn, never the one that is current. */
  guildAnswer(token: number, accept: boolean): void;
  guildLeave(): void;
  guildDisband(): void;
  /** Rank edits, promotions, the bank and contribution. Each names a grade
   *  index or a roster row; none of them names a permission or a player. */
  guildRank(
    grade: number,
    name: string,
    mayInvite: boolean,
    mayKick: boolean,
    mayNotice: boolean,
    maySkill: boolean,
  ): void;
  guildPromote(slot: number, grade: number, rosterRevision: number): void;
  guildExpel(slot: number, rosterRevision: number): void;
  guildGeneral(slot: number, assign: boolean, rosterRevision: number): void;
  guildHandOver(slot: number, rosterRevision: number): void;
  guildBank(amount: number, withdraw: boolean): void;
  guildOffer(amount: number): void;
  guildChargePower(amount: number): void;
  guildSkillUp(skill: number): void;
  guildUseSkill(skill: number): void;
  guildWarChallenge(token: number): void;
  guildWarRefuse(token: number): void;
  guildPostComment(content: string): void;
  guildDeleteComment(slot: number, boardRevision: number): void;
  guildRefreshComments(): void;
  readonly guildWarMenu: import("./net/connection").GuildWarMenuFrame;
  readonly guildWarArena: import("./net/connection").GuildWarArenaFrame;
  readonly guildComments: import("./net/connection").GuildCommentsFrame;
  guildPlaceBuilding(vnum: number, x: number, y: number, rotation: number): void;
  onGuild(sink: (guild: GuildFrame) => void): void;
  onGuildLand(sink: (state: GuildLandStateFrame) => void): void;
  onLandList(sink: (list: LandListFrame) => void): void;
  onGuildInvitation(sink: (invitation: GuildInvitationFrame) => void): void;
  /** Open the existing guild-name form after a server quest event. */
  onGuildFounding(sink: () => void): void;
  onGuildWarMenu(sink: (menu: import("./net/connection").GuildWarMenuFrame) => void): void;
  onGuildWarArena(sink: (arena: import("./net/connection").GuildWarArenaFrame) => void): void;
  onGuildComments(sink: (comments: import("./net/connection").GuildCommentsFrame) => void): void;
  /** The guild emblem. `uploadEmblem` carries no guild id on purpose -- see
   *  `UploadEmblem`. */
  uploadEmblem(bgra: readonly number[]): void;
  requestEmblem(guildId: number): void;
  onEmblem(sink: (emblem: EmblemFrame) => void): void;
  /** Ask once for the sign of each guild a name plate this frame belongs to,
   *  as `RegisterCharacterTextTail` looks a guild's mark up the moment it
   *  registers a member's tail (`PythonTextTail.cpp:540-562`). */
  wantEmblems(guildIds: Iterable<number>): void;
  /** Send this player's guild symbol file; no guild id, as `GuildSymbolChunk`. */
  uploadGuildSymbol(file: Uint8Array): void;
  /** Ask once for the symbol of each guild whose insignia is in sight, as a
   *  land list asks for every guild on it (`PythonNetworkStreamPhaseGame.cpp:4176-4218`). */
  wantSymbols(guildIds: Iterable<number>): void;
  readonly guildSymbols: ReadonlyMap<number, CanvasImageSource>;
  readonly guildNames: ReadonlyMap<number, string>;
  readonly guildEmblems: ReadonlyMap<number, readonly number[]>;
  /** Each ground item in view: the vnum its drop model is found by. */
  readonly groundItems: ReadonlyMap<number, { readonly vnum: number; readonly seenMs: number }>;
  /** The account's storeroom. */
  openSafebox(password: string): void;
  safeboxDeposit(slot: number, cell: number): void;
  safeboxWithdraw(cell: number, slot: number): void;
  /** Rearrange two cells inside the open storeroom. */
  safeboxMove(source: number, destination: number): void;
  closeSafebox(): void;
  onSafebox(sink: (box: Safebox) => void): void;
  // ---- using one bag item on another
  /** Use one bag item on another bag cell. */
  useItemToItem(slot: number, dest: number): void;
  /** Use one live bag item on a visible NPC/BUILDING target. */
  useItemOnNpc(slot: number, npcId: number): void;
}

class OnlineSession implements Session {
  private readonly connection: Connection;
  private statusDetail = "";
  private readonly requestedName: string;
  private chatSink:
    | ((from: string, text: string, channel: number) => void)
    | null = null;
  private whisperSink: ((fromName: string, text: string, refusal: boolean) => void) | null = null;
  private readonly guildSinks: ((guild: GuildFrame) => void)[] = [];
  private guildLandSink: ((state: GuildLandStateFrame) => void) | null = null;
  private landListSink: ((list: LandListFrame) => void) | null = null;
  private emblemSink: ((emblem: EmblemFrame) => void) | null = null;
  /** Every sign the server has served this connection, by guild id. */
  private readonly emblems = new Map<number, readonly number[]>();
  /** The guild ids a sign has already been asked for. */
  private readonly emblemsAsked = new Set<number>();
  private readonly symbols = new Map<number, CanvasImageSource>();
  private readonly symbolsAsked = new Set<number>();
  private guildInvitationSink:
    | ((invitation: GuildInvitationFrame) => void)
    | null = null;
  private guildFoundingSink: (() => void) | null = null;
  private guildCommentsSink: ((comments: GuildComments) => void) | null = null;
  private combatSink: ((event: CombatResolution) => void) | null = null;
  private createFlySink: ((launch: FlyLaunch) => void) | null = null;
  private syncPositionSink: ((entityId: number, shove: PushOutcome) => void) | null = null;
  private skillMotionSink: ((entityId: number, motion: number, grade: number) => void) | null = null;
  private aimedSink: ((aim: AimFrame) => void) | null = null;
  private newWorldSink: (() => void) | null = null;
  /** `attackBeat`, run once per simulation tick -- see its own comment and
   *  `ConnectionOptions.onInputTick`. */
  private inputBeatSink: (() => void) | null = null;
  private bagSink: ((bag: Inventory) => void) | null = null;
  private shopSink: ((stock: ShopStock) => void) | null = null;
  private noticeSink: ((code: number, text: string, big: boolean) => void) | null = null;
  private standingSink: ((standing: Standing_) => void) | null = null;
  private spiritSink: ((spirit: Spirit_) => void) | null = null;
  private affectsSink: ((affects: readonly Affect_[]) => void) | null = null;
  private specialEffectSink: ((entityId: number, effect: number) => void) | null = null;
  private specificEffectSink: ((entityId: number, effectFile: string) => void) | null = null;
  private pointChangeSink: ((entityId: number, point: number, value: number) => void) | null = null;
  private pvpSink: ((mode: number) => void) | null = null;
  private statusResetOfferSink: (() => void) | null = null;
  private statusResetConfirmSink: (() => void) | null = null;
  private skillsSink: ((skills: Skills_) => void) | null = null;
  private quickSlotsSink: ((slots: readonly QuickSlot_[]) => void) | null = null;
  private questDialogSink: ((page: QuestDialog_) => void) | null = null;
  private questInputSink: ((prompt: QuestInputPrompt_) => void) | null = null;
  private questConfirmSink: ((prompt: QuestConfirmPrompt_) => void) | null = null;
  private questItemSink: ((prompt: QuestItemPrompt_) => void) | null = null;
  private questLogSink: ((entries: readonly QuestEntry_[]) => void) | null = null;
  private questTargetSink: ((event: QuestTarget_) => void) | null = null;
  private npcPositionsSink: ((markers: readonly NpcMarker_[]) => void) | null = null;
  private refineOfferSink: ((offer: RefineOffer) => void) | null = null;
  private refineResultSink: ((result: RefineResult) => void) | null = null;
  private cubeRecipesSink: ((recipes: CubeRecipes) => void) | null = null;
  private cubeResultSink: ((result: CubeResult) => void) | null = null;
  private cubeClosedSink: ((closed: CubeClosed) => void) | null = null;
  private dragonSoulsSink: ((souls: DragonSouls) => void) | null = null;
  private dragonSoulRefinedSink:
    | ((result: DragonSoulRefined) => void)
    | null = null;
  private exchangeSink: ((state: ExchangeState) => void) | null = null;
  private safeboxSink: ((box: Safebox) => void) | null = null;
  private partySink: ((party: PartyFrame) => void) | null = null;
  private charactersSink: ((characters: CharactersFrame) => void) | null = null;
  private empireSelectSink: ((prompt: EmpireSelectPrompt) => void) | null = null;
  private invitationSink:
    | ((invitation: PartyInvitationFrame) => void)
    | null = null;
  private emotePlayedSink: ((played: EmotePlayedFrame) => void) | null = null;
  private emoteRequestSink: ((request: EmoteRequestFrame) => void) | null = null;
  private arenaInvitationSink:
    | ((invitation: ArenaInvitationFrame) => void)
    | null = null;
  private arenaDuelSink: ((duel: ArenaDuelFrame) => void) | null = null;
  private arenaResultSink: ((result: ArenaResultFrame) => void) | null = null;
  // ---- a player's own stall
  private stallSignSink: ((entityId: number, sign: string) => void) | null = null;
  private stallSink: ((stall: StallWindowFrame) => void) | null = null;
  private stallSoldSink: ((sale: StallSoldFrame) => void) | null = null;
  private targetedSink: ((targeted: TargetedFrame) => void) | null = null;
  private equipmentViewSink: ((view: EquipmentView_) => void) | null = null;
  // ---- marriage and the friend list
  private marriageSink: ((status: MarriageStatusFrame) => void) | null = null;
  private friendListSink: ((list: FriendListFrame) => void) | null = null;
  private monarchInfoSink: ((info: MonarchInfoFrame) => void) | null = null;
  // ---- the auction house and the item mall
  private auctionBoardSink: ((board: AuctionBoardFrame) => void) | null = null;
  private auctionNewsSink: ((news: AuctionNewsFrame) => void) | null = null;
  private mallSink: ((mall: MallWindowFrame) => void) | null = null;
  private mountSink: ((mount: MountFrame) => void) | null = null;
  private polymorphedSink: ((shape: PolymorphedFrame) => void) | null = null;
  private petSink: ((pet: PetSummonedFrame) => void) | null = null;
  private fishingSink: ((line: FishingFrame) => void) | null = null;
  private fishCaughtSink: ((caught: FishCaughtFrame) => void) | null = null;
  private fishingActSink: ((act: FishingActFrame) => void) | null = null;
  // ---- mining
  private miningSink: ((swing: MiningFrame) => void) | null = null;
  private mineResultSink: ((result: MineResultFrame) => void) | null = null;
  private oxQuestionSink: ((question: OXQuestionFrame) => void) | null = null;
  private oxResultSink: ((result: OXResultFrame) => void) | null = null;
  private autoBanQuizSink: ((quiz: AutoBanQuizFrame) => void) | null = null;
  /** The challenge is state, not a one-shot event: it can arrive with Welcome
   * before the boot function has finished mounting the panel. */
  private autoBanQuiz: AutoBanQuizFrame | null = null;
  // ---- dungeons
  private dungeonSink: ((state: DungeonStateFrame) => void) | null = null;
  private warpSink: ((warp: WarpFrame) => void) | null = null;
  private readonly mapChange = new HeldCrossing<MapChangeFrame>();
  private environmentSink: ((night: boolean) => void) | null = null;
  private weatherSink: ((kind: number) => void) | null = null;
  private fieldMusicSink: ((track: number, volumePercent: number) => void) | null = null;
  private readonly idle: Predictor;
  readonly combat = new CombatFeed();

  /** What the server calls this player, falling back to what was asked for. */
  get displayName(): string {
    return this.connection.selfName || this.requestedName;
  }

  close(): void {
    this.connection.close();
  }

  /** Which ground the server says this world is on. Empty before Welcome. */
  get mapId(): string {
    return this.connection.mapId;
  }

  /** True once the server has answered with a Welcome. The login screen waits on
   *  this rather than on the attempt, so a rejection keeps the form up. */
  joined = false;

  constructor(
    url: string,
    playerName: string,
    world: ObjectWorld,
    input: InputSource,
    credentials: Credentials | null = null,
    onReject?: (code: number, reason: string) => void,
    crossing: string | null = null,
  ) {
    this.requestedName = playerName;
    // The connection only builds its predictor once the server has said where the
    // player spawns; until then the frame loop still needs something to draw.
    this.idle = new Predictor(moveState(0, 0), DEFAULT_STATS, world);
    this.connection = new Connection({
      url,
      playerName,
      world,
      guildAttributes: () => guildAttributesTable,
      usingSkill: (entityId) => bodyUsingSkill(entityId),
      input,
      credentials,
      crossing,
      onStatus: (status, detail) => {
        if (crossing !== null || status === "rejected") markCrossing(`status:${status}`, detail);
        if (status === "rejected") onReject?.(0, detail);
        this.statusDetail = detail;
        // A reconnect is a new world with new entity ids. Floaters and log lines
        // carried across it would be drawn over whoever inherited the numbers.
        if (status === "joined") {
          this.joined = true;
          this.combat.clear();
          this.newWorldSink?.();
        }
      },
      onInputTick: () => this.inputBeatSink?.(),
      onChat: (from, text, channel) => this.chatSink?.(from, text, channel),
      onWhisper: (from, text, refusal) => this.whisperSink?.(from, text, refusal),
      onGuild: (roster) => {
        for (const sink of this.guildSinks) sink(roster);
      },
      onGuildLand: (state) => this.guildLandSink?.(state),
      onLandList: (list) => this.landListSink?.(list),
      onEmblem: (frame) => {
        this.emblems.set(frame.guild_id, frame.rgba);
        this.emblemSink?.(frame);
      },
      onGuildSymbol: (frame) => this.decodeSymbol(frame.guild_id, frame.data),
      onGuildInvitation: (invitation) =>
        this.guildInvitationSink?.(invitation),
      onGuildFounding: () => this.guildFoundingSink?.(),
      onGuildComments: (comments) => this.guildCommentsSink?.(comments),
      onStanding: (standing) => this.standingSink?.(standing),
      onSpirit: (spirit) => this.spiritSink?.(spirit),
      onAffects: (affects) => this.affectsSink?.(affects),
      onSpecialEffect: (entityId, effect) => {
        this.combat.special(entityId, effect, performance.now());
        this.specialEffectSink?.(entityId, effect);
      },
      onSpecificEffect: (entityId, effectFile) => this.specificEffectSink?.(entityId, effectFile),
      onPointChange: (entityId, point, value) => {
        this.combat.point(entityId, point, performance.now());
        this.pointChangeSink?.(entityId, point, value);
      },
      onPvpMode: (mode) => this.pvpSink?.(mode),
      onStatusResetOffer: () => this.statusResetOfferSink?.(),
      onStatusResetConfirm: () => this.statusResetConfirmSink?.(),
      onSkills: (skills) => this.skillsSink?.(skills),
      onQuickSlots: (slots) => this.quickSlotsSink?.(slots),
      onCombat: (event) => {
        this.combat.record(event, this.combatContext(), performance.now());
        this.combatSink?.(event);
      },
      onCreateFly: (launch) => this.createFlySink?.(launch),
      onSyncPosition: (entityId, shove) => this.syncPositionSink?.(entityId, shove),
      onSkillMotion: (entityId, motion, grade) => this.skillMotionSink?.(entityId, motion, grade),
      onAimed: (aim) => this.aimedSink?.(aim),
      onBag: (bag) => this.bagSink?.(bag),
      onShop: (stock) => this.shopSink?.(stock),
      onNotice: (code, text, big) => this.noticeSink?.(code, text, big),
      onCharacters: (characters) => this.charactersSink?.(characters),
      onEmpireSelect: (prompt) => this.empireSelectSink?.(prompt),
      onQuestDialog: (page) => this.questDialogSink?.(page),
      onQuestInput: (prompt) => this.questInputSink?.(prompt),
      onQuestConfirm: (prompt) => this.questConfirmSink?.(prompt),
      onQuestItem: (prompt) => this.questItemSink?.(prompt),
      onQuestLog: (entries) => this.questLogSink?.(entries),
      onQuestTarget: (event) => this.questTargetSink?.(event),
      onNpcPositions: (markers) => this.npcPositionsSink?.(markers),
      onRefineOffer: (offer) => this.refineOfferSink?.(offer),
      onRefineResult: (result) => this.refineResultSink?.(result),
      onCubeRecipes: (recipes) => this.cubeRecipesSink?.(recipes),
      onCubeResult: (result) => this.cubeResultSink?.(result),
      onCubeClosed: (closed) => this.cubeClosedSink?.(closed),
      onDragonSouls: (souls) => this.dragonSoulsSink?.(souls),
      onDragonSoulRefined: (result) => this.dragonSoulRefinedSink?.(result),
      onExchange: (state) => this.exchangeSink?.(state),
      onSafebox: (box) => this.safeboxSink?.(box),
      onParty: (roster) => this.partySink?.(roster),
      onPartyInvitation: (invitation) => this.invitationSink?.(invitation),
      onEmotePlayed: (played) => this.emotePlayedSink?.(played),
      onEmoteRequest: (request) => this.emoteRequestSink?.(request),
      onArenaInvitation: (invitation) => this.arenaInvitationSink?.(invitation),
      onArenaDuel: (duel) => this.arenaDuelSink?.(duel),
      onArenaResult: (result) => this.arenaResultSink?.(result),
      // ---- a player's own stall
      onStallSign: (entityId, sign) => this.stallSignSink?.(entityId, sign),
      onStall: (stall) => this.stallSink?.(stall),
      onStallSold: (sale) => this.stallSoldSink?.(sale),
      onTargeted: (targeted) => this.targetedSink?.(targeted),
      onEquipmentView: (view) => this.equipmentViewSink?.(view),
      // ---- marriage and the friend list
      onMarriageStatus: (status) => this.marriageSink?.(status),
      onFriendList: (list) => this.friendListSink?.(list),
      onMonarchInfo: (info) => this.monarchInfoSink?.(info),
      // ---- the auction house and the item mall
      onAuctionBoard: (board) => this.auctionBoardSink?.(board),
      onAuctionNews: (news) => this.auctionNewsSink?.(news),
      onMall: (mall) => this.mallSink?.(mall),
      onMount: (mount) => this.mountSink?.(mount),
      onPolymorphed: (shape) => this.polymorphedSink?.(shape),
      onPetSummoned: (pet) => this.petSink?.(pet),
      onFishing: (line) => this.fishingSink?.(line),
      onFishCaught: (caught) => this.fishCaughtSink?.(caught),
      onFishingAct: (act) => this.fishingActSink?.(act),
      // ---- mining
      onMining: (swing) => this.miningSink?.(swing),
      onMineResult: (result) => this.mineResultSink?.(result),
      onOXQuestion: (question) => this.oxQuestionSink?.(question),
      onOXResult: (result) => this.oxResultSink?.(result),
      onAutoBanQuiz: (quiz) => {
        this.autoBanQuiz = quiz;
        this.autoBanQuizSink?.(quiz);
      },
      // ---- dungeons
      onDungeon: (state) => this.dungeonSink?.(state),
      onWarp: (warp) => this.warpSink?.(warp),
      onMapChange: (change) => this.mapChange.deliver(change),
      onEnvironment: (night) => this.environmentSink?.(night),
      onWeather: (kind) => this.weatherSink?.(kind),
      onFieldMusic: (track, volumePercent) => this.fieldMusicSink?.(track, volumePercent),
    });
    this.connection.open();
  }

  get predictor(): Predictor {
    return this.connection.predictor ?? this.idle;
  }

  get targeted(): TargetedFrame {
    return this.connection.targeted;
  }

  pumpInput(): void {
    this.connection.pumpInputs();
  }

  get status(): ConnectionStatus {
    return this.connection.status;
  }

  get detail(): string {
    return this.statusDetail;
  }

  get pingMs(): number {
    return this.connection.pingMs;
  }

  get tick(): number {
    return this.connection.serverTick;
  }

  get entityCount(): number {
    return this.connection.entities.size + 1;
  }

  private combatContext(): CombatContext {
    return {
      selfId: this.connection.entityId,
      nameOf: (id) => this.connection.entities.get(id)?.name ?? `#${id}`,
      positionOf: (id) =>
        id === this.connection.entityId
          ? this.predictor.state
          : this.connection.entities.get(id),
    };
  }

  get selfId(): number {
    return this.connection.entityId;
  }

  get snapshotsApplied(): number {
    return this.connection.snapshotsApplied;
  }

  get selfVitals(): Vitals {
    return this.connection.selfVitals;
  }

  get selfRace(): number {
    return this.connection.selfRace;
  }

  get selfMount(): number {
    return this.connection.selfMount;
  }

  get selfFlags(): number {
    return this.connection.selfFlags;
  }

  get selfAffectFlags(): number {
    return this.connection.selfAffectFlags;
  }

  get selfGmLevel(): number {
    return this.connection.selfGmLevel;
  }

  /** The guild whose name goes over this player's own head
   *  (`RegisterCharacterTextTail`, `UserInterface/PythonTextTail.cpp:530-560`).
   *  `guild_id` of zero is the server saying "no guild". */
  get guildName(): string {
    return this.connection.guild.guild_id === 0 ? "" : this.connection.guild.name;
  }

  /** Every guild this connection has been told the name of, for the line over
   *  *other* players' heads (`CPythonGuild::m_GuildNameMap`). The live map, not
   *  a copy: it grows by one row per guild first seen and the renderer only
   *  reads it. */
  get guildNames(): ReadonlyMap<number, string> {
    return this.connection.guildNames;
  }

  get groundItems(): ReadonlyMap<number, { readonly vnum: number; readonly seenMs: number }> {
    return this.connection.groundItems;
  }

  others(nowMs: number): readonly RemoteEntity[] {
    return this.connection.entities.render(nowMs, this.connection.entityId);
  }

  entity(id: number): RemoteEntity | null {
    return this.connection.entities.get(id);
  }

  aliveIds(): ReadonlySet<number> {
    return this.connection.entities.ids();
  }

  attack(targetId: number, lockTicks?: number, nextSwingTicks?: number, comboStep?: number): void {
    this.connection.attack(targetId, lockTicks, nextSwingTicks, comboStep);
  }

  faceTowards(heading: number): void {
    this.connection.faceTowards(heading);
  }

  armSwingLock(ticks?: number, nextSwingTicks?: number): void {
    this.connection.armSwingLock(ticks, nextSwingTicks);
  }

  get swingDue(): boolean {
    return this.connection.swingDue;
  }

  get bag(): Inventory {
    return this.connection.bag;
  }

  pickUp(entityId: number): void {
    this.connection.pickUp(entityId);
  }

  useItem(slot: number): void {
    this.connection.useItem(slot);
  }

  dropItem(slot: number): void {
    this.connection.dropItem(slot);
  }

  dropGold(amount: number): void {
    this.connection.dropGold(amount);
  }

  moveItem(source: number, destination: number): void {
    this.connection.moveItem(source, destination);
  }

  equipItem(slot: number): void {
    this.connection.equipItem(slot);
  }

  unequip(wear: number): void {
    this.connection.unequip(wear);
  }

  sellItem(slot: number, count = 0): void {
    this.connection.sellItem(slot, count);
  }

  buyItem(slot: number): void {
    this.connection.buyItem(slot);
  }

  closeShop(): void {
    this.connection.closeShop();
  }

  onShop(sink: (stock: ShopStock) => void): void {
    this.shopSink = sink;
  }

  onBag(sink: (bag: Inventory) => void): void {
    this.bagSink = sink;
  }

  onCharacters(sink: (characters: CharactersFrame) => void): void {
    this.charactersSink = sink;
  }

  /** The authenticated account must choose its first kingdom before slots open. */
  onEmpireSelect(sink: (prompt: EmpireSelectPrompt) => void): void {
    this.empireSelectSink = sink;
  }

  /** Persist the first kingdom choice through the server-owned account phase. */
  selectEmpire(empire: number): void {
    this.connection.selectEmpire(empire);
  }

  /** Play the character in one of this account's slots. A slot index and
   *  nothing else -- see `ui/charselect.ts` on why there is no id. */
  selectCharacter(slot: number): void {
    this.connection.selectCharacter(slot);
  }

  /** Retire the character in one of this account's slots, with the account
   *  password as the confirmation the original asks for in another form
   *  (`ClientManagerPlayer.cpp:966`). */
  deleteCharacter(slot: number, confirm: string): void {
    this.connection.deleteCharacter(slot, confirm);
  }

  /** Ask the server to make a character. The slot and race came from the
   *  server's `Characters` frame; shape is the player's appearance choice and
   *  the server validates it -- see `ui/charcreate.ts`. */
  createCharacter(
    slot: number,
    name: string,
    race: number,
    shape: number,
  ): void {
    this.connection.createCharacter(slot, name, race, shape);
  }

  onNotice(sink: (code: number, text: string, big: boolean) => void): void {
    this.noticeSink = sink;
  }

  say(text: string, channel?: number): void {
    this.connection.say(text, channel);
  }

  whisper(targetName: string, text: string): void {
    this.connection.whisper(targetName, text);
  }

  onWhisper(sink: (fromName: string, text: string, refusal: boolean) => void): void {
    this.whisperSink = sink;
  }

  onChat(sink: (from: string, text: string, channel: number) => void): void {
    this.chatSink = sink;
  }

  onCombat(sink: (event: CombatResolution) => void): void {
    this.combatSink = sink;
  }

  onCreateFly(sink: (launch: FlyLaunch) => void): void {
    this.createFlySink = sink;
  }

  onSyncPosition(sink: (entityId: number, shove: PushOutcome) => void): void {
    this.syncPositionSink = sink;
  }

  onSkillMotion(sink: (entityId: number, motion: number, grade: number) => void): void {
    this.skillMotionSink = sink;
  }

  onNewWorld(sink: () => void): void {
    this.newWorldSink = sink;
  }

  onInputBeat(sink: () => void): void {
    this.inputBeatSink = sink;
  }

  get spirit(): Spirit_ {
    return this.connection.spirit;
  }

  get stamina(): Stamina_ {
    return this.connection.stamina;
  }

  get skills(): Skills_ {
    return this.connection.skills;
  }

  useSkill(vnum: number, targetId: number): void {
    this.connection.useSkill(vnum, targetId);
  }

  trainSkill(vnum: number): void {
    this.connection.trainSkill(vnum);
  }

  onSkills(sink: (skills: Skills_) => void): void {
    this.skillsSink = sink;
  }

  get quickslots(): readonly QuickSlot_[] {
    return this.connection.quickslots;
  }

  sendQuickSlotAdd(pos: number, type: number, target: number): void {
    this.connection.sendQuickSlotAdd(pos, type, target);
  }

  sendQuickSlotDel(pos: number): void {
    this.connection.sendQuickSlotDel(pos);
  }

  sendQuickSlotSwap(pos: number, posTo: number): void {
    this.connection.sendQuickSlotSwap(pos, posTo);
  }

  onQuickSlots(sink: (slots: readonly QuickSlot_[]) => void): void {
    this.quickSlotsSink = sink;
  }

  get pvpMode(): number {
    return this.connection.pvpMode;
  }

  get duels(): ReadonlyMap<string, DuelRecord> {
    return this.connection.duels;
  }

  spendPoint(stat: number): void {
    this.connection.spendPoint(stat);
  }

  statusReset(): void {
    this.connection.statusReset();
  }

  onStatusResetOffer(sink: () => void): void {
    this.statusResetOfferSink = sink;
  }

  onStatusResetConfirm(sink: () => void): void {
    this.statusResetConfirmSink = sink;
  }

  setPvpMode(mode: number): void {
    this.connection.setPvpMode(mode);
  }

  onStanding(sink: (standing: Standing_) => void): void {
    this.standingSink = sink;
  }

  onSpirit(sink: (spirit: Spirit_) => void): void {
    this.spiritSink = sink;
  }

  onAffects(sink: (affects: readonly Affect_[]) => void): void {
    this.affectsSink = sink;
  }

  onSpecialEffect(sink: (entityId: number, effect: number) => void): void {
    this.specialEffectSink = sink;
  }

  onSpecificEffect(sink: (entityId: number, effectFile: string) => void): void {
    this.specificEffectSink = sink;
  }

  onPointChange(sink: (entityId: number, point: number, value: number) => void): void {
    this.pointChangeSink = sink;
  }

  onPvpMode(sink: (mode: number) => void): void {
    this.pvpSink = sink;
  }

  get standing(): Standing_ {
    return this.connection.standing;
  }

  get dead(): boolean {
    return this.connection.dead;
  }

  get respawnSeconds(): number {
    return this.connection.respawnTicks / TICK_HZ;
  }

  clickNpc(npcId: number): void {
    this.connection.clickNpc(npcId);
  }

  questReply(token: number, choice: number): void {
    this.connection.questReply(token, choice);
  }

  questInput(token: number, text: string): void {
    this.connection.questInput(token, text);
  }

  questConfirm(token: number, accept: boolean): void {
    this.connection.questConfirm(token, accept);
  }

  questItem(token: number, cell: number): void {
    this.connection.questItem(token, cell);
  }

  questInfo(index: number, describe = false): void {
    this.connection.questInfo(index, describe);
  }

  get questLog(): readonly QuestEntry_[] {
    return this.connection.questLog;
  }

  refine(slot: number): void {
    this.connection.refine(slot);
  }

  onRefineOffer(sink: (offer: RefineOffer) => void): void {
    this.refineOfferSink = sink;
  }

  onRefineResult(sink: (result: RefineResult) => void): void {
    this.refineResultSink = sink;
  }

  cubeInfo(): void {
    this.connection.cubeInfo();
  }

  cubeMake(slots: readonly number[]): void {
    this.connection.cubeMake(slots);
  }

  onCubeRecipes(sink: (recipes: CubeRecipes) => void): void {
    this.cubeRecipesSink = sink;
  }

  onCubeResult(sink: (result: CubeResult) => void): void {
    this.cubeResultSink = sink;
  }

  onCubeClosed(sink: (closed: CubeClosed) => void): void {
    this.cubeClosedSink = sink;
  }

  dragonSoulMove(source: number, dest: number): void {
    this.connection.dragonSoulMove(source, dest);
  }

  dragonSoulDeck(deck: number): void {
    this.connection.dragonSoulDeck(deck);
  }

  dragonSoulRefine(
    operation: number,
    souls: readonly number[],
    stones: readonly number[],
  ): void {
    this.connection.dragonSoulRefine(operation, souls, stones);
  }

  onDragonSouls(sink: (souls: DragonSouls) => void): void {
    this.dragonSoulsSink = sink;
  }

  get souls(): DragonSouls {
    return this.connection.souls;
  }

  onDragonSoulRefined(sink: (result: DragonSoulRefined) => void): void {
    this.dragonSoulRefinedSink = sink;
  }

  exchangeStart(targetId: number): void {
    this.connection.exchangeStart(targetId);
  }

  exchangeOffer(slot: number): void {
    this.connection.exchangeOffer(slot);
  }

  exchangeWithdraw(slot: number): void {
    this.connection.exchangeWithdraw(slot);
  }

  exchangeGold(amount: number): void {
    this.connection.exchangeGold(amount);
  }

  exchangeAccept(token: number): void {
    this.connection.exchangeAccept(token);
  }

  exchangeCancel(): void {
    this.connection.exchangeCancel();
  }

  onExchange(sink: (state: ExchangeState) => void): void {
    this.exchangeSink = sink;
  }

  openSafebox(password: string): void {
    this.connection.openSafebox(password);
  }

  safeboxDeposit(slot: number, cell: number): void {
    this.connection.safeboxDeposit(slot, cell);
  }

  safeboxWithdraw(cell: number, slot: number): void {
    this.connection.safeboxWithdraw(cell, slot);
  }

  safeboxMove(source: number, destination: number): void {
    this.connection.safeboxMove(source, destination);
  }

  closeSafebox(): void {
    this.connection.closeSafebox();
  }

  partyInvite(targetId: number): void {
    this.connection.partyInvite(targetId);
  }

  partyAnswer(token: number, accept: boolean): void {
    this.connection.partyAnswer(token, accept);
  }

  partyLeave(): void {
    this.connection.partyLeave();
  }

  partyKick(slot: number, rosterRevision: number): void {
    this.connection.partyKick(slot, rosterRevision);
  }

  partyExpMode(mode: number): void {
    this.connection.partyExpMode(mode);
  }

  partyAttacker(slot: number, assign: boolean, rosterRevision: number): void {
    this.connection.partyAttacker(slot, assign, rosterRevision);
  }

  partyRole(slot: number, role: number, assign: boolean, rosterRevision: number): void {
    this.connection.partyRole(slot, role, assign, rosterRevision);
  }

  partyHeal(): void {
    this.connection.partyHeal();
  }

  guildCreate(name: string): void {
    this.connection.guildCreate(name);
  }

  guildInvite(targetId: number): void {
    this.connection.guildInvite(targetId);
  }

  guildAnswer(token: number, accept: boolean): void {
    this.connection.guildAnswer(token, accept);
  }

  guildLeave(): void {
    this.connection.guildLeave();
  }

  guildDisband(): void {
    this.connection.guildDisband();
  }

  guildRank(
    grade: number,
    name: string,
    mayInvite: boolean,
    mayKick: boolean,
    mayNotice: boolean,
    maySkill: boolean,
  ): void {
    this.connection.guildRank(grade, name, mayInvite, mayKick, mayNotice, maySkill);
  }

  guildPromote(slot: number, grade: number, rosterRevision: number): void {
    this.connection.guildPromote(slot, grade, rosterRevision);
  }

  guildExpel(slot: number, rosterRevision: number): void {
    this.connection.guildExpel(slot, rosterRevision);
  }

  guildGeneral(slot: number, assign: boolean, rosterRevision: number): void {
    this.connection.guildGeneral(slot, assign, rosterRevision);
  }

  guildHandOver(slot: number, rosterRevision: number): void {
    this.connection.guildHandOver(slot, rosterRevision);
  }

  guildBank(amount: number, withdraw: boolean): void {
    this.connection.guildBank(amount, withdraw);
  }

  guildOffer(amount: number): void {
    this.connection.guildOffer(amount);
  }

  guildChargePower(amount: number): void {
    this.connection.guildChargePower(amount);
  }

  guildSkillUp(skill: number): void { this.connection.guildSkillUp(skill); }

  guildUseSkill(skill: number): void { this.connection.guildUseSkill(skill); }

  guildWarChallenge(token: number): void { this.connection.guildWarChallenge(token); }
  guildWarRefuse(token: number): void { this.connection.guildWarRefuse(token); }
  guildPostComment(content: string): void { this.connection.guildPostComment(content); }
  guildDeleteComment(slot: number, boardRevision: number): void {
    this.connection.guildDeleteComment(slot, boardRevision);
  }
  guildRefreshComments(): void { this.connection.guildRefreshComments(); }

  get guildWarMenu() { return this.connection.guildWarMenu; }
  get guildWarArena() { return this.connection.guildWarArena; }
  get guildComments() { return this.connection.guildComments; }

  onGuildWarMenu(sink: (menu: import("./net/connection").GuildWarMenuFrame) => void): void {
    this.connection.onGuildWarMenu(sink);
  }

  onGuildWarArena(sink: (arena: import("./net/connection").GuildWarArenaFrame) => void): void {
    this.connection.onGuildWarArena(sink);
  }
  onGuildComments(sink: (comments: import("./net/connection").GuildCommentsFrame) => void): void {
    this.connection.onGuildComments(sink);
  }
  guildPlaceBuilding(vnum: number, x: number, y: number, rotation: number): void { this.connection.guildPlaceBuilding(vnum, x, y, rotation); }

  onGuild(sink: (guild: GuildFrame) => void): void {
    this.guildSinks.push(sink);
    // Replayed on registration, for `onParty`'s reason: a panel built after a
    // `Guild` frame has already landed would otherwise draw nothing until the
    // roster next changed, which for a guild can be days.
    sink(this.connection.guild);
  }

  onGuildLand(sink: (state: GuildLandStateFrame) => void): void {
    this.guildLandSink = sink;
    sink(this.connection.guildLand);
  }

  onLandList(sink: (list: LandListFrame) => void): void {
    this.landListSink = sink;
    sink(this.connection.landList);
  }

  onGuildInvitation(sink: (invitation: GuildInvitationFrame) => void): void {
    this.guildInvitationSink = sink;
  }

  onGuildFounding(sink: () => void): void {
    this.guildFoundingSink = sink;
  }

  uploadEmblem(bgra: readonly number[]): void {
    this.connection.uploadEmblem(bgra);
  }

  requestEmblem(guildId: number): void {
    this.connection.requestEmblem(guildId);
  }

  wantEmblems(guildIds: Iterable<number>): void {
    for (const guildId of emblemsToAsk(guildIds, this.emblemsAsked)) {
      this.connection.requestEmblem(guildId);
    }
  }

  uploadGuildSymbol(file: Uint8Array): void {
    this.connection.uploadGuildSymbol(file);
  }

  wantSymbols(guildIds: Iterable<number>): void {
    for (const guildId of emblemsToAsk(guildIds, this.symbolsAsked)) {
      this.connection.askGuildSymbol(guildId, 0, 0);
    }
  }

  get guildSymbols(): ReadonlyMap<number, CanvasImageSource> {
    return this.symbols;
  }

  /** Decode one served symbol file into the picture its insignia draws, the
   *  browser's `ChangeMaterial` of the saved file (`InstanceBase.cpp:834-838`). */
  private decodeSymbol(guildId: number, data: readonly number[]): void {
    const blob = new Blob([Uint8Array.from(data)], { type: "image/jpeg" });
    void createImageBitmap(blob).then(
      (image) => this.symbols.set(guildId, image),
      () => this.symbols.delete(guildId),
    );
  }

  /** The local player's guild id, 0 in none. */
  get guildId(): number {
    return this.connection.guild.guild_id;
  }

  get guildEmblems(): ReadonlyMap<number, readonly number[]> {
    return this.emblems;
  }

  onEmblem(sink: (emblem: EmblemFrame) => void): void {
    this.emblemSink = sink;
    // Not replayed on registration, unlike `onGuild`: the connection keeps no
    // last emblem, because the panel asks for one the moment it learns which
    // guild it is drawing (`EmblemPanel.setGuild`) and a second copy of that
    // answer here would be a cache with nothing to invalidate it.
  }

  onParty(sink: (party: PartyFrame) => void): void {
    this.partySink = sink;
    sink(this.connection.party);
  }

  onPartyInvitation(sink: (invitation: PartyInvitationFrame) => void): void {
    this.invitationSink = sink;
  }

  emote(emote: number, targetId: number): void {
    this.connection.emote(emote, targetId);
  }

  emoteAnswer(token: number, accept: boolean): void {
    this.connection.emoteAnswer(token, accept);
  }

  onEmotePlayed(sink: (played: EmotePlayedFrame) => void): void {
    this.emotePlayedSink = sink;
  }

  onEmoteRequest(sink: (request: EmoteRequestFrame) => void): void {
    this.emoteRequestSink = sink;
  }

  arenaChallenge(opponentId: number): void {
    this.connection.arenaChallenge(opponentId);
  }

  arenaAnswer(token: number, accept: boolean): void {
    this.connection.arenaAnswer(token, accept);
  }

  arenaWatch(duelId: number): void {
    this.connection.arenaWatch(duelId);
  }

  onArenaInvitation(sink: (invitation: ArenaInvitationFrame) => void): void {
    this.arenaInvitationSink = sink;
  }

  onArenaDuel(sink: (duel: ArenaDuelFrame) => void): void {
    this.arenaDuelSink = sink;
    sink(this.connection.arenaDuel);
  }

  onArenaResult(sink: (result: ArenaResultFrame) => void): void {
    this.arenaResultSink = sink;
  }

  // ---- a player's own stall
  openStall(sign: string, listings: readonly StallDraftLine[]): void {
    this.connection.openStall(sign, listings);
  }

  closeStall(): void {
    this.connection.closeStall();
  }

  enterStall(sellerId: number): void {
    this.connection.enterStall(sellerId);
  }

  leaveStall(): void {
    this.connection.leaveStall();
  }

  buyFromStall(slot: number): void {
    this.connection.buyFromStall(slot);
  }

  get stallSigns(): ReadonlyMap<number, string> {
    return this.connection.stallSigns;
  }

  onStallSign(sink: (entityId: number, sign: string) => void): void {
    this.stallSignSink = sink;
  }

  onStall(sink: (stall: StallWindowFrame) => void): void {
    this.stallSink = sink;
    // Replayed on registration, like `onArenaDuel`: a panel built after a
    // reconnect would otherwise sit blank over a window the server still has.
    sink(this.connection.stall);
  }

  onStallSold(sink: (sale: StallSoldFrame) => void): void {
    this.stallSoldSink = sink;
  }

  setTarget(id: number): void {
    this.connection.setTarget(id);
  }

  flyTarget(id: number, xMm: number, yMm: number): void {
    this.connection.flyTarget(id, xMm, yMm);
  }

  onAimed(sink: (aim: AimFrame) => void): void {
    this.aimedSink = sink;
  }

  onTargeted(sink: (targeted: TargetedFrame) => void): void {
    this.targetedSink = sink;
  }

  onEquipmentView(sink: (view: EquipmentView_) => void): void {
    this.equipmentViewSink = sink;
  }

  // ---- marriage and the friend list
  proposeMarriage(targetId: number): void {
    this.connection.proposeMarriage(targetId);
  }

  answerMarriage(token: number, accept: boolean): void {
    this.connection.answerMarriage(token, accept);
  }

  marriageCommand(command: number): void {
    this.connection.marriageCommand(command);
  }

  onMarriageStatus(sink: (status: MarriageStatusFrame) => void): void {
    this.marriageSink = sink;
    // Replayed on registration, like `onArenaDuel` and `onStall`: a panel built
    // after the join frames have already landed would otherwise sit blank over
    // a marriage the server has been holding all along.
    sink(this.connection.marriage);
  }

  addFriend(name: string): void {
    this.connection.addFriend(name);
  }

  answerFriend(token: number, accept: boolean): void {
    this.connection.answerFriend(token, accept);
  }

  removeFriend(name: string): void {
    this.connection.removeFriend(name);
  }

  setBlockMode(
    whisper: boolean,
    messengerInvite: boolean,
    exchange: boolean,
    partyInvite: boolean,
    guildInvite: boolean,
    partyRequest = false,
  ): void {
    this.connection.setBlockMode(whisper, messengerInvite, exchange, partyInvite, guildInvite, partyRequest);
  }

  onFriendList(sink: (list: FriendListFrame) => void): void {
    this.friendListSink = sink;
    sink(this.connection.friends);
  }

  onMonarchInfo(sink: (info: MonarchInfoFrame) => void): void {
    this.monarchInfoSink = sink;
  }

  // ---- the auction house and the item mall
  openAuction(): void {
    this.connection.openAuction();
  }

  listLot(sourceSlot: number, startingBid: number, buyNowPrice: number): void {
    this.connection.listLot(sourceSlot, startingBid, buyNowPrice);
  }

  placeBid(slot: number, total: number, boardRevision: number): void {
    this.connection.placeBid(slot, total, boardRevision);
  }

  buyLotNow(slot: number, boardRevision: number): void {
    this.connection.buyLotNow(slot, boardRevision);
  }

  claimLot(slot: number, boardRevision: number): void {
    this.connection.claimLot(slot, boardRevision);
  }

  onAuctionBoard(sink: (board: AuctionBoardFrame) => void): void {
    this.auctionBoardSink = sink;
    // Replayed on registration, like `onStall`: a panel built after a
    // reconnect would otherwise sit blank over a board the client has.
    sink(this.connection.auction);
  }

  onAuctionNews(sink: (news: AuctionNewsFrame) => void): void {
    this.auctionNewsSink = sink;
  }

  mallCheckout(cell: number, slot: number): void {
    this.connection.mallCheckout(cell, slot);
  }

  onMall(sink: (mall: MallWindowFrame) => void): void {
    this.mallSink = sink;
    sink(this.connection.mall);
  }

  onMount(sink: (mount: MountFrame) => void): void {
    this.mountSink = sink;
    // Re-stated immediately, unlike the request sink above it: a horse is state
    // and the connection is holding the last one it was sent, so a panel built
    // after the frame arrived would otherwise draw nothing until the next
    // change -- which for an untouched horse is three days away.
    sink(this.connection.mount);
  }

  fish(heading: number): void {
    this.connection.fish(heading);
  }

  dismissPet(): void {
    this.connection.dismissPet();
  }

  onPolymorphed(sink: (shape: PolymorphedFrame) => void): void {
    this.polymorphedSink = sink;
    // Re-stated for `onMount`'s reason, and with a sharper edge: a shape is
    // state with a *deadline*, so a panel built after the frame arrived would
    // otherwise show nothing at all for up to thirty-five minutes.
    sink(this.connection.polymorphed);
  }

  onPetSummoned(sink: (pet: PetSummonedFrame) => void): void {
    this.petSink = sink;
    sink(this.connection.pet);
  }

  restartHere(): void {
    this.connection.restartHere();
  }

  recall(): void {
    this.connection.recall();
  }

  onFishing(sink: (line: FishingFrame) => void): void {
    this.fishingSink = sink;
    // Re-stated for `onMount`'s reason: the line is state, and a panel built
    // after the first frame would draw nothing until the next cast.
    sink(this.connection.fishing);
  }

  // ---- mining
  mine(heading: number): void {
    this.connection.mine(heading);
  }

  onMining(sink: (swing: MiningFrame) => void): void {
    this.miningSink = sink;
    // Re-stated immediately, like `onFishing` below it: a swing is state and
    // the connection holds the last frame it was sent, so a panel built after
    // one arrived would otherwise draw nothing for up to thirty seconds.
    sink(this.connection.mining);
  }

  onMineResult(sink: (result: MineResultFrame) => void): void {
    this.mineResultSink = sink;
  }

  // ---- OX quiz event
  answerOX(token: number, answer: boolean): void {
    this.connection.answerOX(token, answer);
  }

  onOXQuestion(sink: (question: OXQuestionFrame) => void): void {
    this.oxQuestionSink = sink;
  }

  onOXResult(sink: (result: OXResultFrame) => void): void {
    this.oxResultSink = sink;
  }

  onAutoBanQuiz(sink: (quiz: AutoBanQuizFrame) => void): void {
    this.autoBanQuizSink = sink;
    // Credentialed login sends the challenge during `Welcome`, before the
    // boot function can subscribe. Replay the server-owned state so a real
    // player never joins with a challenge that is silently invisible.
    if (this.autoBanQuiz !== null) sink(this.autoBanQuiz);
  }

  // ---- dungeons
  useDungeonDoor(): void {
    this.connection.useDungeonDoor();
  }

  onDungeon(sink: (state: DungeonStateFrame) => void): void {
    this.dungeonSink = sink;
    sink(this.connection.dungeon);
  }

  onFishCaught(sink: (caught: FishCaughtFrame) => void): void {
    this.fishCaughtSink = sink;
  }

  onFishingAct(sink: (act: FishingActFrame) => void): void {
    this.fishingActSink = sink;
  }

  onWarp(sink: (warp: WarpFrame) => void): void {
    this.warpSink = sink;
  }

  onMapChange(sink: (change: MapChangeFrame) => void): void {
    this.mapChange.subscribe(sink);
  }

  /** Rendering is a sink for shard state: the browser cannot choose a time. */
  onEnvironment(sink: (night: boolean) => void): void {
    this.environmentSink = sink;
    // Environment is state, like Mount: registration may happen after the
    // first frame, and darkness that waits ten minutes for its next boundary is
    // indistinguishable from a dead feature.
    sink(this.connection.environmentNight);
  }

  /** Rendering receives deployment state; URL parameters never select weather. */
  onWeather(sink: (kind: number) => void): void {
    this.weatherSink = sink;
    sink(this.connection.weather);
  }

  /** Music follows complete shard state, not an option or browser URL. */
  onFieldMusic(sink: (track: number, volumePercent: number) => void): void {
    this.fieldMusicSink = sink;
    sink(this.connection.fieldMusic.track, this.connection.fieldMusic.volume_percent);
  }

  onSafebox(sink: (box: Safebox) => void): void {
    this.safeboxSink = sink;
  }

  onQuestDialog(sink: (page: QuestDialog_) => void): void {
    this.questDialogSink = sink;
  }

  onQuestInput(sink: (prompt: QuestInputPrompt_) => void): void {
    this.questInputSink = sink;
  }

  onQuestConfirm(sink: (prompt: QuestConfirmPrompt_) => void): void {
    this.questConfirmSink = sink;
  }

  onQuestItem(sink: (prompt: QuestItemPrompt_) => void): void {
    this.questItemSink = sink;
  }

  onQuestLog(sink: (entries: readonly QuestEntry_[]) => void): void {
    this.questLogSink = sink;
  }

  onQuestTarget(sink: (event: QuestTarget_) => void): void {
    this.questTargetSink = sink;
    // Re-stated immediately, like `onMount`: the login targets arrive before
    // the world screen registers this sink.
    for (const target of this.connection.questTargets.values()) sink(target);
  }

  onNpcPositions(sink: (markers: readonly NpcMarker_[]) => void): void {
    this.npcPositionsSink = sink;
    // `SendNPCPosition` is sent once, at `Entergame` (`input_login.cpp:548`),
    // in the burst before this sink exists.
    if (this.connection.npcMarkers.length > 0) sink(this.connection.npcMarkers);
  }

  useItemToItem(slot: number, dest: number): void {
    this.connection.useItemToItem(slot, dest);
  }

  useItemOnNpc(slot: number, npcId: number): void {
    this.connection.useItemOnNpc(slot, npcId);
  }
}

/** Prediction with nothing to reconcile against: the simulation, standalone. */
class OfflineSession implements Session {
  readonly predictor: Predictor;
  readonly status: ConnectionStatus = "offline";
  readonly detail = "no server — local simulation";
  readonly pingMs = -1;
  readonly selfId = -1;
  /** There is no server to snapshot the offline scene; it is simply there,
   *  so the curtain never waits on one. */
  readonly snapshotsApplied = 1;
  readonly selfVitals = NO_VITALS;
  /** Nobody is logged in, so this is the body the offline scene draws. */
  readonly selfRace = 0;
  /** Nobody is riding anything in the offline scene either. */
  readonly selfMount = 0;
  /** Offline simulation has no server-owned public status flags. */
  readonly selfFlags = 0;
  readonly selfAffectFlags = 0;
  readonly selfGmLevel = 0;
  readonly displayName = "Offline";
  /** Empty, and stays empty: with nobody to fight there is no combat to show.
   *  It exists so the frame loop has one shape rather than two. */
  readonly combat = new CombatFeed();
  readonly targeted = NO_TARGETED;
  private ticks = 0;
  private readonly clock = new InputClock(performance.now(), TICK_MS);
  private readonly timer: ReturnType<typeof setInterval>;

  constructor(world: ObjectWorld, private readonly input: InputSource) {
    this.predictor = new Predictor(
      moveState(OFFLINE_SPAWN.xMm, OFFLINE_SPAWN.yMm),
      DEFAULT_STATS,
      world,
    );
    this.timer = setInterval(() => this.pumpInput(), TICK_MS);
  }

  close(): void {
    clearInterval(this.timer);
  }

  pumpInput(): void {
    for (let owed = this.clock.due(performance.now()); owed > 0; owed -= 1) {
      const sampled = this.input.sample();
      this.predictor.pushInput(sampled.heading, sampled.gait);
      this.ticks += 1;
    }
  }

  get tick(): number {
    return this.ticks;
  }

  get entityCount(): number {
    return 1;
  }

  others(): readonly RemoteEntity[] {
    return [];
  }

  entity(): RemoteEntity | null {
    return null;
  }

  aliveIds(): ReadonlySet<number> {
    return new Set();
  }

  attack(): void {
    // No server, no swing. Deliberately not simulated locally: damage is the one
    // thing the client is never allowed to decide, offline included, because an
    // offline path that computes damage is a path someone will later reuse.
  }

  faceTowards(_heading: number): void {
    // A no-op for the same reason `armSwingLock` below is one: nothing offline
    // ever reaches the branch that calls it.
  }

  /** Nothing offline is paced by anything: the beat's `canSwing` reads
   *  `selfVitals.hp > 0`, which `NO_VITALS` never satisfies, so this is never
   *  the reason a swing does or does not happen here. */
  get swingDue(): boolean {
    return true;
  }

  armSwingLock(_ticks?: number, _nextSwingTicks?: number): void {
  }


  say(): void {
    // Nobody to say it to. Echoing it back locally would be worse than silence:
    // it would look like the message reached a world that is not there.
  }

  whisper(): void {
    // And nobody to whisper to. Unreachable rather than unimplemented: a
    // whisper names somebody, and offline there is nobody to name.
  }

  onChat(): void {
    // Never called back.
  }

  onWhisper(): void {
    // Never called back.
  }

  onCombat(): void {
    // No server, no swings to hear.
  }

  onCreateFly(): void {
    // No server, so nothing throws a mote at anybody.
  }

  onSyncPosition(): void {
    // No server, so nothing shoves anybody.
  }

  onSkillMotion(): void {
    // No server, so nobody else begins a skill.
  }

  // The character sheet, offline. Deliberately inert rather than simulated: a
  // point spent locally would mean this client deciding what a statistic is
  // worth, which is the one kind of authority the offline path is careful never
  // to take -- see `attack` above for the same reasoning about damage.
  spendPoint(): void {}
  statusReset(): void {}
  onStatusResetOffer(): void {}
  onStatusResetConfirm(): void {}

  // Offline a skill would have to decide its own damage, which is the authority
  // this path is careful never to take -- see `attack` above.
  useSkill(): void {}

  trainSkill(): void {}

  onSkills(): void {}

  // The quick-slot table is the character's, saved in the player row
  // (`char.cpp:1190`), and there is no character offline. The three verbs go
  // nowhere and the sink is never called, so the bar stays empty rather than
  // showing a table this path would have had to invent.
  readonly quickslots: readonly QuickSlot_[] = NO_QUICK_SLOTS;
  sendQuickSlotAdd(): void {}
  sendQuickSlotDel(): void {}
  sendQuickSlotSwap(): void {}
  onQuickSlots(): void {}

  setPvpMode(): void {}

  onStanding(): void {}

  onSpirit(): void {}

  // Nothing offline applies an affect, so the sink is never called. Present
  // because the interface is the contract both sessions answer to.
  onAffects(): void {}

  // Nor does anything offline drink a potion. Same contract, same reason.
  onSpecialEffect(): void {}

  onSpecificEffect(): void {}

  onPointChange(): void {}

  onPvpMode(): void {}

  readonly bag: Inventory = NO_BAG;

  pickUp(): void {
    // Nothing has dropped, because nothing has died.
  }

  useItem(): void {
    // An empty bag has nothing in it to use.
  }

  dropItem(): void {
    // And nothing to put down.
  }

  dropGold(): void {
    // The offline purse is empty, so there is no amount to put down.
  }

  moveItem(): void {
    // And nothing to rearrange.
  }

  equipItem(): void {
    // Nothing to wear.
  }

  unequip(): void {
    // And nothing on.
  }

  sellItem(): void {
    // No dealers offline, and nothing worth selling to one.
  }

  buyItem(): void {
    // Unreachable -- nothing ever opens a counter to click.
  }

  closeShop(): void {
    // Nothing is open.
  }

  onShop(): void {
    // Never called back.
  }

  onBag(): void {
    // Never called back.
  }

  onNotice(): void {
    // Nothing to refuse.
  }

  clickNpc(): void {
    // Nobody is standing there: the offline path has no world data, only terrain.
  }

  questReply(): void {}

  questInput(): void {}

  questConfirm(): void {}

  questItem(): void {}

  questInfo(_index: number, _describe = false): void {}

  readonly questLog: readonly QuestEntry_[] = [];

  onQuestDialog(): void {}

  onQuestInput(): void {}

  onQuestConfirm(): void {}

  onQuestItem(): void {}

  onQuestLog(): void {}

  onQuestTarget(): void {}

  onNpcPositions(): void {}

  // Refinement, trading and the storeroom all need somebody on the other end:
  // a smith, another player, a keeper. The offline path has terrain and a
  // predictor and nothing standing on the map, so every one of these is
  // unreachable rather than unimplemented.
  refine(): void {}

  onRefineOffer(): void {}

  onRefineResult(): void {}

  cubeInfo(): void {}

  cubeMake(): void {}

  onCubeRecipes(): void {}

  onCubeResult(): void {}

  onCubeClosed(): void {}

  dragonSoulMove(): void {}

  dragonSoulDeck(): void {}

  dragonSoulRefine(): void {}

  onDragonSouls(): void {}

  get souls(): DragonSouls {
    return NO_SOULS;
  }

  onDragonSoulRefined(): void {}

  exchangeStart(): void {}

  exchangeOffer(): void {}

  exchangeWithdraw(): void {}

  exchangeGold(): void {}

  exchangeAccept(): void {}

  exchangeCancel(): void {}

  onExchange(): void {}

  openSafebox(): void {}

  safeboxDeposit(): void {}

  safeboxWithdraw(): void {}

  safeboxMove(): void {}

  closeSafebox(): void {}

  onSafebox(): void {}

  // A party needs somebody else on the map, and the offline path has nobody:
  // unreachable rather than unimplemented, like the three above it.
  partyInvite(): void {}

  partyAnswer(): void {}

  partyLeave(): void {}

  partyKick(): void {}

  partyExpMode(): void {}
  partyAttacker(): void {}
  partyRole(): void {}
  partyHeal(): void {}

  onParty(): void {}

  onPartyInvitation(): void {}

  // A gesture is broadcast to everybody who can see you, and offline nobody
  // can: unreachable rather than unimplemented, like the party above it. The
  // bar is hidden offline for the same reason rather than being drawn dead.
  emote(): void {}

  emoteAnswer(): void {}

  onEmotePlayed(): void {}

  onEmoteRequest(): void {}

  // A duel needs a second player and an NPC to arrange it through, and the
  // offline path has neither: unreachable rather than unimplemented, like the
  // party above it.
  arenaChallenge(): void {}

  arenaAnswer(): void {}

  arenaWatch(): void {}
  // ---- a player's own stall. Nobody else is here to sell to, and nobody is
  // selling: the offline scene has no second player by construction.
  openStall(): void {}
  closeStall(): void {}
  enterStall(): void {}
  leaveStall(): void {}
  buyFromStall(): void {}
  readonly stallSigns: ReadonlyMap<number, string> = new Map();
  onStallSign(): void {}
  onStall(): void {}
  onStallSold(): void {}
  // ---- marriage and the friend list. Nobody to marry and nobody to befriend,
  // for the stall's reason exactly: there is no second player in the offline
  // scene, so these are unreachable rather than unimplemented.
  proposeMarriage(): void {}
  answerMarriage(): void {}
  marriageCommand(): void {}
  onMarriageStatus(): void {}
  addFriend(): void {}
  answerFriend(): void {}
  removeFriend(): void {}
  setBlockMode(): void {}
  onFriendList(): void {}
  onMonarchInfo(): void {}
  // ---- the auction house and the item mall. Both need an account and the
  // offline scene has none: a lot outlives a session and a mall belongs to an
  // account, so there is nothing here for either to be about.
  openAuction(): void {}
  listLot(): void {}
  placeBid(): void {}
  buyLotNow(): void {}
  claimLot(): void {}
  onAuctionBoard(): void {}
  onAuctionNews(): void {}
  mallCheckout(): void {}
  onMall(): void {}

  onArenaInvitation(): void {}

  onArenaDuel(): void {}

  onArenaResult(): void {}

  // Nobody to tell. The offline client has terrain and no server, so a
  // selection is a ring it draws for itself -- which is what targeting was
  // everywhere until the server was told.
  setTarget(): void {}

  flyTarget(): void {}

  onAimed(): void {}

  onTargeted(): void {}

  onEquipmentView(): void {}

  onMount(): void {}

  // Fishing needs a rod from a shop and a species table on the server, and the
  // offline path has terrain and no world data: unreachable rather than
  // unimplemented, like the horse above it.
  fish(): void {}

  // A marble comes off a corpse and a pet out of a bag, and the offline path
  // has terrain and no world data. Same bargain as the horse and the rod.
  dismissPet(): void {}

  onPolymorphed(): void {}

  onPetSummoned(): void {}

  // Town is a point out of the shard's own `Town.txt`, and the offline path has
  // terrain and no world data. Same bargain.
  recall(): void {}

  onFishing(): void {}

  onFishCaught(): void {}

  onFishingAct(): void {}

  // ---- mining. A vein is a body the shard places out of `stone.txt` and a
  // pick comes from a dealer; the offline path has terrain and no world data.
  // Same bargain as the rod above it.
  mine(): void {}

  onMining(): void {}

  onMineResult(): void {}

  answerOX(): void {}

  onOXQuestion(): void {}

  onOXResult(): void {}

  onAutoBanQuiz(): void {}

  // ---- dungeons. A door leads to another map and the offline path has one
  // terrain blob and no server to hand out a crossing token. Same bargain as
  // the pick above it.
  useDungeonDoor(): void {}

  onDungeon(): void {}

  // Nothing offline warps, because there is no server to decide that anybody
  // has. Present because the interface is the contract both sessions answer to.
  onWarp(): void {}

  onMapChange(): void {}

  // A guild needs other players and an account, and the offline path has
  // neither: unreachable rather than unimplemented, like the party above it.
  guildCreate(): void {}

  guildInvite(): void {}

  guildAnswer(): void {}

  guildLeave(): void {}

  guildDisband(): void {}

  guildRank(): void {}

  guildPromote(): void {}

  guildExpel(): void {}

  guildGeneral(): void {}

  guildHandOver(): void {}

  guildBank(): void {}

  guildOffer(): void {}

  guildChargePower(): void {}

  guildSkillUp(): void {}

  guildUseSkill(): void {}
  guildPlaceBuilding(): void {}

  onGuild(): void {}

  // Land ownership and buildings are server-owned guild state; the offline
  // scene has neither an account nor a guild to receive them.
  onGuildLand(): void {}

  onLandList(): void {}

  // An emblem needs a guild, and a guild needs other players and an account.
  // Unreachable rather than unimplemented, like the roster above it.
  uploadEmblem(): void {}

  requestEmblem(): void {}

  onEmblem(): void {}

  wantEmblems(): void {}

  uploadGuildSymbol(): void {}

  wantSymbols(): void {}

  readonly guildSymbols: ReadonlyMap<number, CanvasImageSource> = new Map();
  readonly guildNames: ReadonlyMap<number, string> = new Map();
  readonly guildEmblems: ReadonlyMap<number, readonly number[]> = new Map();
  readonly groundItems: ReadonlyMap<number, { readonly vnum: number; readonly seenMs: number }> = new Map();

  /** No guild offline, so no plate carries a mark. */
  get guildId(): number {
    return 0;
  }

  onGuildInvitation(): void {}

  // A quest cannot request guild founding without an authenticated actor.
  onGuildFounding(): void {}

  guildWarChallenge(): void {}
  guildWarRefuse(): void {}
  readonly guildWarMenu = new GuildWarMenu();
  readonly guildWarArena = new GuildWarArena();
  onGuildWarMenu(): void {}
  onGuildWarArena(): void {}
  guildPostComment(): void {}
  guildDeleteComment(): void {}
  guildRefreshComments(): void {}
  readonly guildComments = new GuildComments();
  onGuildComments(): void {}

  readonly standing: Standing_ = NO_STANDING;
  readonly spirit: Spirit_ = NO_SPIRIT;
  readonly stamina: Stamina_ = NO_STAMINA;
  readonly pvpMode: number = NO_PVP_MODE;
  /** Offline there is nobody to duel, so the table is permanently empty. */
  readonly duels: ReadonlyMap<string, DuelRecord> = new Map();
  readonly skills: Skills_ = NO_SKILLS;
  readonly dead = false;
  readonly respawnSeconds = 0;

  useItemToItem(): void {
    // Nothing to use on anything: the offline path has an empty bag.
  }

  restartHere(): void {
    // No server to revive on; the offline stand-in has no death either.
  }

  useItemOnNpc(): void {
    // Nobody is standing on the offline map.
  }
}

boot().catch((error: unknown) => {
  console.error(error);
  const overlay = document.getElementById("overlay");
  if (overlay === null) return;
  overlay.replaceChildren();
  new FatalErrorScreen(overlay).show("Client startup failed", error);
});
